"use client";
import {
  TreeItem,
  computeExpandedItemIds,
  findItemById
} from "./chunk-YNZ7LDHP.js";

// src/index.ts
import { toast as toast2 } from "sonner";

// src/components/accordion/Accordion.tsx
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "@tessel/utils";
import { ChevronDown } from "lucide-react";
import { forwardRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var Accordion = forwardRef(
  ({ title, children, defaultOpen = false, className }, ref) => {
    const [isOpen, setIsOpen] = useState(
      defaultOpen ? ["item-1"] : []
    );
    return /* @__PURE__ */ jsx(
      AccordionPrimitive.Root,
      {
        ref,
        type: "multiple",
        value: isOpen,
        onValueChange: setIsOpen,
        className: cn("w-full", className),
        children: /* @__PURE__ */ jsxs(
          AccordionPrimitive.Item,
          {
            value: "item-1",
            className: "border px-3 border-border rounded-md bg-input dark:bg-input",
            children: [
              /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(AccordionPrimitive.Trigger, { className: "group flex flex-1 cursor-pointer items-center justify-between h-9 text-sm font-medium transition-all [&[data-state=open]>svg]:rotate-180", children: [
                title,
                /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" })
              ] }) }),
              /* @__PURE__ */ jsx(AccordionPrimitive.Content, { className: "data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up", children: /* @__PURE__ */ jsx("div", { className: "pb-3 pt-0", children }) })
            ]
          }
        )
      }
    );
  }
);
Accordion.displayName = "Accordion";
var AccordionRoot = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Root,
  {
    ref,
    className: cn("w-full", className),
    ...props
  }
));
AccordionRoot.displayName = "AccordionRoot";
var AccordionItem = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Item,
  {
    ref,
    className: cn("border-b last:border-b-0", className),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
  AccordionPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex flex-1 cursor-pointer items-center justify-between py-4 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" })
    ]
  }
) }));
AccordionTrigger.displayName = "AccordionTrigger";
var AccordionContent = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Content,
  {
    ref,
    className: "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: cn("pb-4 pt-0", className), children })
  }
));
AccordionContent.displayName = "AccordionContent";

// src/components/alert/Alert.tsx
import { cn as cn2 } from "@tessel/utils";

// src/components/alert/alert.styles.ts
import { tv } from "tailwind-variants";
var alertStyles = tv({
  slots: {
    wrapper: "p-4 mb-4 border rounded-lg",
    header: "flex items-center",
    icon: "flex-shrink-0 w-4 h-4 me-2",
    srOnly: "sr-only",
    title: "text-lg font-medium",
    description: "mt-2 mb-4 text-sm",
    actions: "flex"
  },
  variants: {
    variant: {
      info: {
        wrapper: [
          "text-foreground bg-[#4bb9e8]/10 border-[#4bb9e8]/30 glow-primary-sm"
        ]
      },
      danger: {
        wrapper: ["text-foreground bg-destructive/10 border-destructive/30"]
      },
      success: {
        wrapper: ["text-foreground bg-[#64c28d]/10 border-[#64c28d]/30"]
      },
      warning: {
        wrapper: ["text-foreground bg-[#dd9c51]/10 border-[#dd9c51]/30"]
      },
      dark: {
        wrapper: ["text-foreground bg-muted border-border"]
      }
    }
  },
  defaultVariants: {
    variant: "info"
  }
});

// src/components/alert/Alert.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Alert = ({
  variant = "info",
  title,
  description,
  icon,
  actions,
  children,
  className
}) => {
  const {
    wrapper,
    header,
    icon: iconSlot,
    srOnly,
    title: titleSlot,
    description: desc,
    actions: actionsSlot
  } = alertStyles({ variant });
  return /* @__PURE__ */ jsxs2("div", { role: "alert", className: cn2(wrapper(), className), children: [
    (icon || title) && /* @__PURE__ */ jsxs2("div", { className: header(), children: [
      icon && /* @__PURE__ */ jsx2("div", { className: iconSlot(), children: icon }),
      /* @__PURE__ */ jsx2("span", { className: srOnly(), children: variant }),
      title && /* @__PURE__ */ jsx2("h3", { className: titleSlot(), children: title })
    ] }),
    description && /* @__PURE__ */ jsx2("div", { className: desc(), children: description }),
    children,
    actions && /* @__PURE__ */ jsx2("div", { className: actionsSlot(), children: actions })
  ] });
};

// src/components/audio-player/AudioPlayer.tsx
import { cn as cn3 } from "@tessel/utils";
import { jsx as jsx3 } from "react/jsx-runtime";
function AudioPlayer({
  src,
  className,
  preload = "metadata"
}) {
  return /* @__PURE__ */ jsx3(
    "audio",
    {
      controls: true,
      src,
      preload,
      className: cn3("w-full", className),
      children: /* @__PURE__ */ jsx3("track", { kind: "captions" })
    }
  );
}

// src/components/avatar/Avatar.tsx
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn as cn4 } from "@tessel/utils";
import * as React from "react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var AVATAR_COLORS = [
  {
    bg: "bg-red-500/20",
    text: "text-red-600",
    ring: "ring-red-300 dark:ring-red-700"
  },
  {
    bg: "bg-orange-500/20",
    text: "text-orange-600",
    ring: "ring-orange-300 dark:ring-orange-700"
  },
  {
    bg: "bg-amber-500/20",
    text: "text-amber-600",
    ring: "ring-amber-300 dark:ring-amber-700"
  },
  {
    bg: "bg-yellow-500/20",
    text: "text-yellow-600",
    ring: "ring-yellow-300 dark:ring-yellow-700"
  },
  {
    bg: "bg-lime-500/20",
    text: "text-lime-600",
    ring: "ring-lime-300 dark:ring-lime-700"
  },
  {
    bg: "bg-green-500/20",
    text: "text-green-600",
    ring: "ring-green-300 dark:ring-green-700"
  },
  {
    bg: "bg-emerald-500/20",
    text: "text-emerald-600",
    ring: "ring-emerald-300 dark:ring-emerald-700"
  },
  {
    bg: "bg-teal-500/20",
    text: "text-teal-600",
    ring: "ring-teal-300 dark:ring-teal-700"
  },
  {
    bg: "bg-cyan-500/20",
    text: "text-cyan-600",
    ring: "ring-cyan-300 dark:ring-cyan-700"
  },
  {
    bg: "bg-sky-500/20",
    text: "text-sky-600",
    ring: "ring-sky-300 dark:ring-sky-700"
  },
  {
    bg: "bg-blue-500/20",
    text: "text-blue-600",
    ring: "ring-blue-300 dark:ring-blue-700"
  },
  {
    bg: "bg-indigo-500/20",
    text: "text-indigo-600",
    ring: "ring-indigo-300 dark:ring-indigo-700"
  },
  {
    bg: "bg-violet-500/20",
    text: "text-violet-600",
    ring: "ring-violet-300 dark:ring-violet-700"
  },
  {
    bg: "bg-purple-500/20",
    text: "text-purple-600",
    ring: "ring-purple-300 dark:ring-purple-700"
  },
  {
    bg: "bg-fuchsia-500/20",
    text: "text-fuchsia-600",
    ring: "ring-fuchsia-300 dark:ring-fuchsia-700"
  },
  {
    bg: "bg-pink-500/20",
    text: "text-pink-600",
    ring: "ring-pink-300 dark:ring-pink-700"
  },
  {
    bg: "bg-rose-500/20",
    text: "text-rose-600",
    ring: "ring-rose-300 dark:ring-rose-700"
  }
];
var DEFAULT_COLOR = {
  bg: "bg-muted-bg",
  text: "text-muted-foreground",
  ring: "ring-border"
};
var getColorFromName = (name) => {
  if (!name) {
    return DEFAULT_COLOR;
  }
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index] ?? DEFAULT_COLOR;
};
var getInitials = (name) => {
  if (!name) {
    return "?";
  }
  return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
};
var IMAGE_RING = "ring-primary/30";
var Avatar = React.forwardRef(
  ({
    className,
    size = "md",
    shape = "circle",
    status,
    bordered,
    src,
    alt,
    fallback,
    name,
    coloredFallback,
    children,
    ...props
  }, ref) => {
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const [imageError, setImageError] = React.useState(false);
    React.useEffect(() => {
      setImageLoaded(false);
      setImageError(false);
    }, []);
    const sizeClasses2 = {
      xs: "h-6 w-6 text-[10px]",
      sm: "h-8 w-8 text-xs",
      md: "h-10 w-10 text-sm",
      lg: "h-12 w-12 text-base",
      xl: "h-16 w-16 text-lg"
    };
    const shapeClasses = {
      circle: "rounded-full",
      square: "rounded-md"
    };
    const statusColors = {
      online: "bg-[#64c28d]",
      // Better Stack green
      offline: "bg-muted",
      away: "bg-[#dd9c51]",
      // Better Stack yellow
      busy: "bg-destructive"
    };
    const useColoredFallback = coloredFallback ?? Boolean(name);
    const fallbackContent = fallback ?? (name ? getInitials(name) : void 0);
    const color = useColoredFallback ? getColorFromName(name) : DEFAULT_COLOR;
    const showingImage = src && imageLoaded && !imageError;
    const ringColor = showingImage ? IMAGE_RING : color.ring;
    return /* @__PURE__ */ jsxs3("div", { className: "relative inline-block", children: [
      /* @__PURE__ */ jsxs3(
        AvatarPrimitive.Root,
        {
          ref,
          className: cn4(
            sizeClasses2[size],
            shapeClasses[shape],
            bordered && "border-2 border-background",
            "relative flex shrink-0 overflow-hidden",
            "ring-2",
            ringColor,
            className
          ),
          ...props,
          children: [
            src && /* @__PURE__ */ jsx4(
              AvatarImage,
              {
                src,
                alt: alt ?? name ?? "",
                onLoadingStatusChange: (status2) => {
                  if (status2 === "loaded") {
                    setImageLoaded(true);
                  } else if (status2 === "error") {
                    setImageError(true);
                  }
                },
                onImageLoad: () => setImageLoaded(true),
                onImageError: () => setImageError(true)
              }
            ),
            fallbackContent && /* @__PURE__ */ jsx4(
              AvatarFallback,
              {
                className: cn4(color.bg, color.text, "font-semibold"),
                children: fallbackContent
              }
            ),
            children
          ]
        }
      ),
      status && /* @__PURE__ */ jsx4(
        "span",
        {
          className: cn4(
            "absolute bottom-0 right-0 block rounded-full ring-2 ring-background",
            statusColors[status],
            size === "xs" ? "h-1.5 w-1.5" : "h-2.5 w-2.5"
          )
        }
      )
    ] });
  }
);
Avatar.displayName = "Avatar";
var AvatarImage = React.forwardRef(
  ({ className, onLoadingStatusChange, onImageLoad, onImageError, ...props }, ref) => /* @__PURE__ */ jsx4(
    AvatarPrimitive.Image,
    {
      ref,
      className: cn4("aspect-square h-full w-full", className),
      onLoadingStatusChange,
      onLoad: onImageLoad,
      onError: onImageError,
      ...props
    }
  )
);
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
var AvatarFallback = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn4(
      "flex h-full w-full items-center justify-center rounded-full bg-muted-bg",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// src/components/avatar/AvatarGroup.tsx
import { cn as cn5 } from "@tessel/utils";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var AvatarGroup = ({
  avatars,
  max = 5,
  size = "md",
  shape = "circle",
  bordered = true,
  className,
  ...props
}) => {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;
  const showCount = remainingCount > 0;
  const marginMap = {
    xs: "-mr-1",
    sm: "-mr-2",
    md: "-mr-2.5",
    lg: "-mr-3",
    xl: "-mr-4"
  };
  const sizeClasses2 = {
    xs: "h-6 w-6 text-xs",
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-16 w-16 text-lg"
  };
  const shapeClasses = {
    circle: "rounded-full",
    square: "rounded-md"
  };
  const margin = marginMap[size];
  return /* @__PURE__ */ jsx5("div", { className: cn5("flex items-center", className), ...props, children: /* @__PURE__ */ jsxs4("div", { className: "flex", children: [
    visibleAvatars.map((avatar, index) => /* @__PURE__ */ jsx5(
      "div",
      {
        className: cn5("relative inline-block", index !== 0 && margin),
        style: { zIndex: visibleAvatars.length - index },
        children: /* @__PURE__ */ jsx5(Avatar, { ...avatar, size, shape, bordered })
      },
      avatar.key || index
    )),
    showCount && /* @__PURE__ */ jsx5(
      "div",
      {
        className: cn5("relative inline-block", margin),
        style: { zIndex: 0 },
        children: /* @__PURE__ */ jsx5(
          "div",
          {
            className: cn5(
              "flex items-center justify-center bg-muted text-muted-foreground",
              sizeClasses2[size],
              shapeClasses[shape],
              bordered && "border-2 border-background"
            ),
            children: /* @__PURE__ */ jsxs4("span", { className: "font-medium", children: [
              "+",
              remainingCount
            ] })
          }
        )
      }
    )
  ] }) });
};
AvatarGroup.displayName = "AvatarGroup";

// src/components/badge/Badge.tsx
import { cn as cn7 } from "@tessel/utils";
import {
  useCallback,
  useRef,
  useState as useState3
} from "react";

// src/components/tooltip/Tooltip.tsx
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn as cn6 } from "@tessel/utils";
import { jsx as jsx6 } from "react/jsx-runtime";
function TooltipProvider({
  delayDuration = 0,
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    TooltipPrimitive.Provider,
    {
      "data-slot": "tooltip-provider",
      delayDuration,
      ...props
    }
  );
}
function Tooltip({
  delayDuration,
  ...props
}) {
  return /* @__PURE__ */ jsx6(TooltipProvider, { delayDuration, children: /* @__PURE__ */ jsx6(TooltipPrimitive.Root, { "data-slot": "tooltip", ...props }) });
}
function TooltipTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx6(TooltipPrimitive.Trigger, { "data-slot": "tooltip-trigger", ...props });
}
function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx6(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx6(
    TooltipPrimitive.Content,
    {
      "data-slot": "tooltip-content",
      sideOffset,
      className: cn6(
        "bg-bg-950 text-soft-white border border-bg-500 shadow-md",
        "animate-in fade-in-0 zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-lg px-3 py-2 text-sm",
        className
      ),
      ...props,
      children
    }
  ) });
}

// src/components/badge/Badge.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var ColorVariants = [
  "primary",
  "secondary",
  "success",
  "warning",
  "destructive",
  "muted",
  "info"
];
var SizeVariants = ["default", "normal", "sm", "xs"];
var getRandomVariant = (name) => {
  const hash = name.split("").reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
  return ColorVariants[Math.abs(hash) % ColorVariants.length] || "primary";
};
var Badge = ({
  children,
  colorVariant = "primary",
  size = "default",
  className,
  maxWidth,
  tooltipContent
}) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState3(false);
  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);
  const colorStyles = {
    primary: "bg-primary/10 text-primary border-primary/20",
    secondary: "bg-secondary/10 text-secondary-foreground border-secondary/20",
    success: "bg-[#64c28d]/10 text-[#64c28d] border-[#64c28d]/20",
    warning: "bg-[#dd9c51]/10 text-[#dd9c51] border-[#dd9c51]/20",
    destructive: "bg-destructive/10 text-destructive border-destructive/20",
    muted: "bg-muted/50 text-muted-foreground border-border",
    info: "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
  };
  const sizeStyles2 = {
    default: "text-[13px] px-2.5 py-0.5 rounded inline-flex items-center",
    normal: "text-base px-2.5 py-1 rounded inline-flex items-center",
    sm: "text-sm px-2 py-0.5 rounded inline-flex items-center",
    xs: "text-xs px-1.5 py-0.5 rounded-sm inline-flex items-center align-middle"
  };
  const badge = /* @__PURE__ */ jsx7(
    "span",
    {
      ref: maxWidth ? ref : void 0,
      onMouseEnter: maxWidth ? checkTruncation : void 0,
      className: cn7(
        "font-medium",
        "border",
        "transition-colors",
        colorStyles[colorVariant],
        sizeStyles2[size],
        maxWidth && "overflow-hidden text-ellipsis whitespace-nowrap inline-block align-middle",
        className
      ),
      style: maxWidth ? { maxWidth } : void 0,
      children
    }
  );
  if (maxWidth && tooltipContent) {
    return /* @__PURE__ */ jsxs5(Tooltip, { children: [
      /* @__PURE__ */ jsx7(TooltipTrigger, { asChild: true, disabled: !isTruncated, children: badge }),
      isTruncated && /* @__PURE__ */ jsx7(TooltipContent, { children: tooltipContent })
    ] });
  }
  return badge;
};

// src/components/base-input/BaseInput.tsx
import { cn as cn9 } from "@tessel/utils";
import { forwardRef as forwardRef3 } from "react";

// src/components/form/general.styles.ts
import { cn as cn8 } from "@tessel/utils";
import { tv as tv2 } from "tailwind-variants";
var fieldControlShared = cn8(
  "w-full rounded-md border border-input bg-background",
  "placeholder:text-muted-foreground text-foreground",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-input/50"
);
var fieldControlError = cn8(
  "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30",
  "aria-invalid:border-destructive aria-invalid:ring-destructive/30"
);
var fieldControlStyles = tv2({
  slots: {
    control: fieldControlShared,
    textarea: cn8(
      fieldControlShared,
      "ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2",
      "min-h-0 h-auto resize-y"
    )
  },
  variants: {
    size: {
      sm: {
        control: "h-8 px-2.5 py-1 text-sm",
        textarea: "min-h-[44px] px-2 py-1 text-sm"
      },
      md: {
        control: "h-9 px-3 py-1.5 text-sm",
        textarea: "min-h-[80px] px-3 py-2 text-sm"
      },
      lg: {
        control: "h-12 px-3 py-3 text-base",
        textarea: "min-h-[120px] px-3 py-2 text-base"
      }
    },
    density: {
      default: {},
      compact: {
        control: "h-6 rounded-sm px-1.5 py-0 text-sm leading-6"
      }
    },
    error: {
      true: {
        control: fieldControlError,
        textarea: fieldControlError
      }
    },
    disabled: {
      true: {
        control: "disabled:cursor-not-allowed disabled:opacity-50",
        textarea: "disabled:cursor-not-allowed disabled:opacity-50"
      }
    }
  },
  defaultVariants: {
    size: "md",
    density: "default"
  }
});
var baseInputStyles = ({
  size,
  error,
  disabled,
  className
}) => fieldControlStyles({
  size,
  error,
  disabled,
  className
}).control();

// src/components/base-input/BaseInput.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var BaseInput = forwardRef3(
  ({
    className,
    type = "text",
    size = "md",
    density = "default",
    error = false,
    ...props
  }, ref) => /* @__PURE__ */ jsx8(
    "input",
    {
      ref,
      type,
      "aria-invalid": error || void 0,
      className: cn9(
        fieldControlStyles({ size, density, error }).control(),
        // Hide native number-input spinners — em larguras pequenas as setas
        // ocupam toda a área visível, parecendo um checkbox vazio.
        "[appearance:textfield]",
        "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        "[&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:m-0",
        className
      ),
      ...props
    }
  )
);
BaseInput.displayName = "BaseInput";

// src/components/button/Button.tsx
import { cn as cn10 } from "@tessel/utils";
import { forwardRef as forwardRef4 } from "react";

// src/components/button/button.styles.ts
import { tv as tv3 } from "tailwind-variants";
var buttonStyles = tv3({
  base: [
    "inline-flex items-center justify-center",
    "font-medium text-sm",
    "rounded-md",
    // 8px radius (conforme referência)
    "cursor-pointer",
    "focus:outline-none focus:ring-2",
    // Transição da referência com timing cubic-bezier
    "transition-[color,background-color,border-color,outline-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform] duration-150 ease-out",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
    "whitespace-nowrap",
    "flex-shrink-0"
  ],
  variants: {
    variant: {
      primary: [
        // Usando as variáveis CSS atualizadas
        "bg-primary text-primary-foreground",
        // Borda mais clara no tema light, mais escura no dark
        "border border-soft-white/20 dark:border-primary/70",
        // Elevação natural permanente
        "shadow-[0_2px_8px_0_rgba(0,0,0,0.15),0_1px_3px_0_rgba(0,0,0,0.1)]",
        // Estados hover com elevação aumentada
        "hover:bg-primary/90 hover:shadow-[0_4px_12px_0_rgba(0,0,0,0.2),0_2px_6px_0_rgba(0,0,0,0.1)]",
        "focus:ring-2 focus:ring-primary/30",
        // Estado active sem escala para evitar lag
        ""
      ],
      secondary: [
        "bg-secondary text-secondary-foreground border border-border",
        // Elevação natural permanente para secondary
        "shadow-sm",
        "hover:bg-accent hover:text-accent-foreground hover:shadow-md",
        "focus:ring-2 focus:ring-ring/30",
        // Estado active sem sobrescrever shadow
        ""
      ],
      ghost: [
        "bg-transparent text-muted-foreground",
        "hover:bg-primary/15 hover:text-foreground",
        "focus:ring-ring/30",
        ""
      ],
      outline: [
        "bg-muted-bg border border-border text-foreground",
        // Elevação sutil para outline (tem borda)
        "shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]",
        "hover:bg-hover hover:text-foreground hover:shadow-[0_2px_4px_0_rgba(0,0,0,0.1)]",
        "focus:ring-ring/30",
        ""
      ],
      selected: [
        "bg-accent text-accent-foreground border",
        "border-primary/30",
        // Elevação para estado selecionado
        "shadow-[0_2px_4px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]",
        "hover:bg-accent/90 hover:shadow-[0_3px_6px_0_rgba(0,0,0,0.12),0_2px_4px_0_rgba(0,0,0,0.08)]",
        "focus:ring-primary/30"
      ],
      info: [
        "text-soft-white bg-[#4bb9e8] hover:bg-[#4bb9e8]/90 focus:ring-[#4bb9e8]/30",
        "border border-[#4bb9e8]/20",
        // Elevação para botões coloridos
        "shadow-[0_2px_4px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_3px_6px_0_rgba(0,0,0,0.12),0_2px_4px_0_rgba(0,0,0,0.08)]"
      ],
      danger: [
        "text-soft-white bg-destructive hover:bg-destructive/90 focus:ring-destructive/30",
        "border border-destructive/20",
        // Elevação para botões coloridos
        "shadow-[0_2px_4px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_3px_6px_0_rgba(0,0,0,0.12),0_2px_4px_0_rgba(0,0,0,0.08)]"
      ],
      warning: [
        "text-soft-white bg-[#dd9c51] hover:bg-[#dd9c51]/90 focus:ring-[#dd9c51]/30",
        "border border-[#dd9c51]/20",
        // Elevação para botões coloridos
        "shadow-[0_2px_4px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_3px_6px_0_rgba(0,0,0,0.12),0_2px_4px_0_rgba(0,0,0,0.08)]"
      ],
      dark: [
        "text-primary bg-card hover:bg-card/80 hover:text-primary focus:ring-ring/30",
        "border border-border",
        // Elevação para botão dark
        "shadow-[0_2px_4px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_3px_6px_0_rgba(0,0,0,0.12),0_2px_4px_0_rgba(0,0,0,0.08)]"
      ],
      link: [
        "text-primary hover:text-primary/80 hover:underline focus:ring-primary/30",
        "bg-transparent p-0 h-auto"
      ],
      accent: [
        "bg-primary text-primary-foreground shadow-sm",
        "hover:bg-primary/90 hover:shadow-md",
        "focus:ring-primary/30",
        "border border-primary/20"
      ]
    },
    color: {
      default: "",
      green: [
        "bg-emerald-500/10 text-emerald-500",
        "hover:bg-emerald-500/20 hover:text-emerald-600",
        "focus:ring-emerald-500/30",
        "border border-emerald-500/30",
        "hover:border-emerald-500/50"
      ],
      red: [
        "bg-red-500/10 text-red-500",
        "hover:bg-red-500/20 hover:text-red-600",
        "focus:ring-red-500/30",
        "border border-red-500/30",
        "hover:border-red-500/50"
      ],
      amber: [
        "bg-amber-500/10 text-amber-500",
        "hover:bg-amber-500/20 hover:text-amber-600",
        "focus:ring-amber-500/30",
        "border border-amber-500/30",
        "hover:border-amber-500/50"
      ],
      gray: [
        "bg-gray-500/10 text-gray-500",
        "hover:bg-gray-500/20 hover:text-gray-600",
        "focus:ring-gray-500/30",
        "border border-gray-500/30",
        "hover:border-gray-500/50"
      ]
    },
    size: {
      xs: "h-6 px-2 text-xs gap-1",
      sm: "h-8 px-3 text-sm gap-1.5",
      md: "h-9 px-3 text-sm gap-2",
      // 36px height, 12px padding conforme referência
      lg: "h-11 px-6 text-base gap-2",
      xl: "h-12 px-8 text-lg gap-2.5",
      icon: "h-10 w-10 p-0",
      "icon-sm": "h-8 w-8 p-0",
      "icon-xs": "h-6 w-6 p-0"
    },
    rounded: {
      default: "",
      full: "rounded-full"
    },
    appearance: {
      default: "",
      loading: "cursor-not-allowed opacity-70"
    }
  },
  compoundVariants: [
    // Reset ghost hover styles when using color variants
    {
      variant: "ghost",
      color: "green",
      class: [
        "bg-emerald-500/10 text-emerald-500",
        "hover:bg-emerald-500/20 hover:text-emerald-600"
      ]
    },
    {
      variant: "ghost",
      color: "red",
      class: [
        "bg-red-500/10 text-red-500",
        "hover:bg-red-500/20 hover:text-red-600"
      ]
    },
    {
      variant: "ghost",
      color: "amber",
      class: [
        "bg-amber-500/10 text-amber-500",
        "hover:bg-amber-500/20 hover:text-amber-600"
      ]
    },
    {
      variant: "ghost",
      color: "gray",
      class: [
        "bg-gray-500/10 text-gray-500",
        "hover:bg-gray-500/20 hover:text-gray-600"
      ]
    }
  ],
  defaultVariants: {
    variant: "primary",
    size: "md",
    rounded: "default",
    appearance: "default",
    color: "default"
  }
});

// src/components/button/Button.tsx
import { jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
var ICON_SIZE_MAP = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-5 w-5",
  xl: "h-5 w-5",
  icon: "h-5 w-5",
  "icon-sm": "h-4 w-4",
  "icon-xs": "h-3.5 w-3.5"
};
var Button = forwardRef4(
  ({
    children,
    testId,
    className,
    disabled,
    appearance,
    onClick,
    form,
    type = "button",
    variant = "primary",
    size = "md",
    rounded = "default",
    color = "default",
    icon: IconLeft,
    iconRight: IconRight,
    ...rest
  }, ref) => {
    const baseClasses = cn10(
      buttonStyles({
        variant,
        size,
        rounded,
        appearance,
        color
      }),
      className
    );
    const iconClass = ICON_SIZE_MAP[size ?? "md"] ?? ICON_SIZE_MAP.md;
    return /* @__PURE__ */ jsxs6(
      "button",
      {
        ref,
        "data-testid": testId,
        type,
        className: baseClasses,
        disabled,
        onClick,
        form,
        ...rest,
        children: [
          IconLeft && /* @__PURE__ */ jsx9(IconLeft, { className: iconClass }),
          children,
          IconRight && /* @__PURE__ */ jsx9(IconRight, { className: iconClass })
        ]
      }
    );
  }
);
Button.displayName = "Button";

// src/components/button/ButtonLink.tsx
import { useCallback as useCallback2 } from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
var ButtonLink = ({
  children,
  link,
  className,
  ...props
}) => {
  const onClickHandler = useCallback2(() => {
    window.location.href = link;
  }, [link]);
  return /* @__PURE__ */ jsx10(Button, { className, onClick: onClickHandler, ...props, children });
};

// src/components/calendar/Calendar.tsx
import { cn as cn11 } from "@tessel/utils";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "lucide-react";
import * as React2 from "react";
import {
  DayPicker,
  getDefaultClassNames
} from "react-day-picker";
import { jsx as jsx11 } from "react/jsx-runtime";
var CalendarRoot = ({ className, rootRef, ...props }) => {
  return /* @__PURE__ */ jsx11(
    "div",
    {
      "data-slot": "calendar",
      ref: rootRef,
      className: cn11(className),
      ...props
    }
  );
};
var CalendarChevron = ({ className, orientation, ...props }) => {
  if (orientation === "left") {
    return /* @__PURE__ */ jsx11(ChevronLeftIcon, { className: cn11("h-4 w-4", className), ...props });
  }
  if (orientation === "right") {
    return /* @__PURE__ */ jsx11(ChevronRightIcon, { className: cn11("h-4 w-4", className), ...props });
  }
  return /* @__PURE__ */ jsx11(ChevronDownIcon, { className: cn11("h-4 w-4", className), ...props });
};
var CalendarWeekNumber = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx11("td", { ...props, children: /* @__PURE__ */ jsx11("div", { className: "flex h-8 w-8 items-center justify-center text-center", children }) });
};
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components: components2,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  return /* @__PURE__ */ jsx11(
    DayPicker,
    {
      showOutsideDays,
      className: cn11(
        "bg-background group/calendar p-3 [--cell-size:theme(spacing.8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        className
      ),
      captionLayout,
      formatters: {
        formatMonthDropdown: (date) => date.toLocaleString("default", { month: "short" }),
        ...formatters
      },
      classNames: {
        root: cn11("w-fit", defaultClassNames.root),
        months: cn11(
          "flex gap-4 flex-col md:flex-row relative",
          defaultClassNames.months
        ),
        month: cn11("flex flex-col w-full gap-4", defaultClassNames.month),
        nav: cn11(
          "flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between",
          defaultClassNames.nav
        ),
        button_previous: cn11(
          buttonStyles({ variant: buttonVariant, size: "icon-sm" }),
          "aria-disabled:opacity-50 select-none",
          defaultClassNames.button_previous
        ),
        button_next: cn11(
          buttonStyles({ variant: buttonVariant, size: "icon-sm" }),
          "aria-disabled:opacity-50 select-none",
          defaultClassNames.button_next
        ),
        month_caption: cn11(
          "flex items-center justify-center h-8 w-full px-8",
          defaultClassNames.month_caption
        ),
        dropdowns: cn11(
          "w-full flex items-center text-sm font-medium justify-center h-8 gap-1.5",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn11(
          "relative focus-within:border-ring border border-input shadow-sm focus-within:ring-ring/20 focus-within:ring-2 rounded-md",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn11(
          "absolute bg-popover inset-0 opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn11(
          "select-none font-medium",
          captionLayout === "label" ? "text-sm" : "rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn11("flex", defaultClassNames.weekdays),
        weekday: cn11(
          "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none",
          defaultClassNames.weekday
        ),
        week: cn11("flex w-full mt-2", defaultClassNames.week),
        week_number_header: cn11(
          "select-none w-8",
          defaultClassNames.week_number_header
        ),
        week_number: cn11(
          "text-[0.8rem] select-none text-muted-foreground",
          defaultClassNames.week_number
        ),
        day: cn11(
          "relative w-full h-full p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none",
          defaultClassNames.day
        ),
        range_start: cn11(
          "rounded-l-md bg-accent",
          defaultClassNames.range_start
        ),
        range_middle: cn11("rounded-none", defaultClassNames.range_middle),
        range_end: cn11("rounded-r-md bg-accent", defaultClassNames.range_end),
        today: cn11(
          "bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn11(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn11(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn11("invisible", defaultClassNames.hidden),
        ...classNames
      },
      components: {
        Root: CalendarRoot,
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
        WeekNumber: CalendarWeekNumber,
        ...components2
      },
      ...props
    }
  );
}
function CalendarDayButton({
  className,
  day,
  modifiers,
  color: _color,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  const ref = React2.useRef(null);
  React2.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);
  return /* @__PURE__ */ jsx11(
    Button,
    {
      ref,
      variant: "ghost",
      size: "icon",
      "data-day": day.date.toLocaleDateString(),
      "data-selected-single": modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle,
      "data-range-start": modifiers.range_start,
      "data-range-end": modifiers.range_end,
      "data-range-middle": modifiers.range_middle,
      className: cn11(
        "data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-focus-visible/day:ring-ring dark:hover:text-accent-foreground flex aspect-square h-auto w-full min-w-8 flex-col gap-1 leading-none font-normal group-focus-visible/day:relative group-focus-visible/day:z-10 group-focus-visible/day:ring-2 data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      ),
      ...props
    }
  );
}

// src/components/card/Card.tsx
import { cn as cn12 } from "@tessel/utils";
import { jsx as jsx12 } from "react/jsx-runtime";
var cardVariants = {
  variant: {
    default: "bg-muted-bg border-border card-shadow hover:bg-hover hover:border-primary/50 hover:shadow-sm",
    outline: "border-border bg-transparent hover:bg-hover hover:border-primary/50",
    ghost: "border-transparent bg-transparent hover:bg-hover"
  },
  size: {
    sm: "p-3",
    md: "p-4",
    lg: "p-6"
  },
  hover: {
    true: "cursor-pointer",
    false: ""
  },
  shadow: {
    none: "",
    sm: "card-shadow",
    md: "card-shadow-md",
    lg: "card-shadow-lg"
  }
};
var Card = ({
  children,
  className,
  variant = "default",
  size = "md",
  hover = false,
  shadow = "sm",
  onClick,
  ...props
}) => {
  return /* @__PURE__ */ jsx12(
    "div",
    {
      className: cn12(
        "rounded-lg border transition-colors",
        cardVariants.variant[variant],
        cardVariants.size[size],
        cardVariants.hover[hover ? "true" : "false"],
        cardVariants.shadow[shadow],
        className
      ),
      onClick,
      ...props,
      children
    }
  );
};
var CardHeader = ({ children, className }) => {
  return /* @__PURE__ */ jsx12("div", { className: cn12("flex flex-col space-y-1.5", className), children });
};
var CardTitle = ({ children, className }) => {
  return /* @__PURE__ */ jsx12(
    "h3",
    {
      className: cn12(
        "text-2xl font-semibold leading-none tracking-tight",
        className
      ),
      children
    }
  );
};
var CardDescription = ({
  children,
  className
}) => {
  return /* @__PURE__ */ jsx12("p", { className: cn12("text-sm text-muted-foreground", className), children });
};
var CardContent = ({ children, className }) => {
  return /* @__PURE__ */ jsx12("div", { className: cn12("pt-0", className), children });
};
var CardFooter = ({ children, className }) => {
  return /* @__PURE__ */ jsx12("div", { className: cn12("flex items-center pt-0", className), children });
};

// src/components/charts/EChart.tsx
import { lazy, Suspense } from "react";
import { jsx as jsx13 } from "react/jsx-runtime";
var ReactECharts = lazy(() => import("echarts-for-react"));
var ChartLoading = () => /* @__PURE__ */ jsx13("div", { className: "h-full w-full flex items-center justify-center", children: /* @__PURE__ */ jsx13("div", { className: "animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" }) });
var EChart = ({ option, style, className }) => {
  const mergedOption = {
    backgroundColor: "transparent",
    ...option
  };
  return /* @__PURE__ */ jsx13(Suspense, { fallback: /* @__PURE__ */ jsx13(ChartLoading, {}), children: /* @__PURE__ */ jsx13(
    ReactECharts,
    {
      option: mergedOption,
      style: style ?? { width: "100%", height: "100%" },
      className,
      notMerge: true
    }
  ) });
};

// src/components/chat/ChatContainer.tsx
import { cn as cn19 } from "@tessel/utils";

// src/components/chat/ChatEmptyState.tsx
import { cn as cn13 } from "@tessel/utils";
import { MessageSquare } from "lucide-react";
import { jsx as jsx14, jsxs as jsxs7 } from "react/jsx-runtime";
var ChatEmptyState = ({
  title,
  description,
  suggestions = [],
  onSuggestionClick,
  className
}) => {
  return /* @__PURE__ */ jsxs7(
    "div",
    {
      className: cn13(
        "flex flex-col items-center justify-center text-center px-4 md:px-8",
        className
      ),
      children: [
        /* @__PURE__ */ jsx14("div", { className: "mb-4 md:mb-6", children: /* @__PURE__ */ jsx14("div", { className: "mx-auto flex h-12 w-12 md:h-16 md:w-16 items-center justify-center rounded-full bg-primary/10", children: /* @__PURE__ */ jsx14(MessageSquare, { className: "h-6 w-6 md:h-8 md:w-8 text-primary" }) }) }),
        title && /* @__PURE__ */ jsx14("h2", { className: "text-xl md:text-2xl font-semibold text-foreground mb-1.5 md:mb-2", children: title }),
        description && /* @__PURE__ */ jsx14("p", { className: "text-sm md:text-base text-muted-foreground max-w-md mb-5 md:mb-8", children: description }),
        suggestions.length > 0 && /* @__PURE__ */ jsx14("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 w-full max-w-xl", children: suggestions.map((suggestion) => {
          const Icon3 = suggestion.icon;
          return /* @__PURE__ */ jsxs7(
            Button,
            {
              variant: "outline",
              className: "h-auto py-3 px-3 md:py-4 md:px-4 text-left justify-start whitespace-normal gap-2 md:gap-3",
              onClick: () => onSuggestionClick?.(suggestion.message),
              children: [
                Icon3 && /* @__PURE__ */ jsx14(Icon3, { className: "h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" }),
                /* @__PURE__ */ jsx14("span", { className: "text-sm leading-relaxed", children: suggestion.label })
              ]
            },
            suggestion.label
          );
        }) })
      ]
    }
  );
};

// src/components/chat/ChatInput.tsx
import { cn as cn15 } from "@tessel/utils";
import { RotateCcw, Send } from "lucide-react";
import { useState as useState4 } from "react";

// src/components/textarea/Textarea.tsx
import { cn as cn14 } from "@tessel/utils";
import { jsx as jsx15 } from "react/jsx-runtime";
var Textarea = ({
  className,
  size,
  error = false,
  ...props
}) => {
  return /* @__PURE__ */ jsx15(
    "textarea",
    {
      "aria-invalid": error || void 0,
      className: cn14(fieldControlStyles({ size, error }).textarea(), className),
      ...props
    }
  );
};

// src/components/chat/ChatInput.tsx
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
var ChatInput = ({
  onSendMessage,
  isLoading = false,
  placeholder,
  className = "",
  disabled = false,
  processingText,
  waitingText,
  sendHintText,
  sendAriaLabel
}) => {
  const [message, setMessage] = useState4("");
  const handleSendMessage = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !isLoading && !disabled) {
      onSendMessage(trimmedMessage);
      setMessage("");
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleChange = (e) => {
    setMessage(e.target.value);
  };
  return /* @__PURE__ */ jsxs8("div", { className: cn15("flex flex-col space-y-2", className), children: [
    /* @__PURE__ */ jsx16(
      "div",
      {
        className: cn15(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          isLoading ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        ),
        children: /* @__PURE__ */ jsx16("div", { className: "overflow-hidden", children: processingText && /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2 text-sm text-primary px-3 py-2", children: [
          /* @__PURE__ */ jsx16(RotateCcw, { className: "h-4 w-4 animate-spin" }),
          /* @__PURE__ */ jsx16("span", { children: processingText })
        ] }) })
      }
    ),
    /* @__PURE__ */ jsxs8("div", { className: "relative", children: [
      /* @__PURE__ */ jsx16(
        Textarea,
        {
          value: message,
          onChange: handleChange,
          onKeyDown: handleKeyDown,
          placeholder: isLoading ? waitingText : placeholder,
          className: cn15(
            "min-h-[50px] md:min-h-[60px] max-h-[150px] md:max-h-[200px] w-full resize-none rounded-xl border border-primary/20 bg-primary/10 p-3 pr-12 md:p-4 md:pr-14",
            "focus:ring-2 focus:ring-primary/30 focus:border-primary/40",
            isLoading && "opacity-50 cursor-not-allowed"
          ),
          disabled: isLoading || disabled,
          "data-testid": "chat-input-textarea"
        }
      ),
      /* @__PURE__ */ jsx16(
        Button,
        {
          onClick: handleSendMessage,
          disabled: message.trim() === "" || isLoading || disabled,
          size: "icon",
          className: "absolute bottom-2 right-2 md:bottom-3 md:right-3 h-8 w-8 md:h-9 md:w-9 rounded-lg",
          "aria-label": sendAriaLabel,
          "data-testid": "chat-input-send-button",
          children: isLoading ? /* @__PURE__ */ jsx16(RotateCcw, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx16(Send, { className: "h-4 w-4" })
        }
      )
    ] }),
    sendHintText && /* @__PURE__ */ jsx16("p", { className: "hidden md:block text-xs text-muted-foreground text-center", children: sendHintText })
  ] });
};

// src/components/chat/ChatMessageList.tsx
import { cn as cn18 } from "@tessel/utils";
import { useEffect as useEffect3, useMemo as useMemo2, useRef as useRef3 } from "react";

// src/components/chat/ChatMessage.tsx
import * as AccordionPrimitive2 from "@radix-ui/react-accordion";
import { cn as cn17, splitTextByRefs } from "@tessel/utils";
import { ChevronDown as ChevronDown2, ImageIcon } from "lucide-react";
import { useMemo } from "react";
import { IoReload, IoSearch } from "react-icons/io5";
import ReactMarkdown from "react-markdown";

// src/components/skeleton/Skeleton.tsx
import { cn as cn16 } from "@tessel/utils";
import { jsx as jsx17 } from "react/jsx-runtime";
var Skeleton = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx17(
    "div",
    {
      className: cn16("animate-pulse rounded-md bg-primary/10", className),
      ...props
    }
  );
};

// src/components/chat/ChatMessage.tsx
import { Fragment, jsx as jsx18, jsxs as jsxs9 } from "react/jsx-runtime";
var parseNoteReferences = (content, NoteReferenceLink, onSourceClick, enableNotePreview, noteReferenceFallback) => {
  const segments = splitTextByRefs(content);
  if (segments.length === 1 && segments[0]?.type === "text") {
    return [content];
  }
  return segments.map((segment, index) => {
    if (segment.type === "text") {
      return segment.content;
    }
    const { noteId, noteName } = segment.ref;
    if (NoteReferenceLink) {
      return /* @__PURE__ */ jsx18(
        NoteReferenceLink,
        {
          noteId,
          fallbackName: noteName,
          onClick: onSourceClick,
          enablePreview: enableNotePreview
        },
        `ref-${noteId}-${index}`
      );
    }
    const fallback = noteName || noteReferenceFallback;
    return fallback ? `[${fallback}]` : `[${noteId}]`;
  });
};
var TextWithReferences = ({
  content,
  NoteReferenceLink,
  onSourceClick,
  enableNotePreview,
  noteReferenceFallback
}) => {
  const parsedContent = useMemo(
    () => parseNoteReferences(
      content,
      NoteReferenceLink,
      onSourceClick,
      enableNotePreview,
      noteReferenceFallback
    ),
    [
      content,
      NoteReferenceLink,
      onSourceClick,
      enableNotePreview,
      noteReferenceFallback
    ]
  );
  return /* @__PURE__ */ jsx18(Fragment, { children: parsedContent });
};
var renderChildrenWithReferences = (children, NoteReferenceLink, onSourceClick, enableNotePreview, noteReferenceFallback) => {
  if (typeof children === "string") {
    if (children.includes("[[ref:")) {
      return /* @__PURE__ */ jsx18(
        TextWithReferences,
        {
          content: children,
          NoteReferenceLink,
          onSourceClick,
          enableNotePreview,
          noteReferenceFallback
        }
      );
    }
    return children;
  }
  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === "string" && child.includes("[[ref:")) {
        const contentKey = `ref-text-${child.slice(0, 50).replace(/[^a-z0-9]/gi, "")}-${index}`;
        return /* @__PURE__ */ jsx18(
          TextWithReferences,
          {
            content: child,
            NoteReferenceLink,
            onSourceClick,
            enableNotePreview,
            noteReferenceFallback
          },
          contentKey
        );
      }
      return child;
    });
  }
  return children;
};
var ToolCallCard = ({
  message,
  truncate = false,
  hideStatus = false,
  toolCallCompletedLabel
}) => {
  if (!message.toolCall) {
    return null;
  }
  const isActive = message.toolCall.isActive;
  const isImageTool = message.toolCall.toolName === "generateImage";
  const ToolIcon = isImageTool ? ImageIcon : IoSearch;
  return /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 md:gap-3 min-w-0", children: [
    /* @__PURE__ */ jsx18("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsxs9("div", { className: "relative", children: [
      /* @__PURE__ */ jsx18(
        ToolIcon,
        {
          className: `h-4 w-4 ${isActive ? "text-primary" : "text-muted-foreground"}`
        }
      ),
      isActive && /* @__PURE__ */ jsx18("div", { className: "absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 bg-primary rounded-full animate-pulse" })
    ] }) }),
    /* @__PURE__ */ jsx18(
      "p",
      {
        className: cn17(
          "text-sm flex-1 min-w-0 break-words",
          truncate && "truncate",
          isActive ? "text-primary font-medium" : "text-muted-foreground"
        ),
        children: message.toolCall.message
      }
    ),
    !hideStatus && isActive && /* @__PURE__ */ jsx18(IoReload, { className: "h-3.5 w-3.5 text-primary animate-spin flex-shrink-0" }),
    !(hideStatus || isActive) && toolCallCompletedLabel && /* @__PURE__ */ jsx18("span", { className: "text-xs text-muted-foreground/70 flex-shrink-0", children: toolCallCompletedLabel })
  ] });
};
var ToolCallGroup = ({
  toolMessages,
  toolCallCompletedLabel
}) => {
  if (toolMessages.length === 0) {
    return null;
  }
  const lastMessage = toolMessages[toolMessages.length - 1];
  if (!lastMessage) {
    return null;
  }
  const hasActiveCall = toolMessages.some((m) => m.toolCall?.isActive);
  if (toolMessages.length === 1) {
    return /* @__PURE__ */ jsx18("div", { className: "flex justify-start my-1 md:my-2 chat-message-enter", children: /* @__PURE__ */ jsx18(
      "div",
      {
        className: cn17(
          "max-w-[90%] md:max-w-[80%] flex items-center gap-2 md:gap-3 px-3 py-2 md:px-4 md:py-2.5 rounded-lg border transition-all duration-300",
          hasActiveCall ? "bg-primary/5 border-primary/30 shadow-sm" : "bg-muted/30 border-border/50"
        ),
        children: /* @__PURE__ */ jsx18(
          ToolCallCard,
          {
            message: lastMessage,
            toolCallCompletedLabel
          }
        )
      }
    ) });
  }
  return /* @__PURE__ */ jsx18("div", { className: "flex justify-start my-1 md:my-2 chat-message-enter", children: /* @__PURE__ */ jsx18(AccordionPrimitive2.Root, { type: "multiple", className: "w-[90%] md:w-[80%]", children: /* @__PURE__ */ jsxs9(
    AccordionPrimitive2.Item,
    {
      value: "tool-calls",
      className: cn17(
        "rounded-lg border transition-all duration-300",
        hasActiveCall ? "bg-primary/5 border-primary/30 shadow-sm" : "bg-muted/30 border-border/50"
      ),
      children: [
        /* @__PURE__ */ jsx18(AccordionPrimitive2.Header, { className: "flex", children: /* @__PURE__ */ jsxs9(AccordionPrimitive2.Trigger, { className: "group flex flex-1 cursor-pointer items-center justify-between px-3 py-2 md:px-4 md:py-2.5 text-left text-sm [&[data-state=open]>svg]:rotate-180", children: [
          /* @__PURE__ */ jsx18("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsx18(
            ToolCallCard,
            {
              message: lastMessage,
              truncate: true,
              hideStatus: true,
              toolCallCompletedLabel
            }
          ) }),
          /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 ml-2 flex-shrink-0", children: [
            /* @__PURE__ */ jsx18("span", { className: "text-xs text-muted-foreground", children: toolMessages.length }),
            /* @__PURE__ */ jsx18(ChevronDown2, { className: "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200" })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx18(AccordionPrimitive2.Content, { className: "overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down", children: /* @__PURE__ */ jsx18("div", { className: "px-3 pb-2 md:px-4 md:pb-2.5 space-y-1.5 border-t border-border/30 pt-2", children: toolMessages.map((msg, index) => /* @__PURE__ */ jsx18(
          "div",
          {
            className: index < toolMessages.length - 1 ? "opacity-70" : "",
            children: /* @__PURE__ */ jsx18(
              ToolCallCard,
              {
                message: msg,
                toolCallCompletedLabel
              }
            )
          },
          msg.id
        )) }) })
      ]
    }
  ) }) });
};
var ChatMessageItem = ({
  message,
  onSourceClick,
  enableNotePreview = false,
  NoteReferenceLink,
  labels,
  renderMessageFooter
}) => {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  const isTool = message.role === "tool";
  if (isTool && message.toolCall) {
    return null;
  }
  if (isUser) {
    return /* @__PURE__ */ jsx18(
      "div",
      {
        className: "flex justify-end chat-message-enter",
        "data-testid": `chat-message-${message.id}`,
        children: /* @__PURE__ */ jsxs9("div", { className: "max-w-[90%] md:max-w-[80%] rounded-2xl bg-primary px-3 py-2 md:px-4 md:py-3 text-primary-foreground", children: [
          /* @__PURE__ */ jsx18("div", { className: "prose prose-sm prose-invert max-w-none", children: /* @__PURE__ */ jsx18(
            ReactMarkdown,
            {
              components: {
                p: ({ children }) => /* @__PURE__ */ jsx18("p", { className: "mb-2 last:mb-0", children })
              },
              children: message.content
            }
          ) }),
          /* @__PURE__ */ jsx18("div", { className: "mt-1 text-xs opacity-70 text-right", children: message.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
          }) })
        ] })
      }
    );
  }
  const assistantAriaLabel = labels?.assistantAriaLabel ?? labels?.assistantName;
  return /* @__PURE__ */ jsxs9(
    "div",
    {
      className: "flex flex-col chat-message-enter",
      "data-testid": `chat-message-${message.id}`,
      children: [
        /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2 text-primary", children: [
          /* @__PURE__ */ jsxs9(
            "svg",
            {
              className: "h-5 w-5",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              strokeLinecap: "round",
              strokeLinejoin: "round",
              role: "img",
              "aria-label": assistantAriaLabel,
              children: [
                /* @__PURE__ */ jsx18("path", { d: "M12 2L2 7l10 5 10-5-10-5z" }),
                /* @__PURE__ */ jsx18("path", { d: "M2 17l10 5 10-5" }),
                /* @__PURE__ */ jsx18("path", { d: "M2 12l10 5 10-5" })
              ]
            }
          ),
          labels?.assistantName && /* @__PURE__ */ jsx18("span", { className: "font-semibold", children: labels.assistantName }),
          message.ragMetadata?.isUsingRAG && /* @__PURE__ */ jsx18("span", { className: "text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full", children: "RAG" })
        ] }),
        /* @__PURE__ */ jsxs9("div", { className: "max-w-full md:max-w-[90%] rounded-2xl border border-border bg-card px-3 py-2 md:px-4 md:py-3 transition-all duration-200 ease-out", children: [
          message.isLoading && !message.content ? /* @__PURE__ */ jsxs9("div", { className: "space-y-3", children: [
            labels?.messageProcessing && /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 text-sm text-muted-foreground", children: [
              /* @__PURE__ */ jsx18(IoReload, { className: "h-4 w-4 animate-spin" }),
              /* @__PURE__ */ jsx18("span", { children: labels.messageProcessing })
            ] }),
            /* @__PURE__ */ jsxs9("div", { className: "space-y-2", children: [
              /* @__PURE__ */ jsx18(Skeleton, { className: "h-4 w-3/4" }),
              /* @__PURE__ */ jsx18(Skeleton, { className: "h-4 w-1/2" }),
              /* @__PURE__ */ jsx18(Skeleton, { className: "h-4 w-2/3" })
            ] })
          ] }) : /* @__PURE__ */ jsxs9("div", { className: "prose prose-sm dark:prose-invert max-w-none", children: [
            /* @__PURE__ */ jsx18(
              ReactMarkdown,
              {
                components: {
                  p: ({ children }) => /* @__PURE__ */ jsx18("p", { className: "mb-2 last:mb-0", children: renderChildrenWithReferences(
                    children,
                    NoteReferenceLink,
                    onSourceClick,
                    enableNotePreview,
                    labels?.noteReferenceFallback
                  ) }),
                  ul: ({ children }) => /* @__PURE__ */ jsx18("ul", { className: "mb-2 ml-4 list-disc", children }),
                  ol: ({ children }) => /* @__PURE__ */ jsx18("ol", { className: "mb-2 ml-4 list-decimal", children }),
                  li: ({ children }) => /* @__PURE__ */ jsx18("li", { className: "mb-1", children: renderChildrenWithReferences(
                    children,
                    NoteReferenceLink,
                    onSourceClick,
                    enableNotePreview,
                    labels?.noteReferenceFallback
                  ) }),
                  strong: ({ children }) => /* @__PURE__ */ jsx18("strong", { className: "font-bold", children: renderChildrenWithReferences(
                    children,
                    NoteReferenceLink,
                    onSourceClick,
                    enableNotePreview,
                    labels?.noteReferenceFallback
                  ) }),
                  em: ({ children }) => /* @__PURE__ */ jsx18("em", { className: "italic", children: renderChildrenWithReferences(
                    children,
                    NoteReferenceLink,
                    onSourceClick,
                    enableNotePreview,
                    labels?.noteReferenceFallback
                  ) }),
                  code: ({ children }) => /* @__PURE__ */ jsx18("code", { className: "rounded bg-muted px-1 py-0.5 text-sm", children }),
                  pre: ({ children }) => /* @__PURE__ */ jsx18("pre", { className: "rounded bg-muted p-3 text-sm overflow-x-auto", children }),
                  h1: ({ children }) => /* @__PURE__ */ jsx18("h1", { className: "text-xl font-bold mb-2", children }),
                  h2: ({ children }) => /* @__PURE__ */ jsx18("h2", { className: "text-lg font-bold mb-2", children }),
                  h3: ({ children }) => /* @__PURE__ */ jsx18("h3", { className: "text-base font-bold mb-1", children }),
                  blockquote: ({ children }) => /* @__PURE__ */ jsx18("blockquote", { className: "border-l-4 border-border pl-4 italic", children }),
                  a: ({ href, children }) => /* @__PURE__ */ jsx18(
                    "a",
                    {
                      href,
                      className: "text-primary underline hover:text-primary/80",
                      target: "_blank",
                      rel: "noopener noreferrer",
                      children
                    }
                  ),
                  img: ({ src, alt }) => (
                    // biome-ignore lint/performance/noImgElement: shared UI library, not Next.js
                    /* @__PURE__ */ jsx18(
                      "img",
                      {
                        src,
                        alt: alt || labels?.generatedImageAlt,
                        className: "rounded-lg max-w-full max-h-96 my-2 border border-border",
                        loading: "lazy"
                      }
                    )
                  )
                },
                children: message.content
              }
            ),
            message.isLoading && message.content && labels?.continuing && /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 mt-2", children: [
              /* @__PURE__ */ jsx18(IoReload, { className: "h-3 w-3 animate-spin text-primary" }),
              /* @__PURE__ */ jsx18("span", { className: "text-xs text-muted-foreground", children: labels.continuing })
            ] })
          ] }),
          isAssistant && message.ragMetadata && message.ragMetadata.eventSources.length > 0 && labels?.formatSources && /* @__PURE__ */ jsx18("div", { className: "mt-3 pt-2 border-t border-border", children: /* @__PURE__ */ jsxs9("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx18("div", { className: "text-xs font-medium text-muted-foreground flex items-center gap-1", children: /* @__PURE__ */ jsx18("span", { children: labels.formatSources(
              message.ragMetadata.eventSources.length
            ) }) }),
            /* @__PURE__ */ jsxs9("div", { className: "grid gap-2 max-h-32 overflow-y-auto", children: [
              message.ragMetadata.eventSources.slice(0, 3).map((source) => /* @__PURE__ */ jsxs9(
                "div",
                {
                  className: "p-2 bg-muted/50 rounded text-xs border border-transparent hover:border-border transition-colors cursor-pointer",
                  onClick: () => onSourceClick?.(source.id),
                  children: [
                    /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 mb-1", children: [
                      /* @__PURE__ */ jsx18("span", { className: "font-medium truncate", children: source.name }),
                      /* @__PURE__ */ jsx18("span", { className: "bg-primary/10 text-primary px-1 py-0.5 rounded text-xs", children: source.type })
                    ] }),
                    /* @__PURE__ */ jsx18("p", { className: "text-muted-foreground line-clamp-2", children: source.description })
                  ]
                },
                source.id
              )),
              message.ragMetadata.eventSources.length > 3 && labels.formatMoreSources && /* @__PURE__ */ jsx18("div", { className: "text-xs text-muted-foreground text-center py-1", children: labels.formatMoreSources(
                message.ragMetadata.eventSources.length - 3
              ) })
            ] })
          ] }) }),
          renderMessageFooter && !message.isLoading && /* @__PURE__ */ jsx18("div", { className: "mt-2 pt-2 border-t border-border/60", children: renderMessageFooter(message) })
        ] })
      ]
    }
  );
};

// src/components/chat/ChatMessageList.tsx
import { jsx as jsx19, jsxs as jsxs10 } from "react/jsx-runtime";
function groupMessages(messages) {
  const groups = [];
  let currentToolGroup = [];
  for (const message of messages) {
    if (message.role === "tool" && message.toolCall) {
      currentToolGroup.push(message);
    } else {
      if (currentToolGroup.length > 0) {
        groups.push({ type: "tool-group", messages: currentToolGroup });
        currentToolGroup = [];
      }
      groups.push({ type: "message", message });
    }
  }
  if (currentToolGroup.length > 0) {
    groups.push({ type: "tool-group", messages: currentToolGroup });
  }
  return groups;
}
var ChatMessageList = ({
  messages,
  className = "",
  onSourceClick,
  enableNotePreview = false,
  NoteReferenceLink,
  labels,
  renderMessageFooter
}) => {
  const endOfMessagesRef = useRef3(null);
  const prevMessageCountRef = useRef3(0);
  const groupedItems = useMemo2(() => groupMessages(messages), [messages]);
  const lastMessageContent = messages.length > 0 ? messages[messages.length - 1]?.content : "";
  const messageCount = messages.length;
  useEffect3(() => {
    if (!endOfMessagesRef.current) {
      return;
    }
    const isNewMessage = messageCount > prevMessageCountRef.current;
    prevMessageCountRef.current = messageCount;
    endOfMessagesRef.current.scrollIntoView({
      behavior: isNewMessage ? "smooth" : "instant",
      block: "end"
    });
  }, [messageCount, lastMessageContent]);
  if (messages.length === 0) {
    return /* @__PURE__ */ jsx19("div", { className: "flex h-full items-center justify-center p-8 text-center", children: /* @__PURE__ */ jsxs10("div", { className: "space-y-2", children: [
      labels?.emptyTitle && /* @__PURE__ */ jsx19("p", { className: "text-lg font-medium", children: labels.emptyTitle }),
      labels?.emptyDescription && /* @__PURE__ */ jsx19("p", { className: "text-sm text-muted-foreground", children: labels.emptyDescription })
    ] }) });
  }
  return /* @__PURE__ */ jsxs10("div", { className: cn18("flex flex-col gap-3 p-1 md:gap-4 md:p-4", className), children: [
    groupedItems.map((item) => {
      if (item.type === "tool-group") {
        const groupKey = item.messages.map((m) => m.id).join("-");
        return /* @__PURE__ */ jsx19(
          ToolCallGroup,
          {
            toolMessages: item.messages,
            toolCallCompletedLabel: labels?.toolCallCompleted
          },
          groupKey
        );
      }
      return /* @__PURE__ */ jsx19(
        ChatMessageItem,
        {
          message: item.message,
          onSourceClick,
          enableNotePreview,
          NoteReferenceLink,
          labels,
          renderMessageFooter
        },
        item.message.id
      );
    }),
    /* @__PURE__ */ jsx19("div", { ref: endOfMessagesRef })
  ] });
};

// src/components/chat/ChatContainer.tsx
import { jsx as jsx20, jsxs as jsxs11 } from "react/jsx-runtime";
var ChatContainer = ({
  messages,
  onSendMessage,
  isLoading = false,
  className = "",
  disabled = false,
  onSourceClick,
  enableNotePreview = false,
  NoteReferenceLink,
  suggestions = [],
  labels,
  renderMessageFooter
}) => {
  const isEmpty = messages.length === 0;
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      className: cn19(
        "flex h-full w-full flex-col px-3 py-3 md:px-8 md:py-6",
        className
      ),
      children: [
        /* @__PURE__ */ jsx20("div", { className: "min-h-0 flex-1 overflow-y-auto", children: isEmpty ? /* @__PURE__ */ jsx20("div", { className: "flex min-h-[200px] md:min-h-[280px] flex-col items-center justify-center", children: /* @__PURE__ */ jsx20(
          ChatEmptyState,
          {
            title: labels?.emptyTitle,
            description: labels?.emptyDescription,
            suggestions,
            onSuggestionClick: onSendMessage
          }
        ) }) : /* @__PURE__ */ jsx20(
          ChatMessageList,
          {
            messages,
            onSourceClick,
            enableNotePreview,
            NoteReferenceLink,
            labels,
            renderMessageFooter
          }
        ) }),
        /* @__PURE__ */ jsx20("div", { className: "shrink-0 pt-3 md:pt-4", children: /* @__PURE__ */ jsx20(
          ChatInput,
          {
            onSendMessage,
            isLoading,
            placeholder: labels?.inputPlaceholder,
            disabled,
            processingText: labels?.inputProcessing,
            waitingText: labels?.inputWaiting,
            sendHintText: labels?.sendHint,
            sendAriaLabel: labels?.sendAriaLabel
          }
        ) })
      ]
    }
  );
};

// src/components/checkbox/Checkbox.tsx
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn as cn20 } from "@tessel/utils";
import { CheckIcon } from "lucide-react";
import { jsx as jsx21 } from "react/jsx-runtime";
function Checkbox({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx21(
    CheckboxPrimitive.Root,
    {
      "data-slot": "checkbox",
      className: cn20(
        "peer border-input dark:bg-input/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] cursor-pointer",
        "disabled:cursor-not-allowed disabled:opacity-40 disabled:bg-muted disabled:border-muted-foreground/30 disabled:data-[state=checked]:bg-muted-foreground/50 disabled:data-[state=checked]:border-muted-foreground/50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx21(
        CheckboxPrimitive.Indicator,
        {
          "data-slot": "checkbox-indicator",
          className: "grid place-content-center text-current transition-none",
          children: /* @__PURE__ */ jsx21(CheckIcon, { className: "size-3.5" })
        }
      )
    }
  );
}

// src/components/context-menu/ContextMenu.tsx
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { cn as cn21 } from "@tessel/utils";
import { CheckIcon as CheckIcon2, ChevronRightIcon as ChevronRightIcon2, CircleIcon } from "lucide-react";
import { jsx as jsx22, jsxs as jsxs12 } from "react/jsx-runtime";
function ContextMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx22(ContextMenuPrimitive.Root, { "data-slot": "context-menu", ...props });
}
function ContextMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx22(ContextMenuPrimitive.Trigger, { "data-slot": "context-menu-trigger", ...props });
}
function ContextMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx22(ContextMenuPrimitive.Group, { "data-slot": "context-menu-group", ...props });
}
function ContextMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx22(ContextMenuPrimitive.Portal, { "data-slot": "context-menu-portal", ...props });
}
function ContextMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx22(ContextMenuPrimitive.Sub, { "data-slot": "context-menu-sub", ...props });
}
function ContextMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    ContextMenuPrimitive.RadioGroup,
    {
      "data-slot": "context-menu-radio-group",
      ...props
    }
  );
}
function ContextMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs12(
    ContextMenuPrimitive.SubTrigger,
    {
      "data-slot": "context-menu-sub-trigger",
      "data-inset": inset,
      className: cn21(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none pointer-events-auto data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx22(ChevronRightIcon2, { className: "ml-auto" })
      ]
    }
  );
}
function ContextMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    ContextMenuPrimitive.SubContent,
    {
      "data-slot": "context-menu-sub-content",
      className: cn21(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg",
        className
      ),
      ...props
    }
  );
}
function ContextMenuContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(ContextMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx22(
    ContextMenuPrimitive.Content,
    {
      "data-slot": "context-menu-content",
      className: cn21(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-context-menu-content-available-height) min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md",
        className
      ),
      ...props
    }
  ) });
}
function ContextMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    ContextMenuPrimitive.Item,
    {
      "data-slot": "context-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn21(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none pointer-events-auto data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs12(
    ContextMenuPrimitive.CheckboxItem,
    {
      "data-slot": "context-menu-checkbox-item",
      className: cn21(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none pointer-events-auto data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx22("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx22(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx22(CheckIcon2, { className: "size-4" }) }) }),
        children
      ]
    }
  );
}
function ContextMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs12(
    ContextMenuPrimitive.RadioItem,
    {
      "data-slot": "context-menu-radio-item",
      className: cn21(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none pointer-events-auto data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx22("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx22(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx22(CircleIcon, { className: "size-2 fill-current" }) }) }),
        children
      ]
    }
  );
}
function ContextMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    ContextMenuPrimitive.Label,
    {
      "data-slot": "context-menu-label",
      "data-inset": inset,
      className: cn21(
        "text-foreground px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function ContextMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    ContextMenuPrimitive.Separator,
    {
      "data-slot": "context-menu-separator",
      className: cn21("bg-border -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function ContextMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx22(
    "span",
    {
      "data-slot": "context-menu-shortcut",
      className: cn21(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className
      ),
      ...props
    }
  );
}

// src/components/date-picker/DatePicker.tsx
import { cn as cn23, formatDate } from "@tessel/utils";
import { CalendarIcon } from "lucide-react";

// src/components/popover/Popover.tsx
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn as cn22 } from "@tessel/utils";
import { jsx as jsx23 } from "react/jsx-runtime";
function Popover({
  ...props
}) {
  return /* @__PURE__ */ jsx23(PopoverPrimitive.Root, { "data-slot": "popover", ...props });
}
function PopoverTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx23(PopoverPrimitive.Trigger, { "data-slot": "popover-trigger", ...props });
}
function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx23(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx23(
    PopoverPrimitive.Content,
    {
      "data-slot": "popover-content",
      align,
      sideOffset,
      className: cn22(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden",
        className
      ),
      ...props
    }
  ) });
}
function PopoverAnchor({
  ...props
}) {
  return /* @__PURE__ */ jsx23(PopoverPrimitive.Anchor, { "data-slot": "popover-anchor", ...props });
}

// src/components/date-picker/DatePicker.tsx
import { jsx as jsx24, jsxs as jsxs13 } from "react/jsx-runtime";
function DatePicker({
  date,
  onSelect,
  placeholder,
  className,
  disabled = false,
  label,
  locale
}) {
  const buttonId = `date-picker-${Math.random().toString(36).substr(2, 9)}`;
  return /* @__PURE__ */ jsxs13("div", { className: "space-y-2", children: [
    label && /* @__PURE__ */ jsx24(
      "label",
      {
        htmlFor: buttonId,
        className: "block text-sm font-medium text-foreground",
        children: label
      }
    ),
    /* @__PURE__ */ jsxs13(Popover, { children: [
      /* @__PURE__ */ jsx24(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs13(
        Button,
        {
          id: buttonId,
          variant: "outline",
          className: cn23(
            "h-10 w-full justify-start text-left font-normal bg-input border-border px-3 py-2 text-base shadow-sm hover:border-border/80 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 md:text-sm",
            !date && "text-muted-foreground",
            date && "text-foreground font-medium",
            className
          ),
          disabled,
          children: [
            /* @__PURE__ */ jsx24(CalendarIcon, { className: "mr-2 h-4 w-4 text-muted-foreground/70" }),
            date ? formatDate(date, "long", locale ?? void 0) : placeholder && /* @__PURE__ */ jsx24("span", { children: placeholder })
          ]
        }
      ) }),
      /* @__PURE__ */ jsx24(PopoverContent, { className: "w-auto p-0", children: /* @__PURE__ */ jsx24(
        Calendar,
        {
          mode: "single",
          selected: date,
          onSelect,
          initialFocus: true
        }
      ) })
    ] })
  ] });
}
function DatePickerInput({
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
  label,
  locale
}) {
  const date = value ? new Date(value) : void 0;
  const handleSelect = (newDate) => {
    if (onChange) {
      onChange(newDate ? formatDate(newDate, "iso") : "");
    }
  };
  return /* @__PURE__ */ jsx24(
    DatePicker,
    {
      date,
      onSelect: handleSelect,
      placeholder,
      className,
      disabled,
      label,
      locale
    }
  );
}

// src/components/dropdown-menu/DropdownMenu.tsx
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cn as cn24 } from "@tessel/utils";
import { Check, ChevronRight, CircleDot } from "lucide-react";
import { jsx as jsx25, jsxs as jsxs14 } from "react/jsx-runtime";
function DropdownMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx25(DropdownMenuPrimitive.Root, { "data-slot": "dropdown-menu", ...props });
}
function DropdownMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx25(DropdownMenuPrimitive.Portal, { "data-slot": "dropdown-menu-portal", ...props });
}
function DropdownMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    DropdownMenuPrimitive.Trigger,
    {
      "data-slot": "dropdown-menu-trigger",
      ...props
    }
  );
}
function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx25(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx25(
    DropdownMenuPrimitive.Content,
    {
      "data-slot": "dropdown-menu-content",
      sideOffset,
      className: cn24(
        "bg-muted-bg text-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border border-border p-1 shadow-md",
        className
      ),
      ...props
    }
  ) });
}
function DropdownMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx25(DropdownMenuPrimitive.Group, { "data-slot": "dropdown-menu-group", ...props });
}
function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    DropdownMenuPrimitive.Item,
    {
      "data-slot": "dropdown-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn24(
        "data-[highlighted]:bg-foreground/10 focus:bg-foreground/10 data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs14(
    DropdownMenuPrimitive.CheckboxItem,
    {
      "data-slot": "dropdown-menu-checkbox-item",
      className: cn24(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx25("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx25(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx25(Check, { className: "size-4" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    DropdownMenuPrimitive.RadioGroup,
    {
      "data-slot": "dropdown-menu-radio-group",
      ...props
    }
  );
}
function DropdownMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs14(
    DropdownMenuPrimitive.RadioItem,
    {
      "data-slot": "dropdown-menu-radio-item",
      className: cn24(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx25("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx25(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx25(CircleDot, { className: "size-2 fill-current" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    DropdownMenuPrimitive.Label,
    {
      "data-slot": "dropdown-menu-label",
      "data-inset": inset,
      className: cn24(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    DropdownMenuPrimitive.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      className: cn24("bg-border -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function DropdownMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    "span",
    {
      "data-slot": "dropdown-menu-shortcut",
      className: cn24(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx25(DropdownMenuPrimitive.Sub, { "data-slot": "dropdown-menu-sub", ...props });
}
function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs14(
    DropdownMenuPrimitive.SubTrigger,
    {
      "data-slot": "dropdown-menu-sub-trigger",
      "data-inset": inset,
      className: cn24(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx25(ChevronRight, { className: "ml-auto size-4" })
      ]
    }
  );
}
function DropdownMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx25(
    DropdownMenuPrimitive.SubContent,
    {
      "data-slot": "dropdown-menu-sub-content",
      className: cn24(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg",
        className
      ),
      ...props
    }
  );
}

// src/components/form/components/FormFieldWrapper.tsx
import { cn as cn25 } from "@tessel/utils";

// src/components/form/hooks/useFormContext.ts
import { createContext, useContext } from "react";
var FormContext = createContext(null);
function useFormContext() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("Form components must be used within a Form");
  }
  return context;
}

// src/components/form/utils/formUtils.ts
function formatError(error) {
  if (typeof error === "string") {
    return error;
  }
  if (error?.message) {
    return error.message;
  }
  return "Validation error";
}
function shouldShowErrors(field) {
  return field.state.meta.errors.length > 0 && (field.state.meta.isTouched || field.state.meta.isDirty);
}

// src/components/form/components/FormFieldWrapper.tsx
import { jsx as jsx26, jsxs as jsxs15 } from "react/jsx-runtime";
function FormFieldWrapper({
  name,
  label,
  required,
  helpText,
  className,
  children
}) {
  const form = useFormContext();
  return /* @__PURE__ */ jsx26(
    form.Field,
    {
      name,
      children: (field) => /* @__PURE__ */ jsxs15("div", { className: cn25("space-y-2", className), children: [
        label && /* @__PURE__ */ jsxs15(
          "label",
          {
            htmlFor: field.name,
            className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
            children: [
              label,
              " ",
              required && /* @__PURE__ */ jsx26("span", { className: "text-red-500", children: "*" })
            ]
          }
        ),
        children(field),
        shouldShowErrors(field) && /* @__PURE__ */ jsx26("p", { className: "text-sm text-red-600", children: formatError(field.state.meta.errors[0]) }),
        helpText && /* @__PURE__ */ jsx26("p", { className: "text-sm text-muted-foreground", children: helpText })
      ] })
    }
  );
}

// src/components/form/components/FormCustom.tsx
var FormCustom = FormFieldWrapper;

// src/components/form/components/FormField.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
function FormField({ name, children }) {
  const form = useFormContext();
  return /* @__PURE__ */ jsx27(form.Field, { name, children });
}

// src/components/form/components/FormInput.tsx
import { cn as cn26 } from "@tessel/utils";
import { jsx as jsx28 } from "react/jsx-runtime";
function FormInput({
  name,
  label,
  placeholder,
  className,
  required,
  helpText,
  type = "text",
  size = "md"
}) {
  return /* @__PURE__ */ jsx28(
    FormFieldWrapper,
    {
      name,
      label,
      required,
      helpText,
      children: (field) => {
        const handleInputChange = (e) => {
          field.handleChange(e.target.value);
          field.handleBlur();
        };
        const hasError = shouldShowErrors(field);
        return /* @__PURE__ */ jsx28(
          BaseInput,
          {
            id: field.name,
            type,
            placeholder: placeholder || "",
            value: field.state.value || "",
            onChange: handleInputChange,
            onBlur: field.handleBlur,
            size,
            error: hasError,
            className: cn26(className)
          }
        );
      }
    }
  );
}

// src/components/form/components/FormRoot.tsx
import { useForm } from "@tanstack/react-form";
import { jsx as jsx29 } from "react/jsx-runtime";
function FormRoot({
  children,
  defaultValues,
  onSubmit,
  validationSchema,
  className
}) {
  const schema = validationSchema;
  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      try {
        await onSubmit(value);
      } catch (error) {
        console.error("Form submission error:", error);
      }
    },
    validators: schema ? {
      onChange: schema,
      onBlur: schema,
      onSubmit: schema
    } : void 0
  });
  return /* @__PURE__ */ jsx29(FormContext.Provider, { value: form, children: /* @__PURE__ */ jsx29(
    "form",
    {
      onSubmit: (e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      },
      className,
      children
    }
  ) });
}

// src/components/form/components/FormSelect.tsx
import { cn as cn28 } from "@tessel/utils";

// src/components/input-select/SelectSingle.tsx
import { cn as cn27 } from "@tessel/utils";
import { ChevronDown as ChevronDown3, X } from "lucide-react";
import { lazy as lazy2, Suspense as Suspense2 } from "react";
import ReactSelect, { components } from "react-select";
import { tv as tv4 } from "tailwind-variants";
import { jsx as jsx30, jsxs as jsxs16 } from "react/jsx-runtime";
var selectVariants = tv4({
  slots: {
    control: "flex w-full min-w-0 cursor-pointer shadow-sm transition-[color,box-shadow,border-color] outline-none",
    placeholder: "text-muted-foreground cursor-pointer",
    input: "text-foreground cursor-pointer",
    singleValue: "text-foreground font-medium cursor-pointer",
    option: "cursor-pointer transition-all duration-150 text-foreground",
    noOptions: "text-muted-foreground bg-muted/50 border border-dashed border-border rounded-sm text-center"
  },
  variants: {
    size: {
      sm: {
        singleValue: "text-sm leading-5",
        option: "px-2.5 py-1.5 text-sm",
        noOptions: "p-2 text-xs"
      },
      md: {
        singleValue: "text-sm leading-5",
        option: "px-3 py-2",
        noOptions: "p-2.5 text-sm"
      },
      lg: {
        singleValue: "leading-6",
        option: "px-3 py-2.5",
        noOptions: "p-3 text-sm"
      }
    }
  },
  defaultVariants: {
    size: "md"
  }
});
var controlStateStyles = {
  focus: "border-ring ring-2 ring-ring/30 focus-visible:border-ring focus-visible:ring-ring/30 focus-visible:ring-2",
  nonFocus: "border-border hover:border-border/80",
  invalid: "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  disabled: "!pointer-events-none !cursor-not-allowed !opacity-50 !bg-muted/30 !border-dashed !border-muted-foreground/20"
};
var valueContainerStyles = "gap-1";
var indicatorsContainerStyles = "gap-1";
var clearIndicatorStyles = "p-1 hover:bg-accent text-muted-foreground/70 rounded-md hover:text-accent-foreground transition-colors cursor-pointer";
var dropdownIndicatorStyles = {
  base: "p-1 hover:bg-accent text-muted-foreground/70 rounded-md hover:text-accent-foreground transition-colors cursor-pointer",
  disabled: "text-muted-foreground/30 hover:bg-transparent hover:text-muted-foreground/30"
};
var menuStyles = "mt-1 border border-border bg-popover rounded-md shadow-lg overflow-hidden z-[99999]";
var optionStateStyles = {
  focus: "bg-foreground/10",
  selected: "bg-primary/10 text-foreground font-medium"
};
var groupStyles = "pt-2 first:pt-0";
var groupHeadingStyles = "px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 border-b border-border flex items-center gap-2";
var DropdownIndicator = (props) => /* @__PURE__ */ jsx30(components.DropdownIndicator, { ...props, children: /* @__PURE__ */ jsx30(ChevronDown3, { className: "h-4 w-4 text-muted-foreground/70" }) });
var ClearIndicator = (props) => /* @__PURE__ */ jsx30(components.ClearIndicator, { ...props, children: /* @__PURE__ */ jsx30(X, { className: "h-4 w-4 text-muted-foreground/70 hover:text-foreground transition-colors cursor-pointer" }) });
var CustomOption = (props) => {
  const { data, children } = props;
  return /* @__PURE__ */ jsx30(components.Option, { ...props, children: /* @__PURE__ */ jsxs16("div", { className: "flex items-center gap-2 w-full", children: [
    data.icon && /* @__PURE__ */ jsx30("span", { className: "flex-shrink-0", children: data.icon }),
    /* @__PURE__ */ jsx30("span", { className: "truncate min-w-0 flex-1", children })
  ] }) });
};
var CustomGroupHeading = (props) => {
  const { data } = props;
  return /* @__PURE__ */ jsxs16("div", { className: groupHeadingStyles, children: [
    data.icon && /* @__PURE__ */ jsx30("span", { className: "flex-shrink-0", children: data.icon }),
    /* @__PURE__ */ jsx30("span", { children: data.label })
  ] });
};
var isGroupedOptions = (options) => {
  const firstOption = options[0];
  return options.length > 0 && firstOption != null && "options" in firstOption;
};
var findSelectedOption = (options, value) => {
  if (!value) {
    return void 0;
  }
  if (isGroupedOptions(options)) {
    for (const group of options) {
      const found = group.options.find((opt) => opt.value === value);
      if (found) {
        return found;
      }
    }
    return void 0;
  }
  return options.find((opt) => opt.value === value);
};
var SelectSingleInternal = ({
  options,
  value,
  onChange,
  onBlur,
  onMenuScrollToBottom,
  placeholder,
  className,
  size = "md",
  isDisabled = false,
  isLoading = false,
  isClearable = false
}) => {
  const styles = selectVariants({ size });
  const handleChange = (option) => {
    onChange?.(option?.value ? String(option.value) : "");
  };
  const selectedOption = findSelectedOption(options, value);
  return /* @__PURE__ */ jsx30(
    ReactSelect,
    {
      unstyled: true,
      options,
      value: selectedOption,
      onChange: handleChange,
      onBlur,
      onMenuScrollToBottom,
      placeholder,
      isDisabled,
      isLoading,
      isClearable,
      menuPortalTarget: typeof document !== "undefined" ? document.body : null,
      menuPosition: "fixed",
      classNames: {
        control: ({
          isFocused,
          isDisabled: controlIsDisabled
        }) => cn27(
          fieldControlStyles({ size }).control(),
          styles.control(),
          controlStateStyles.nonFocus,
          isFocused && controlStateStyles.focus,
          controlStateStyles.invalid,
          controlIsDisabled && controlStateStyles.disabled,
          className
        ),
        placeholder: ({
          isDisabled: placeholderIsDisabled
        }) => cn27(
          styles.placeholder(),
          placeholderIsDisabled && "!text-muted-foreground/40"
        ),
        input: () => styles.input(),
        valueContainer: () => valueContainerStyles,
        singleValue: ({
          isDisabled: valueIsDisabled
        }) => cn27(
          styles.singleValue(),
          valueIsDisabled && "text-muted-foreground/50"
        ),
        indicatorsContainer: () => indicatorsContainerStyles,
        clearIndicator: () => clearIndicatorStyles,
        dropdownIndicator: ({
          isDisabled: indicatorIsDisabled
        }) => cn27(
          dropdownIndicatorStyles.base,
          indicatorIsDisabled && dropdownIndicatorStyles.disabled
        ),
        menu: () => menuStyles,
        option: ({
          isFocused,
          isSelected
        }) => cn27(
          styles.option(),
          isFocused && optionStateStyles.focus,
          isSelected && optionStateStyles.selected
        ),
        group: () => groupStyles,
        noOptionsMessage: () => styles.noOptions()
      },
      styles: {
        input: (base) => ({
          ...base,
          cursor: "pointer",
          "input:focus": {
            boxShadow: "none"
          }
        }),
        control: (base) => ({
          ...base,
          transition: "none",
          minHeight: "auto"
        }),
        option: (base) => ({
          ...base,
          cursor: "pointer"
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: 99999
        })
      },
      components: {
        DropdownIndicator,
        ClearIndicator,
        Option: CustomOption,
        GroupHeading: CustomGroupHeading
      }
    }
  );
};
var SelectSingleSkeleton = () => /* @__PURE__ */ jsx30("div", { className: "flex h-10 w-full animate-pulse items-center rounded-md border border-border bg-muted/30 px-3", children: /* @__PURE__ */ jsx30("div", { className: "h-4 w-24 rounded bg-muted" }) });
var SelectSingleLazy = lazy2(
  () => Promise.resolve({ default: SelectSingleInternal })
);
var SelectSingle = (props) => /* @__PURE__ */ jsx30(Suspense2, { fallback: /* @__PURE__ */ jsx30(SelectSingleSkeleton, {}), children: /* @__PURE__ */ jsx30(SelectSingleLazy, { ...props }) });

// src/components/form/components/FormSelect.tsx
import { jsx as jsx31 } from "react/jsx-runtime";
function FormSelect({
  name,
  label,
  placeholder,
  className,
  required,
  helpText,
  options,
  isLoading,
  isClearable,
  size
}) {
  return /* @__PURE__ */ jsx31(
    FormFieldWrapper,
    {
      name,
      label,
      required,
      helpText,
      children: (field) => {
        const handleSelectChange = (value) => {
          field.handleChange(value);
          field.handleBlur();
        };
        const hasError = shouldShowErrors(field);
        return /* @__PURE__ */ jsx31(
          SelectSingle,
          {
            options,
            value: field.state.value,
            onChange: handleSelectChange,
            onBlur: field.handleBlur,
            placeholder,
            isLoading,
            isClearable,
            size,
            className: cn28(
              className,
              hasError && "border-destructive focus-visible:border-destructive"
            )
          }
        );
      }
    }
  );
}

// src/components/form/components/FormSubmit.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
function FormSubmit({
  children,
  className,
  disabled,
  loadingText,
  variant = "primary",
  size = "md",
  rounded = "default"
}) {
  const form = useFormContext();
  return /* @__PURE__ */ jsx32(
    form.Subscribe,
    {
      selector: (state) => [state.canSubmit, state.isSubmitting],
      children: ([canSubmit, isSubmitting]) => /* @__PURE__ */ jsx32(
        Button,
        {
          type: "submit",
          variant,
          size,
          rounded,
          disabled: !canSubmit || disabled,
          className,
          children: isSubmitting && loadingText ? loadingText : children
        }
      )
    }
  );
}

// src/components/form/components/FormTextarea.tsx
import { cn as cn29 } from "@tessel/utils";
import { jsx as jsx33 } from "react/jsx-runtime";
function FormTextarea({
  name,
  label,
  placeholder,
  className,
  required,
  helpText,
  rows = 4,
  size = "md"
}) {
  return /* @__PURE__ */ jsx33(
    FormFieldWrapper,
    {
      name,
      label,
      required,
      helpText,
      children: (field) => {
        const handleTextareaChange = (e) => {
          field.handleChange(e.target.value);
          field.handleBlur();
        };
        const hasError = shouldShowErrors(field);
        return /* @__PURE__ */ jsx33(
          Textarea,
          {
            id: field.name,
            placeholder: placeholder || "",
            value: field.state.value || "",
            onChange: handleTextareaChange,
            onBlur: field.handleBlur,
            rows,
            size,
            error: hasError,
            className: cn29(className)
          }
        );
      }
    }
  );
}

// src/components/textarea-with-actions/TextareaWithActions.tsx
import { cn as cn30 } from "@tessel/utils";
import { useCallback as useCallback3 } from "react";
import { jsx as jsx34, jsxs as jsxs17 } from "react/jsx-runtime";
function TextareaWithActions({
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  disabled = false,
  footerLeft,
  footerRight,
  onSubmit,
  isLoading = false,
  hasError = false,
  className,
  textareaClassName,
  footerClassName
}) {
  const handleChange = useCallback3(
    (e) => {
      const newValue = e.target.value;
      if (maxLength && newValue.length > maxLength) {
        return;
      }
      onChange(newValue);
    },
    [onChange, maxLength]
  );
  const handleKeyDown = useCallback3(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey && onSubmit && value.trim()) {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit, value]
  );
  const hasFooter = footerLeft || footerRight;
  return /* @__PURE__ */ jsxs17(
    "div",
    {
      className: cn30(
        "rounded-lg border border-input bg-background overflow-hidden",
        "focus-within:ring-1 focus-within:ring-ring",
        hasError && "border-destructive focus-within:ring-destructive",
        className
      ),
      children: [
        /* @__PURE__ */ jsx34(
          "textarea",
          {
            value,
            onChange: handleChange,
            onKeyDown: handleKeyDown,
            placeholder,
            rows,
            disabled: disabled || isLoading,
            className: cn30(
              "flex min-h-[80px] w-full bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none border-0",
              textareaClassName
            )
          }
        ),
        hasFooter && /* @__PURE__ */ jsxs17(
          "div",
          {
            className: cn30(
              "flex items-center justify-between px-3 py-2 border-t border-input bg-muted/30",
              footerClassName
            ),
            children: [
              /* @__PURE__ */ jsx34("div", { className: "flex items-center gap-2", children: footerLeft }),
              /* @__PURE__ */ jsx34("div", { className: "flex items-center gap-2", children: footerRight })
            ]
          }
        )
      ]
    }
  );
}

// src/components/form/components/FormTextareaWithActions.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
function FormTextareaWithActions({
  name,
  label,
  className,
  required,
  helpText,
  rows = 3,
  maxLength,
  placeholder,
  footerLeft,
  footerRight,
  onSubmit,
  isLoading,
  textareaClassName,
  footerClassName
}) {
  return /* @__PURE__ */ jsx35(
    FormFieldWrapper,
    {
      name,
      label,
      required,
      helpText,
      children: (field) => {
        const handleChange = (value) => {
          field.handleChange(value);
          field.handleBlur();
        };
        return /* @__PURE__ */ jsx35(
          TextareaWithActions,
          {
            value: field.state.value || "",
            onChange: handleChange,
            placeholder,
            rows,
            maxLength,
            footerLeft,
            footerRight,
            onSubmit,
            isLoading,
            hasError: shouldShowErrors(field),
            className,
            textareaClassName,
            footerClassName
          }
        );
      }
    }
  );
}

// src/components/form/Form.tsx
var Form = {
  Root: FormRoot,
  Input: FormInput,
  Textarea: FormTextarea,
  TextareaWithActions: FormTextareaWithActions,
  SelectSingle: FormSelect,
  Submit: FormSubmit,
  Field: FormField,
  Custom: FormCustom
};

// src/components/form/IconRenderer.tsx
import {
  AlertTriangle,
  Award,
  Bookmark,
  Building,
  Calendar as Calendar2,
  Camera,
  Car,
  Check as Check2,
  CheckCircle,
  Clock,
  CloudLightning,
  CloudRain,
  CloudUpload,
  Copy,
  CreditCard,
  Cross,
  Download,
  Droplet,
  Dumbbell,
  Earth,
  Eye,
  EyeOff,
  FileText,
  Flag,
  Flame,
  Flower,
  FolderOpen,
  Gamepad2,
  Gift,
  GraduationCap,
  Heart,
  Home,
  Image as Image2,
  Info,
  Key,
  Leaf,
  Lock,
  Mail,
  MapPin,
  Medal,
  Minus,
  Music,
  Phone,
  Plane,
  Plus,
  RotateCcw as RotateCcw2,
  Save,
  Scissors,
  Search,
  Settings,
  Share,
  Shield,
  Ship,
  ShoppingCart,
  Snowflake,
  Star,
  Sun,
  Trophy,
  Unlock,
  User,
  UtensilsCrossed,
  Video,
  X as X2,
  XCircle
} from "lucide-react";
import { jsx as jsx36 } from "react/jsx-runtime";
var ICON_MAP = {
  // React-icons mapping
  IoDocumentText: FileText,
  IoFolderOpen: FolderOpen,
  IoBookmark: Bookmark,
  IoStar: Star,
  IoHeart: Heart,
  IoFlag: Flag,
  IoHome: Home,
  IoSettings: Settings,
  IoPerson: User,
  IoMail: Mail,
  IoCall: Phone,
  IoLocation: MapPin,
  IoCamera: Camera,
  IoImage: Image2,
  IoMusicalNotes: Music,
  IoVideocam: Video,
  IoGameController: Gamepad2,
  IoGift: Gift,
  IoCart: ShoppingCart,
  IoCard: CreditCard,
  IoTime: Clock,
  IoCalendar: Calendar2,
  IoBusiness: Building,
  IoSchool: GraduationCap,
  IoMedical: Cross,
  IoFitness: Dumbbell,
  IoRestaurant: UtensilsCrossed,
  IoCarSport: Car,
  IoAirplane: Plane,
  IoBoat: Ship,
  IoSunny: Sun,
  IoRainy: CloudRain,
  IoSnow: Snowflake,
  IoThunderstorm: CloudLightning,
  IoLeaf: Leaf,
  IoFlower: Flower,
  IoSearch: Search,
  IoAdd: Plus,
  IoRemove: Minus,
  IoCheckmark: Check2,
  IoClose: X2,
  IoRefresh: RotateCcw2,
  IoCopy: Copy,
  IoCut: Scissors,
  IoSave: Save,
  IoDownload: Download,
  IoCloudUpload: CloudUpload,
  IoShare: Share,
  IoLockClosed: Lock,
  IoLockOpen: Unlock,
  IoEye: Eye,
  IoEyeOff: EyeOff,
  IoShield: Shield,
  IoKey: Key,
  IoWarning: AlertTriangle,
  IoInformationCircle: Info,
  IoCheckmarkCircle: CheckCircle,
  IoCloseCircle: XCircle,
  IoTrophy: Trophy,
  IoMedal: Medal,
  IoRibbon: Award,
  IoFlame: Flame,
  IoWater: Droplet,
  IoEarth: Earth,
  // Lucide icon names mapping (direct)
  "file-text": FileText,
  "folder-open": FolderOpen,
  folder: FolderOpen,
  bookmark: Bookmark,
  star: Star,
  heart: Heart,
  flag: Flag,
  home: Home,
  settings: Settings,
  user: User,
  users: User,
  mail: Mail,
  phone: Phone,
  "map-pin": MapPin,
  camera: Camera,
  image: Image2,
  music: Music,
  video: Video,
  "gamepad-2": Gamepad2,
  gift: Gift,
  "shopping-cart": ShoppingCart,
  "credit-card": CreditCard,
  clock: Clock,
  calendar: Calendar2,
  building: Building,
  "graduation-cap": GraduationCap,
  stethoscope: Cross,
  dumbbell: Dumbbell,
  utensils: UtensilsCrossed,
  car: Car,
  plane: Plane,
  ship: Ship,
  sun: Sun,
  "cloud-rain": CloudRain,
  snowflake: Snowflake,
  zap: CloudLightning,
  leaf: Leaf,
  flower: Flower,
  search: Search,
  plus: Plus,
  minus: Minus,
  check: Check2,
  x: X2,
  "refresh-cw": RotateCcw2,
  copy: Copy,
  scissors: Scissors,
  save: Save,
  download: Download,
  upload: CloudUpload,
  share: Share,
  lock: Lock,
  unlock: Unlock,
  eye: Eye,
  "eye-off": EyeOff,
  shield: Shield,
  key: Key,
  "alert-triangle": AlertTriangle,
  info: Info,
  "check-circle": CheckCircle,
  "x-circle": XCircle,
  trophy: Trophy,
  award: Award,
  target: Award,
  flame: Flame,
  droplets: Droplet,
  globe: Earth
};
var IconRenderer = ({
  iconName,
  className = "w-4 h-4",
  fallback: FallbackIcon = FileText
}) => {
  const IconComponent = iconName ? ICON_MAP[iconName] : null;
  if (!IconComponent) {
    return /* @__PURE__ */ jsx36(FallbackIcon, { className });
  }
  return /* @__PURE__ */ jsx36(IconComponent, { className });
};

// src/components/form/Input.tsx
import { cn as cn32 } from "@tessel/utils";
import { HelpCircle } from "lucide-react";
import { forwardRef as forwardRef6 } from "react";

// src/components/form/Label.tsx
import { cn as cn31 } from "@tessel/utils";
import { forwardRef as forwardRef5 } from "react";
import { jsx as jsx37 } from "react/jsx-runtime";
var Label3 = forwardRef5(
  ({ id, children, className, htmlFor }, ref) => {
    return /* @__PURE__ */ jsx37(
      "label",
      {
        ref,
        id: `${id}-label`,
        htmlFor,
        className: cn31("block text-sm font-medium", className),
        children
      }
    );
  }
);
Label3.displayName = "Label";

// src/components/form/Input.tsx
import { jsx as jsx38, jsxs as jsxs18 } from "react/jsx-runtime";
var Input = forwardRef6(
  ({
    label,
    helpMessage,
    name,
    id,
    error,
    type = "text",
    value,
    onChange,
    disabled = false,
    className = "",
    required = false,
    leftIcon,
    rightIcon,
    rightAction,
    size = "md",
    placeholder
  }, ref) => {
    const hasIcons = Boolean(leftIcon || rightIcon || rightAction);
    const inputStyles = baseInputStyles({
      size,
      disabled,
      error: Boolean(error),
      className: cn32(
        leftIcon && "pl-10",
        (rightIcon || rightAction) && "pr-10",
        className
      )
    });
    return /* @__PURE__ */ jsxs18("div", { className: "space-y-2 w-full", children: [
      label && /* @__PURE__ */ jsxs18(Label3, { htmlFor: id, id, className: "block text-sm font-medium", children: [
        label,
        helpMessage && /* @__PURE__ */ jsxs18(Tooltip, { children: [
          /* @__PURE__ */ jsx38(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx38(HelpCircle, { className: "ml-1 inline-block cursor-help" }) }),
          /* @__PURE__ */ jsx38(TooltipContent, { children: /* @__PURE__ */ jsx38("p", { children: helpMessage }) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs18("div", { className: cn32("relative", hasIcons && "flex items-center"), children: [
        leftIcon && /* @__PURE__ */ jsx38("div", { className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none", children: leftIcon }),
        /* @__PURE__ */ jsx38(
          "input",
          {
            ref,
            id,
            name: name || id,
            "aria-labelledby": label ? `${id}-label` : void 0,
            "aria-describedby": helpMessage ? `${id}-help` : void 0,
            "aria-invalid": Boolean(error),
            "aria-errormessage": error ? `${id}-error` : void 0,
            "data-testid": `input-${id}`,
            type,
            value,
            onChange,
            disabled,
            required,
            placeholder,
            className: inputStyles
          }
        ),
        rightIcon && /* @__PURE__ */ jsx38("div", { className: "absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none", children: rightIcon }),
        rightAction && /* @__PURE__ */ jsx38("div", { className: "absolute right-1.5 top-1/2 transform -translate-y-1/2", children: rightAction })
      ] }),
      error && /* @__PURE__ */ jsx38(
        "p",
        {
          className: "text-sm text-destructive",
          id: `${id}-error`,
          role: "alert",
          children: error
        }
      ),
      helpMessage && /* @__PURE__ */ jsx38("p", { className: "text-sm text-muted-foreground", id: `${id}-help`, children: helpMessage })
    ] });
  }
);
Input.displayName = "Input";

// src/components/icon/Icon.tsx
import { DynamicIcon } from "lucide-react/dynamic";
import * as React3 from "react";
import { jsx as jsx39 } from "react/jsx-runtime";
var Icon = React3.forwardRef(
  ({ name, ...props }, ref) => {
    return /* @__PURE__ */ jsx39(DynamicIcon, { name, ...props, ref });
  }
);
Icon.displayName = "Icon";

// src/components/icon-picker/IconPicker.tsx
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn as cn33 } from "@tessel/utils";
import Fuse from "fuse.js";
import * as React4 from "react";
import { useCallback as useCallback4, useEffect as useEffect5, useMemo as useMemo3, useState as useState5 } from "react";
import { useDebounceValue } from "usehooks-ts";
import { Fragment as Fragment2, jsx as jsx40, jsxs as jsxs19 } from "react/jsx-runtime";
var IconRenderer2 = React4.memo(({ name }) => {
  return /* @__PURE__ */ jsx40(Icon, { name });
});
IconRenderer2.displayName = "IconRenderer";
var IconsColumnSkeleton = () => {
  const skeletonKeys = useMemo3(
    () => Array.from(
      { length: 40 },
      (_, i) => `skeleton-${Math.random().toString(36).substr(2, 9)}-${i}`
    ),
    []
  );
  return /* @__PURE__ */ jsxs19("div", { className: "flex flex-col gap-2 w-full", children: [
    /* @__PURE__ */ jsx40(Skeleton, { className: "h-4 w-1/2 rounded-md" }),
    /* @__PURE__ */ jsx40("div", { className: "grid grid-cols-5 gap-2 w-full", children: skeletonKeys.map((key) => /* @__PURE__ */ jsx40(Skeleton, { className: "h-10 w-10 rounded-md" }, key)) })
  ] });
};
var useIconsData = (enabled) => {
  const [icons, setIcons] = useState5([]);
  const [isLoading, setIsLoading] = useState5(false);
  useEffect5(() => {
    if (!enabled || icons.length > 0) {
      return;
    }
    let isMounted = true;
    setIsLoading(true);
    import("./icons-data-V5XPDHL6.js").then(({ iconsData }) => {
      if (isMounted) {
        setIcons(iconsData);
        setIsLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [enabled, icons.length]);
  return { icons, isLoading };
};
var IconPicker = React4.forwardRef(
  ({
    value,
    defaultValue,
    onValueChange,
    open,
    defaultOpen,
    onOpenChange,
    children,
    searchable = true,
    searchPlaceholder,
    triggerPlaceholder,
    otherCategoryLabel,
    allIconsCategoryLabel,
    iconsList,
    categorized = true,
    modal = false,
    ...props
  }, ref) => {
    const searchInputId = React4.useId();
    const [selectedIcon, setSelectedIcon] = useState5(
      defaultValue
    );
    const [isOpen, setIsOpen] = useState5(defaultOpen);
    const [search, setSearch] = useDebounceValue("", 100);
    const [isPopoverVisible, setIsPopoverVisible] = useState5(false);
    const [hasOpenedOnce, setHasOpenedOnce] = useState5(false);
    const { icons } = useIconsData(hasOpenedOnce);
    const [isLoading, setIsLoading] = useState5(true);
    const iconsToUse = useMemo3(() => iconsList || icons, [iconsList, icons]);
    const fuseInstance = useMemo3(() => {
      return new Fuse(iconsToUse, {
        keys: ["name", "tags", "categories"],
        threshold: 0.3,
        ignoreLocation: true,
        includeScore: true
      });
    }, [iconsToUse]);
    const filteredIcons = useMemo3(() => {
      if (search.trim() === "") {
        return iconsToUse;
      }
      const results = fuseInstance.search(search.toLowerCase().trim());
      return results.map((result) => result.item);
    }, [search, iconsToUse, fuseInstance]);
    const categorizedIcons = useMemo3(() => {
      if (!categorized || search.trim() !== "") {
        return [
          {
            name: allIconsCategoryLabel ?? "",
            icons: filteredIcons
          }
        ];
      }
      const categories = /* @__PURE__ */ new Map();
      filteredIcons.forEach((icon) => {
        if (icon.categories && icon.categories.length > 0) {
          icon.categories.forEach((category) => {
            if (!categories.has(category)) {
              categories.set(category, []);
            }
            categories.get(category)?.push(icon);
          });
        } else {
          const category = otherCategoryLabel ?? "";
          if (!categories.has(category)) {
            categories.set(category, []);
          }
          categories.get(category)?.push(icon);
        }
      });
      return Array.from(categories.entries()).map(([name, icons2]) => ({ name, icons: icons2 })).sort((a, b) => a.name.localeCompare(b.name));
    }, [filteredIcons, categorized, search]);
    const virtualItems = useMemo3(() => {
      const items = [];
      categorizedIcons.forEach((category, categoryIndex) => {
        items.push({ type: "category", categoryIndex });
        const rows = [];
        for (let i = 0; i < category.icons.length; i += 5) {
          rows.push(category.icons.slice(i, i + 5));
        }
        rows.forEach((rowIcons, rowIndex) => {
          items.push({
            type: "row",
            categoryIndex,
            rowIndex,
            icons: rowIcons
          });
        });
      });
      return items;
    }, [categorizedIcons]);
    const categoryIndices = useMemo3(() => {
      const indices = {};
      virtualItems.forEach((item, index) => {
        if (item.type === "category") {
          const categoryData = categorizedIcons[item.categoryIndex];
          if (categoryData) {
            indices[categoryData.name] = index;
          }
        }
      });
      return indices;
    }, [virtualItems, categorizedIcons]);
    const parentRef = React4.useRef(null);
    const virtualizer = useVirtualizer({
      count: virtualItems.length,
      getScrollElement: () => parentRef.current,
      estimateSize: (index) => virtualItems[index]?.type === "category" ? 25 : 40,
      paddingEnd: 2,
      gap: 10,
      overscan: 5
    });
    const handleValueChange = useCallback4(
      (icon) => {
        if (value === void 0) {
          setSelectedIcon(icon);
        }
        onValueChange?.(icon);
      },
      [value, onValueChange]
    );
    const handleOpenChange = useCallback4(
      (newOpen) => {
        setSearch("");
        if (open === void 0) {
          setIsOpen(newOpen);
        }
        onOpenChange?.(newOpen);
        setIsPopoverVisible(newOpen);
        if (newOpen) {
          setHasOpenedOnce(true);
          setTimeout(() => {
            virtualizer.measure();
            setIsLoading(false);
          }, 1);
        }
      },
      [open, onOpenChange, virtualizer, setSearch]
    );
    const handleIconClick = useCallback4(
      (iconName) => {
        handleValueChange(iconName);
        setIsOpen(false);
        setSearch("");
      },
      [handleValueChange, setSearch]
    );
    const handleSearchChange = useCallback4(
      (e) => {
        setSearch(e.target.value);
        if (parentRef.current) {
          parentRef.current.scrollTop = 0;
        }
        virtualizer.scrollToOffset(0);
      },
      [virtualizer, setSearch]
    );
    const scrollToCategory = useCallback4(
      (categoryName) => {
        const categoryIndex = categoryIndices[categoryName];
        if (categoryIndex !== void 0 && virtualizer) {
          virtualizer.scrollToIndex(categoryIndex, {
            align: "start",
            behavior: "smooth"
          });
        }
      },
      [categoryIndices, virtualizer]
    );
    const categoryButtons = useMemo3(() => {
      if (!categorized || search.trim() !== "") {
        return null;
      }
      return categorizedIcons.map((category) => /* @__PURE__ */ jsx40(
        Button,
        {
          variant: "outline",
          size: "sm",
          className: "text-xs",
          onClick: (e) => {
            e.stopPropagation();
            scrollToCategory(category.name);
          },
          children: category.name.charAt(0).toUpperCase() + category.name.slice(1)
        },
        category.name
      ));
    }, [categorizedIcons, scrollToCategory, categorized, search]);
    const renderIcon = useCallback4(
      (icon) => /* @__PURE__ */ jsx40(TooltipProvider, { children: /* @__PURE__ */ jsxs19(Tooltip, { children: [
        /* @__PURE__ */ jsx40(
          TooltipTrigger,
          {
            className: cn33(
              "p-2 rounded-md border hover:bg-foreground/10 transition",
              "flex items-center justify-center"
            ),
            onClick: () => handleIconClick(icon.name),
            children: /* @__PURE__ */ jsx40(IconRenderer2, { name: icon.name })
          }
        ),
        /* @__PURE__ */ jsx40(TooltipContent, { children: /* @__PURE__ */ jsx40("p", { children: icon.name }) })
      ] }) }, icon.name),
      [handleIconClick]
    );
    const renderVirtualContent = useCallback4(() => {
      if (filteredIcons.length === 0) {
        return /* @__PURE__ */ jsx40("div", { className: "text-center text-gray-500", children: "No icon found" });
      }
      return /* @__PURE__ */ jsx40(
        "div",
        {
          className: "relative w-full overscroll-contain",
          style: {
            height: `${virtualizer.getTotalSize()}px`
          },
          children: virtualizer.getVirtualItems().map((virtualItem) => {
            const item = virtualItems[virtualItem.index];
            if (!item) {
              return null;
            }
            const itemStyle = {
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`
            };
            if (item.type === "category") {
              return /* @__PURE__ */ jsxs19(
                "div",
                {
                  style: itemStyle,
                  className: "top-0 bg-background z-10",
                  children: [
                    /* @__PURE__ */ jsx40("h3", { className: "font-medium text-sm capitalize", children: categorizedIcons[item.categoryIndex]?.name || "Unknown" }),
                    /* @__PURE__ */ jsx40("div", { className: "h-[1px] bg-foreground/10 w-full" })
                  ]
                },
                virtualItem.key
              );
            }
            return /* @__PURE__ */ jsx40(
              "div",
              {
                "data-index": virtualItem.index,
                style: itemStyle,
                children: /* @__PURE__ */ jsx40("div", { className: "grid grid-cols-5 gap-2 w-full", children: item.icons?.map(renderIcon) })
              },
              virtualItem.key
            );
          })
        }
      );
    }, [virtualizer, virtualItems, categorizedIcons, filteredIcons, renderIcon]);
    React4.useEffect(() => {
      if (isPopoverVisible) {
        setIsLoading(true);
        const timer = setTimeout(() => {
          setIsLoading(false);
          virtualizer.measure();
        }, 10);
        const resizeObserver = new ResizeObserver(() => {
          virtualizer.measure();
        });
        if (parentRef.current) {
          resizeObserver.observe(parentRef.current);
        }
        return () => {
          clearTimeout(timer);
          resizeObserver.disconnect();
        };
      }
    }, [isPopoverVisible, virtualizer]);
    return /* @__PURE__ */ jsxs19(
      Popover,
      {
        open: open ?? isOpen,
        onOpenChange: handleOpenChange,
        modal,
        children: [
          /* @__PURE__ */ jsx40(PopoverTrigger, { ref, asChild: true, ...props, children: children || /* @__PURE__ */ jsx40(Button, { variant: "outline", children: value || selectedIcon ? /* @__PURE__ */ jsxs19(Fragment2, { children: [
            /* @__PURE__ */ jsx40(Icon, { name: value || selectedIcon }),
            " ",
            value || selectedIcon
          ] }) : triggerPlaceholder }) }),
          /* @__PURE__ */ jsxs19(PopoverContent, { className: "w-64 p-2", children: [
            searchable && /* @__PURE__ */ jsx40(
              Input,
              {
                id: searchInputId,
                value: search,
                placeholder: searchPlaceholder,
                onChange: handleSearchChange,
                className: "mb-2"
              }
            ),
            categorized && search.trim() === "" && /* @__PURE__ */ jsx40("div", { className: "flex flex-row gap-1 mt-2 overflow-x-auto pb-2", children: categoryButtons }),
            /* @__PURE__ */ jsx40(
              "div",
              {
                ref: parentRef,
                className: "max-h-60 overflow-auto",
                style: { scrollbarWidth: "thin" },
                children: isLoading ? /* @__PURE__ */ jsx40(IconsColumnSkeleton, {}) : renderVirtualContent()
              }
            )
          ] })
        ]
      }
    );
  }
);
IconPicker.displayName = "IconPicker";

// src/components/language-switcher/LanguageSwitcher.tsx
import { LOCALE_FLAGS, LOCALE_NAMES, SUPPORTED_LOCALES } from "@tessel/utils";
import { Check as Check3 } from "lucide-react";
import { jsx as jsx41, jsxs as jsxs20 } from "react/jsx-runtime";
var LanguageSwitcher = ({
  locale,
  onLocaleChange,
  align = "end",
  triggerLabel = "Change language"
}) => {
  const currentFlag = LOCALE_FLAGS[locale];
  return /* @__PURE__ */ jsxs20(DropdownMenu, { children: [
    /* @__PURE__ */ jsx41(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx41(
      "button",
      {
        type: "button",
        "aria-label": triggerLabel,
        className: "flex h-8 w-8 items-center justify-center rounded-md text-base text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer",
        children: /* @__PURE__ */ jsx41("span", { "aria-hidden": "true", children: currentFlag })
      }
    ) }),
    /* @__PURE__ */ jsx41(DropdownMenuContent, { align, className: "min-w-[160px]", children: SUPPORTED_LOCALES.map((code) => /* @__PURE__ */ jsxs20(
      DropdownMenuItem,
      {
        onClick: () => onLocaleChange(code),
        className: "flex items-center justify-between gap-2 cursor-pointer",
        children: [
          /* @__PURE__ */ jsxs20("span", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx41("span", { children: LOCALE_FLAGS[code] }),
            /* @__PURE__ */ jsx41("span", { children: LOCALE_NAMES[code] })
          ] }),
          locale === code && /* @__PURE__ */ jsx41(Check3, { className: "size-4 text-primary" })
        ]
      },
      code
    )) })
  ] });
};

// src/components/input-otp/InputOTP.tsx
import { cn as cn34 } from "@tessel/utils";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";
import * as React5 from "react";
import { jsx as jsx42, jsxs as jsxs21 } from "react/jsx-runtime";
var sizeStyles = {
  sm: { slot: "h-8 w-8 text-sm", caret: "h-3" },
  md: { slot: "h-10 w-10 text-base", caret: "h-4" },
  lg: { slot: "h-12 w-12 text-lg", caret: "h-5" },
  xl: { slot: "h-14 w-14 text-xl font-semibold", caret: "h-6" }
};
var InputOTPSizeContext = React5.createContext("md");
function InputOTP({
  className,
  containerClassName,
  size = "md",
  ...props
}) {
  return /* @__PURE__ */ jsx42(InputOTPSizeContext.Provider, { value: size, children: /* @__PURE__ */ jsx42(
    OTPInput,
    {
      "data-slot": "input-otp",
      containerClassName: cn34(
        "flex items-center gap-2 has-disabled:opacity-50",
        containerClassName
      ),
      className: cn34("disabled:cursor-not-allowed", className),
      ...props
    }
  ) });
}
function InputOTPGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx42(
    "div",
    {
      "data-slot": "input-otp-group",
      className: cn34("flex items-center", className),
      ...props
    }
  );
}
function InputOTPSlot({
  index,
  className,
  ...props
}) {
  const inputOTPContext = React5.useContext(OTPInputContext);
  const size = React5.useContext(InputOTPSizeContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};
  const styles = sizeStyles[size];
  return /* @__PURE__ */ jsxs21(
    "div",
    {
      "data-slot": "input-otp-slot",
      "data-active": isActive,
      className: cn34(
        "data-[active=true]:border-ring data-[active=true]:ring-ring/50 data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive dark:bg-input/30 border-input relative flex items-center justify-center border-y border-r shadow-xs transition-all outline-none first:rounded-l-md first:border-l last:rounded-r-md data-[active=true]:z-10 data-[active=true]:ring-[3px]",
        styles.slot,
        className
      ),
      ...props,
      children: [
        char,
        hasFakeCaret && /* @__PURE__ */ jsx42("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx42(
          "div",
          {
            className: cn34(
              "animate-caret-blink bg-foreground w-px duration-1000",
              styles.caret
            )
          }
        ) })
      ]
    }
  );
}
function InputOTPSeparator({ ...props }) {
  return /* @__PURE__ */ jsx42("div", { "data-slot": "input-otp-separator", "aria-hidden": "true", ...props, children: /* @__PURE__ */ jsx42(MinusIcon, {}) });
}

// src/components/input-select/CampaignSelector.tsx
import { cn as cn35 } from "@tessel/utils";
import { jsx as jsx43 } from "react/jsx-runtime";
var CampaignSelector = ({
  campaigns,
  selectedCampaignId,
  onCampaignChange,
  placeholder,
  className,
  size,
  isDisabled = false,
  isLoading = false
}) => {
  return /* @__PURE__ */ jsx43(
    SelectSingle,
    {
      options: campaigns,
      value: selectedCampaignId,
      onChange: onCampaignChange,
      placeholder,
      className: cn35(
        // Reutiliza os estilos do design system
        "w-full",
        className
      ),
      size,
      isDisabled,
      isLoading,
      isClearable: false
    }
  );
};

// src/components/input-select/Select.tsx
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn as cn36 } from "@tessel/utils";
import { Check as Check4, ChevronDown as ChevronDown4 } from "lucide-react";
import * as React6 from "react";
import { jsx as jsx44, jsxs as jsxs22 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React6.forwardRef(({ className, children, size, density, error, ...props }, ref) => /* @__PURE__ */ jsxs22(
  SelectPrimitive.Trigger,
  {
    ref,
    "aria-invalid": error || void 0,
    className: cn36(
      fieldControlStyles({ size, density, error }).control(),
      "flex cursor-pointer items-center justify-between ring-offset-background",
      "focus:ring-2 focus:ring-ring focus:ring-offset-2",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx44(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx44(ChevronDown4, { className: "h-4 w-4 opacity-50" }) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectContent = React6.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx44(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsx44(
  SelectPrimitive.Content,
  {
    ref,
    className: cn36(
      "relative z-50 min-w-[8rem] overflow-hidden rounded-md border border-border bg-popover text-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: /* @__PURE__ */ jsx44(
      SelectPrimitive.Viewport,
      {
        className: cn36(
          "p-1",
          position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        ),
        children
      }
    )
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx44(
  SelectPrimitive.Label,
  {
    ref,
    className: cn36("py-1.5 pl-8 pr-2 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React6.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs22(
  SelectPrimitive.Item,
  {
    ref,
    className: cn36(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm p-4 py-2 text-sm outline-none hover:bg-foreground/10 data-[highlighted]:bg-foreground/10 focus:bg-foreground/10 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx44(SelectPrimitive.ItemText, { children }),
      /* @__PURE__ */ jsx44(SelectPrimitive.ItemIndicator, { className: "ml-auto", children: /* @__PURE__ */ jsx44(Check4, { className: "h-4 w-4" }) })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx44(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn36("-mx-1 my-1 h-px bg-border", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/components/input-select-card/InputSelectCard.tsx
import { cn as cn37 } from "@tessel/utils";
import { Check as Check5 } from "lucide-react";
import { jsx as jsx45, jsxs as jsxs23 } from "react/jsx-runtime";
var InputSelectCard = ({
  selected,
  onClick,
  children,
  className,
  showIndicator = true,
  disabled = false,
  variant = "list"
}) => {
  const isGrid = variant === "grid";
  return /* @__PURE__ */ jsx45(
    "button",
    {
      type: "button",
      onClick,
      disabled,
      className: cn37(
        "cursor-pointer rounded-lg border p-4 transition-all",
        isGrid ? "flex flex-1 flex-col items-center gap-3 border-2" : "w-full text-left",
        selected ? isGrid ? "border-primary bg-primary/10" : "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/50",
        disabled && "cursor-not-allowed opacity-50",
        className
      ),
      children: isGrid ? children : /* @__PURE__ */ jsxs23("div", { className: "flex items-start justify-between gap-3", children: [
        /* @__PURE__ */ jsx45("div", { className: "flex-1", children }),
        showIndicator && /* @__PURE__ */ jsx45(
          "div",
          {
            className: cn37(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
              selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/30"
            ),
            children: selected && /* @__PURE__ */ jsx45(Check5, { className: "h-3 w-3" })
          }
        )
      ] })
    }
  );
};
var InputSelectCardGroup = ({
  children,
  className
}) => {
  return /* @__PURE__ */ jsx45("div", { className: cn37("space-y-3", className), children });
};

// src/components/loading/Loading.tsx
import { cn as cn38 } from "@tessel/utils";
import { Loader2 } from "lucide-react";
import { jsx as jsx46, jsxs as jsxs24 } from "react/jsx-runtime";
var sizeClasses = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8"
};
var Loading = ({
  className,
  size = "md",
  text,
  variant = "spinner",
  ...props
}) => {
  if (variant === "spinner") {
    return /* @__PURE__ */ jsxs24("div", { className: cn38("flex items-center gap-2", className), ...props, children: [
      /* @__PURE__ */ jsx46(Loader2, { className: cn38("animate-spin", sizeClasses[size]) }),
      text && /* @__PURE__ */ jsx46("span", { className: "text-muted-foreground", children: text })
    ] });
  }
  if (variant === "dots") {
    return /* @__PURE__ */ jsxs24("div", { className: cn38("flex items-center gap-2", className), ...props, children: [
      /* @__PURE__ */ jsxs24("div", { className: "flex space-x-1", children: [
        /* @__PURE__ */ jsx46(
          "div",
          {
            className: cn38(
              "rounded-full bg-current animate-pulse",
              size === "sm" ? "h-1 w-1" : size === "md" ? "h-2 w-2" : "h-3 w-3"
            ),
            style: { animationDelay: "0ms" }
          }
        ),
        /* @__PURE__ */ jsx46(
          "div",
          {
            className: cn38(
              "rounded-full bg-current animate-pulse",
              size === "sm" ? "h-1 w-1" : size === "md" ? "h-2 w-2" : "h-3 w-3"
            ),
            style: { animationDelay: "150ms" }
          }
        ),
        /* @__PURE__ */ jsx46(
          "div",
          {
            className: cn38(
              "rounded-full bg-current animate-pulse",
              size === "sm" ? "h-1 w-1" : size === "md" ? "h-2 w-2" : "h-3 w-3"
            ),
            style: { animationDelay: "300ms" }
          }
        )
      ] }),
      text && /* @__PURE__ */ jsx46("span", { className: "text-muted-foreground", children: text })
    ] });
  }
  if (variant === "pulse") {
    return /* @__PURE__ */ jsxs24("div", { className: cn38("flex items-center gap-2", className), ...props, children: [
      /* @__PURE__ */ jsx46(
        "div",
        {
          className: cn38(
            "rounded-full bg-current animate-pulse",
            sizeClasses[size]
          )
        }
      ),
      text && /* @__PURE__ */ jsx46("span", { className: "text-muted-foreground", children: text })
    ] });
  }
  return null;
};
var LoadingOverlay = ({
  className,
  text,
  ...props
}) => {
  return /* @__PURE__ */ jsx46(
    "div",
    {
      className: cn38(
        "fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx46(Loading, { text, size: "lg" })
    }
  );
};
var LoadingInline = ({
  className,
  text,
  ...props
}) => {
  return /* @__PURE__ */ jsx46(
    "div",
    {
      className: cn38("flex items-center justify-center p-8", className),
      ...props,
      children: /* @__PURE__ */ jsx46(Loading, { text })
    }
  );
};

// src/components/logo/Logo.tsx
import { useCallback as useCallback5 } from "react";
import { useGlitch } from "react-powerglitch";
import { Fragment as Fragment3, jsx as jsx47, jsxs as jsxs25 } from "react/jsx-runtime";
var Logo = ({
  href,
  showDivider = true,
  size = "default",
  alt,
  lightModeSrc = "/logo-light.png",
  darkModeSrc = "/logo-dark.png"
}) => {
  const dimensions = size === "large" ? { width: 120, height: 33 } : { width: 80, height: 22 };
  const glitch = useGlitch({
    playMode: "click",
    createContainers: true,
    hideOverflow: false,
    timing: {
      duration: 500,
      iterations: 1
    },
    glitchTimeSpan: { start: 0, end: 1 },
    shake: { velocity: 15, amplitudeX: 0.02, amplitudeY: 0.02 },
    slice: {
      count: 6,
      velocity: 15,
      minHeight: 0.02,
      maxHeight: 0.15,
      hueRotate: true
    },
    pulse: false
  });
  const targetUrl = href || "/campaign";
  const isExternal = targetUrl.startsWith("http");
  const handleClick = useCallback5(
    (e) => {
      e.preventDefault();
      setTimeout(() => {
        window.location.href = targetUrl;
      }, 500);
    },
    [targetUrl]
  );
  const logoContent = /* @__PURE__ */ jsxs25(Fragment3, { children: [
    /* @__PURE__ */ jsxs25("div", { ref: glitch.ref, className: "cursor-pointer", children: [
      /* @__PURE__ */ jsx47(
        "img",
        {
          src: lightModeSrc,
          alt,
          width: dimensions.width,
          height: dimensions.height,
          className: "block dark:hidden"
        }
      ),
      /* @__PURE__ */ jsx47(
        "img",
        {
          src: darkModeSrc,
          alt,
          width: dimensions.width,
          height: dimensions.height,
          className: "hidden dark:block"
        }
      )
    ] }),
    showDivider && /* @__PURE__ */ jsx47("div", { className: "h-6 w-px ml-2 border rotate-12 border-solid border-border" })
  ] });
  return /* @__PURE__ */ jsx47(
    "a",
    {
      href: targetUrl,
      onClick: handleClick,
      target: isExternal ? "_blank" : void 0,
      rel: isExternal ? "noopener noreferrer" : void 0,
      className: "flex items-center gap-2 text-xl font-bold text-black dark:text-soft-white",
      children: logoContent
    }
  );
};

// src/components/markdown/ParseMarkdownLinks.tsx
import { useEffect as useEffect6, useRef as useRef5 } from "react";
import { jsx as jsx48 } from "react/jsx-runtime";
var ParseMarkdownLinks = ({
  children,
  className
}) => {
  const containerRef = useRef5(null);
  useEffect6(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const handleClick = (event) => {
      if (!event.target) {
        return;
      }
      const target = event.target;
      if (target.tagName === "A") {
        const anchorElement = target;
        const href = anchorElement.getAttribute("href");
        if (href?.startsWith("#")) {
          event.preventDefault();
          const targetElement = document.querySelector(href);
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    const handleKeyDown = (event) => {
      if (!event.target) {
        return;
      }
      const target = event.target;
      if (target.tagName === "A" && (event.key === "Enter" || event.key === " ")) {
        const anchorElement = target;
        const href = anchorElement.getAttribute("href");
        if (href?.startsWith("#")) {
          event.preventDefault();
          const targetElement = document.querySelector(href);
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    container.addEventListener("click", handleClick);
    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("click", handleClick);
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  return /* @__PURE__ */ jsx48("span", { ref: containerRef, className, children });
};

// src/components/markdown/ParseMarkdown.tsx
import { jsx as jsx49 } from "react/jsx-runtime";
var ParseMarkdown = ({
  children,
  className
}) => {
  if (typeof children !== "string") {
    return children;
  }
  return /* @__PURE__ */ jsx49(ParseMarkdownLinks, { className, children });
};

// src/components/menu/Menu.tsx
import { cn as cn39 } from "@tessel/utils";
import { jsx as jsx50 } from "react/jsx-runtime";
var Menu = ({ items, className }) => {
  return /* @__PURE__ */ jsx50(
    "div",
    {
      className: cn39(
        "sticky top-0 bg-secondary border-b border-border z-10 dark:shadow-[0_4px_0_0_var(--color-bg-600)] shadow-[0_4px_0_0_var(--color-bg-50)]",
        className
      ),
      children: /* @__PURE__ */ jsx50("ul", { className: "relative text-sm mx-auto flex w-full px-4", children: items.map((item) => /* @__PURE__ */ jsx50(
        "a",
        {
          href: item.href,
          className: cn39({
            "border-b-2 border-primary-300 mb-0": item.active
          }),
          children: /* @__PURE__ */ jsx50(
            "li",
            {
              className: cn39(
                "text-muted-foreground my-2 py-2 px-4",
                "hover:bg-accent hover:text-accent-foreground rounded-md font-semibold transition-colors",
                item.active && "text-primary-300 font-bold"
              ),
              children: item.label
            }
          )
        },
        item.href
      )) })
    }
  );
};

// src/components/menu-popover/MenuPopover.tsx
import * as PopoverPrimitive2 from "@radix-ui/react-popover";
import { cn as cn40 } from "@tessel/utils";
import * as React7 from "react";
import { jsx as jsx51 } from "react/jsx-runtime";
var MenuPopoverRoot = PopoverPrimitive2.Root;
var MenuPopoverTrigger = PopoverPrimitive2.Trigger;
var MenuPopoverContent = React7.forwardRef(({ className, align = "end", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx51(PopoverPrimitive2.Portal, { children: /* @__PURE__ */ jsx51(
  PopoverPrimitive2.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn40(
      "z-50 w-80 rounded-lg border border-border bg-popover p-4 shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
MenuPopoverContent.displayName = PopoverPrimitive2.Content.displayName;
var MenuPopover = {
  Root: MenuPopoverRoot,
  Trigger: MenuPopoverTrigger,
  Content: MenuPopoverContent
};

// src/components/page-header/PageHeader.tsx
import { cn as cn41 } from "@tessel/utils";
import { jsx as jsx52, jsxs as jsxs26 } from "react/jsx-runtime";
var variantStyles = {
  default: {
    root: "mb-8",
    row: "flex items-start justify-between gap-4",
    left: "flex items-start gap-3",
    titleWrap: "",
    iconWrap: "flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10",
    icon: "h-6 w-6 text-primary",
    title: "text-2xl font-semibold text-foreground",
    description: "text-muted-foreground mt-1",
    actions: "flex-shrink-0 self-start"
  },
  compact: {
    root: "",
    row: "flex items-center justify-between gap-4",
    left: "flex items-center gap-3 min-w-0",
    titleWrap: "min-w-0",
    iconWrap: "p-2 bg-primary/10 rounded-lg flex-shrink-0",
    icon: "w-5 h-5 text-primary",
    title: "text-xl font-semibold text-foreground truncate",
    description: "text-sm text-muted-foreground truncate",
    actions: "flex items-center gap-3 flex-shrink-0"
  }
};
var PageHeader = ({
  icon: Icon3,
  title,
  description,
  subtitle,
  actions,
  className,
  variant = "default"
}) => {
  const secondary = description ?? subtitle;
  const styles = variantStyles[variant];
  return /* @__PURE__ */ jsx52("div", { className: cn41(styles.root, className), children: /* @__PURE__ */ jsxs26("div", { className: styles.row, children: [
    /* @__PURE__ */ jsxs26("div", { className: styles.left, children: [
      Icon3 && /* @__PURE__ */ jsx52("div", { className: styles.iconWrap, children: /* @__PURE__ */ jsx52(Icon3, { className: styles.icon }) }),
      (title || secondary) && /* @__PURE__ */ jsxs26("div", { className: styles.titleWrap, children: [
        title && /* @__PURE__ */ jsx52("h1", { className: styles.title, children: title }),
        secondary && /* @__PURE__ */ jsx52("p", { className: styles.description, children: secondary })
      ] })
    ] }),
    actions && /* @__PURE__ */ jsx52("div", { className: styles.actions, children: actions })
  ] }) });
};

// src/components/pagination/Pagination.tsx
import { cn as cn42 } from "@tessel/utils";
import { ChevronLeft, ChevronRight as ChevronRight2, MoreHorizontal } from "lucide-react";
import { jsx as jsx53, jsxs as jsxs27 } from "react/jsx-runtime";
function Pagination({
  className,
  navAriaLabel,
  ...props
}) {
  return /* @__PURE__ */ jsx53(
    "nav",
    {
      "aria-label": navAriaLabel,
      "data-slot": "pagination",
      className: cn42("mx-auto flex w-full justify-center", className),
      ...props
    }
  );
}
function PaginationContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx53(
    "ul",
    {
      "data-slot": "pagination-content",
      className: cn42("flex flex-row items-center gap-1", className),
      ...props
    }
  );
}
function PaginationItem({ ...props }) {
  return /* @__PURE__ */ jsx53("li", { "data-slot": "pagination-item", ...props });
}
function PaginationLink({
  className,
  isActive,
  color: _color,
  ...props
}) {
  return /* @__PURE__ */ jsx53(
    Button,
    {
      "aria-current": isActive ? "page" : void 0,
      variant: isActive ? "outline" : "ghost",
      size: "sm",
      className: cn42("h-9 w-9 p-0", className),
      ...props
    }
  );
}
function PaginationPrevious({
  className,
  color: _color,
  previousAriaLabel,
  previousText,
  ...props
}) {
  return /* @__PURE__ */ jsxs27(
    Button,
    {
      "aria-label": previousAriaLabel,
      variant: "ghost",
      size: "sm",
      className: cn42("gap-1 px-2.5", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx53(ChevronLeft, { className: "h-4 w-4" }),
        previousText && /* @__PURE__ */ jsx53("span", { className: "hidden sm:block", children: previousText })
      ]
    }
  );
}
function PaginationNext({
  className,
  color: _color,
  nextAriaLabel,
  nextText,
  ...props
}) {
  return /* @__PURE__ */ jsxs27(
    Button,
    {
      "aria-label": nextAriaLabel,
      variant: "ghost",
      size: "sm",
      className: cn42("gap-1 px-2.5", className),
      ...props,
      children: [
        nextText && /* @__PURE__ */ jsx53("span", { className: "hidden sm:block", children: nextText }),
        /* @__PURE__ */ jsx53(ChevronRight2, { className: "h-4 w-4" })
      ]
    }
  );
}
function PaginationEllipsis({
  className,
  morePagesSrText,
  ...props
}) {
  return /* @__PURE__ */ jsxs27(
    "span",
    {
      "aria-hidden": true,
      "data-slot": "pagination-ellipsis",
      className: cn42("flex h-9 w-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx53(MoreHorizontal, { className: "h-4 w-4" }),
        morePagesSrText && /* @__PURE__ */ jsx53("span", { className: "sr-only", children: morePagesSrText })
      ]
    }
  );
}

// src/components/progress/ProgressBar.tsx
import { cn as cn43 } from "@tessel/utils";
import { tv as tv5 } from "tailwind-variants";
import { jsx as jsx54, jsxs as jsxs28 } from "react/jsx-runtime";
var progressBarVariants = tv5({
  slots: {
    container: "w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 relative overflow-hidden",
    bar: "bg-blue-600 h-2.5 rounded-full transition-all duration-700 ease-in-out relative",
    text: "text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
  },
  variants: {
    size: {
      sm: {
        container: "h-1.5",
        bar: "h-1.5",
        text: "text-xs"
      },
      md: {
        container: "h-2.5",
        bar: "h-2.5",
        text: "text-sm"
      },
      lg: {
        container: "h-4",
        bar: "h-4",
        text: "text-base"
      }
    },
    variant: {
      default: {
        bar: "bg-blue-600"
      },
      success: {
        bar: "bg-green-600"
      },
      warning: {
        bar: "bg-yellow-600"
      },
      error: {
        bar: "bg-red-600"
      }
    }
  },
  defaultVariants: {
    size: "md",
    variant: "default"
  }
});
var ProgressBar = ({
  value,
  max = 100,
  showText = true,
  label,
  size = "md",
  variant = "default",
  className
}) => {
  const { container, bar, text } = progressBarVariants({
    size,
    variant
  });
  const percentage = Math.min(Math.max(value / max * 100, 0), 100);
  return /* @__PURE__ */ jsxs28("div", { className, children: [
    (showText || label) && /* @__PURE__ */ jsxs28("div", { className: "flex justify-between items-center mb-1", children: [
      label && /* @__PURE__ */ jsx54("span", { className: text(), children: label }),
      showText && /* @__PURE__ */ jsxs28("span", { className: text(), children: [
        Math.round(percentage),
        "%"
      ] })
    ] }),
    /* @__PURE__ */ jsx54("div", { className: container(), children: /* @__PURE__ */ jsx54(
      "div",
      {
        className: cn43(
          "before:absolute",
          "before:inset-0",
          "before:bg-gradient-to-r",
          "before:from-transparent",
          "before:via-white/20",
          "before:to-transparent",
          "before:translate-x-[-100%]",
          "before:animate-[shimmer_2s_infinite]",
          "before:rounded-full",
          bar()
        ),
        style: { width: `${percentage}%` },
        role: "progressbar",
        "aria-valuenow": value,
        "aria-valuemin": 0,
        "aria-valuemax": max,
        "aria-label": label || `Progress: ${Math.round(percentage)}%`
      }
    ) })
  ] });
};

// src/components/reaction-button/ReactionButton.tsx
import { cn as cn44 } from "@tessel/utils";
import { cva } from "class-variance-authority";
import {
  Frown,
  Heart as Heart2,
  PartyPopper,
  Smile,
  Sparkles,
  ThumbsDown,
  ThumbsUp
} from "lucide-react";
import { jsx as jsx55, jsxs as jsxs29 } from "react/jsx-runtime";
var REACTION_ICONS = {
  like: ThumbsUp,
  dislike: ThumbsDown,
  love: Heart2,
  celebrate: PartyPopper,
  funny: Smile,
  sad: Frown,
  party: Sparkles
};
var FILLABLE_ICONS = ["love", "celebrate", "party"];
var reactionButtonVariants = cva("transition-all gap-1 rounded-full", {
  variants: {
    reactionType: {
      like: "",
      dislike: "",
      love: "",
      celebrate: "",
      funny: "",
      sad: "",
      party: ""
    },
    isActive: {
      true: "",
      false: "text-muted-foreground hover:text-foreground"
    },
    variant: {
      default: "gap-1.5 h-8 px-3",
      compact: "h-7 px-2 text-sm",
      icon: ""
    },
    size: {
      default: "",
      sm: ""
    }
  },
  compoundVariants: [
    // Active state colors per reaction type
    {
      reactionType: "like",
      isActive: true,
      className: "bg-blue-500/15 border-blue-500/50 hover:bg-blue-500/25"
    },
    {
      reactionType: "dislike",
      isActive: true,
      className: "bg-slate-400/15 border-slate-400/50 hover:bg-slate-400/25"
    },
    {
      reactionType: "love",
      isActive: true,
      className: "bg-rose-500/15 border-rose-500/50 hover:bg-rose-500/25"
    },
    {
      reactionType: "celebrate",
      isActive: true,
      className: "bg-amber-500/15 border-amber-500/50 hover:bg-amber-500/25"
    },
    {
      reactionType: "funny",
      isActive: true,
      className: "bg-green-500/15 border-green-500/50 hover:bg-green-500/25"
    },
    {
      reactionType: "sad",
      isActive: true,
      className: "bg-orange-500/15 border-orange-500/50 hover:bg-orange-500/25"
    },
    {
      reactionType: "party",
      isActive: true,
      className: "bg-violet-500/15 border-violet-500/50 hover:bg-violet-500/25"
    },
    // Inactive hover states
    {
      reactionType: "like",
      isActive: false,
      className: "hover:bg-blue-500/10"
    },
    {
      reactionType: "dislike",
      isActive: false,
      className: "hover:bg-slate-400/10"
    },
    {
      reactionType: "love",
      isActive: false,
      className: "hover:bg-rose-500/10"
    },
    {
      reactionType: "celebrate",
      isActive: false,
      className: "hover:bg-amber-500/10"
    },
    {
      reactionType: "funny",
      isActive: false,
      className: "hover:bg-green-500/10"
    },
    {
      reactionType: "sad",
      isActive: false,
      className: "hover:bg-orange-500/10"
    },
    {
      reactionType: "party",
      isActive: false,
      className: "hover:bg-violet-500/10"
    },
    // Compact + small size
    { variant: "compact", size: "sm", className: "h-6 px-1.5 text-xs" }
  ],
  defaultVariants: {
    isActive: false,
    variant: "default",
    size: "default"
  }
});
var reactionIconVariants = cva("", {
  variants: {
    reactionType: {
      like: "text-blue-500",
      dislike: "text-slate-400",
      love: "text-rose-500",
      celebrate: "text-amber-500",
      funny: "text-green-500",
      sad: "text-orange-500",
      party: "text-violet-500"
    },
    size: {
      default: "h-5 w-5",
      sm: "h-4 w-4",
      icon: "h-5 w-5"
    }
  },
  defaultVariants: {
    size: "default"
  }
});
var ReactionButton = ({
  reactionType,
  isActive = false,
  count,
  onClick,
  disabled,
  tooltipContent,
  variant = "default",
  size = "default",
  className
}) => {
  const Icon3 = REACTION_ICONS[reactionType];
  const canFill = FILLABLE_ICONS.includes(reactionType);
  const iconSize = variant === "icon" ? "icon" : size;
  const buttonVariant = variant === "default" ? "outline" : "ghost";
  const button = /* @__PURE__ */ jsxs29(
    Button,
    {
      variant: buttonVariant,
      size: variant === "icon" ? "icon-sm" : "sm",
      className: cn44(
        reactionButtonVariants({
          reactionType,
          isActive,
          variant,
          size
        }),
        className
      ),
      onClick,
      disabled,
      children: [
        /* @__PURE__ */ jsx55(
          Icon3,
          {
            className: reactionIconVariants({ reactionType, size: iconSize }),
            fill: isActive && canFill ? "currentColor" : "none",
            strokeWidth: 1.5
          }
        ),
        variant !== "icon" && count !== void 0 && count > 0 && /* @__PURE__ */ jsx55("span", { className: "font-medium text-xs", children: count })
      ]
    }
  );
  if (tooltipContent) {
    return /* @__PURE__ */ jsxs29(Tooltip, { children: [
      /* @__PURE__ */ jsx55(TooltipTrigger, { asChild: true, children: button }),
      /* @__PURE__ */ jsx55(TooltipContent, { children: /* @__PURE__ */ jsx55("p", { children: tooltipContent }) })
    ] });
  }
  return button;
};

// src/components/resizable/Resizable.tsx
import { cn as cn45 } from "@tessel/utils";
import { GripVertical } from "lucide-react";
import {
  Group as Group4,
  Panel,
  Separator as Separator4
} from "react-resizable-panels";
import { jsx as jsx56 } from "react/jsx-runtime";
function ResizablePanelGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx56(
    Group4,
    {
      "data-slot": "resizable-panel-group",
      className: cn45(
        "flex h-full w-full data-[orientation=vertical]:flex-col",
        className
      ),
      ...props
    }
  );
}
function ResizablePanel(props) {
  return /* @__PURE__ */ jsx56(Panel, { "data-slot": "resizable-panel", ...props });
}
function ResizableHandle({
  withHandle,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx56(
    Separator4,
    {
      "data-slot": "resizable-handle",
      className: cn45(
        "bg-border focus-visible:ring-ring relative flex w-px items-center justify-center",
        "after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2",
        "focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:outline-hidden",
        "data-[orientation=vertical]:h-px data-[orientation=vertical]:w-full",
        "data-[orientation=vertical]:after:left-0 data-[orientation=vertical]:after:h-1",
        "data-[orientation=vertical]:after:w-full data-[orientation=vertical]:after:translate-x-0",
        "data-[orientation=vertical]:after:-translate-y-1/2",
        "[&[data-orientation=vertical]>div]:rotate-90",
        className
      ),
      ...props,
      children: withHandle && /* @__PURE__ */ jsx56("div", { className: "bg-border z-10 flex h-4 w-3 items-center justify-center rounded-sm border", children: /* @__PURE__ */ jsx56(GripVertical, { className: "size-2.5" }) })
    }
  );
}

// src/components/scroll-area/ScrollArea.tsx
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cn as cn46 } from "@tessel/utils";
import * as React8 from "react";
import { jsx as jsx57, jsxs as jsxs30 } from "react/jsx-runtime";
var ScrollArea = React8.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs30(
  ScrollAreaPrimitive.Root,
  {
    ref,
    className: cn46("relative overflow-auto max-h-full", className),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx57(ScrollBar, {}),
      /* @__PURE__ */ jsx57(ScrollAreaPrimitive.Corner, {})
    ]
  }
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = React8.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx57(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn46(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx57(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

// src/components/search-input/SearchInput.tsx
import { cn as cn47 } from "@tessel/utils";
import { Search as Search2, X as X3 } from "lucide-react";
import { useId as useId2 } from "react";
import { jsx as jsx58, jsxs as jsxs31 } from "react/jsx-runtime";
var SearchInput = ({
  value,
  onChange,
  onClear,
  clearLabel,
  placeholder,
  className,
  inputClassName,
  id: idProp,
  ...inputProps
}) => {
  const generatedId = useId2();
  const inputId = idProp ?? generatedId;
  const handleClear = () => {
    onChange("");
    onClear?.();
  };
  return /* @__PURE__ */ jsxs31("div", { className: cn47("relative w-full", className), children: [
    /* @__PURE__ */ jsx58(
      Search2,
      {
        className: "pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground",
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsx58(
      BaseInput,
      {
        id: inputId,
        type: "search",
        value,
        onChange: (e) => {
          onChange(e.target.value);
        },
        placeholder,
        size: "md",
        className: cn47("pl-10 pr-10", inputClassName),
        ...inputProps
      }
    ),
    value ? /* @__PURE__ */ jsx58(
      "button",
      {
        type: "button",
        onClick: handleClear,
        className: "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground",
        "aria-label": clearLabel,
        children: /* @__PURE__ */ jsx58(X3, { className: "h-5 w-5", "aria-hidden": "true" })
      }
    ) : null
  ] });
};

// src/components/separator/Separator.tsx
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { cn as cn48 } from "@tessel/utils";
import * as React9 from "react";
import { jsx as jsx59 } from "react/jsx-runtime";
var Separator5 = React9.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx59(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: cn48(
        "shrink-0 bg-border opacity-50",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator5.displayName = SeparatorPrimitive.Root.displayName;

// src/components/sheet/Sheet.tsx
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cn as cn49 } from "@tessel/utils";
import { cva as cva2 } from "class-variance-authority";
import {
  ChevronLeft as ChevronLeft2,
  ChevronRight as ChevronRight3,
  Expand,
  Maximize,
  Minimize,
  MoreHorizontal as MoreHorizontal2,
  X as X4
} from "lucide-react";
import * as React10 from "react";

// src/router-adapter.tsx
import { createContext as createContext3, useContext as useContext3 } from "react";
var RouterAdapterContext = createContext3(null);
var RouterAdapterProvider = RouterAdapterContext.Provider;
var useRouterAdapter = () => {
  const adapter = useContext3(RouterAdapterContext);
  if (!adapter) {
    throw new Error(
      "useRouterAdapter requires <RouterAdapterProvider> mounted at the app root"
    );
  }
  return adapter;
};

// src/components/sheet/Sheet.tsx
import { jsx as jsx60, jsxs as jsxs32 } from "react/jsx-runtime";
var SheetUrlContext = React10.createContext(null);
var SheetUrlBridge = ({
  urlKey,
  children
}) => {
  const { params, pathname, replace } = useRouterAdapter();
  const section = params.get(urlKey);
  const tab = params.get(`${urlKey}-tab`);
  const open = section !== null;
  const buildUrl = React10.useCallback(
    (next) => {
      const sp = new URLSearchParams(params.toString());
      if (next.section === null) {
        sp.delete(urlKey);
        sp.delete(`${urlKey}-tab`);
      } else if (next.section !== void 0) {
        sp.set(urlKey, next.section);
      }
      if (next.tab === null) {
        sp.delete(`${urlKey}-tab`);
      } else if (next.tab !== void 0) {
        sp.set(`${urlKey}-tab`, next.tab);
      }
      const qs = sp.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [params, pathname, urlKey]
  );
  const setSection = React10.useCallback(
    (s) => replace(buildUrl({ section: s })),
    [replace, buildUrl]
  );
  const setTab = React10.useCallback(
    (t) => replace(buildUrl({ tab: t })),
    [replace, buildUrl]
  );
  const close = React10.useCallback(
    () => replace(buildUrl({ section: null, tab: null })),
    [replace, buildUrl]
  );
  const value = React10.useMemo(
    () => ({ section, tab, setSection, setTab, close }),
    [section, tab, setSection, setTab, close]
  );
  return /* @__PURE__ */ jsx60(SheetUrlContext.Provider, { value, children: /* @__PURE__ */ jsx60(
    SheetPrimitive.Root,
    {
      open,
      onOpenChange: (next) => {
        if (!next) {
          close();
        }
      },
      children
    }
  ) });
};
var useSheetUrl = () => {
  const ctx = React10.useContext(SheetUrlContext);
  if (!ctx) {
    throw new Error("useSheetUrl must be used inside <Sheet urlKey=...>");
  }
  return ctx;
};
var useSheetUrlController = (urlKey) => {
  const { params, pathname, replace } = useRouterAdapter();
  const open = React10.useCallback(
    (section = "general") => {
      const sp = new URLSearchParams(params.toString());
      sp.set(urlKey, section);
      const qs = sp.toString();
      replace(qs ? `${pathname}?${qs}` : pathname);
    },
    [params, pathname, replace, urlKey]
  );
  const close = React10.useCallback(() => {
    const sp = new URLSearchParams(params.toString());
    sp.delete(urlKey);
    sp.delete(`${urlKey}-tab`);
    const qs = sp.toString();
    replace(qs ? `${pathname}?${qs}` : pathname);
  }, [params, pathname, replace, urlKey]);
  return { open, close };
};
var Sheet = ({
  urlKey,
  open,
  onOpenChange,
  children,
  ...rest
}) => {
  if (urlKey) {
    return /* @__PURE__ */ jsx60(SheetUrlBridge, { urlKey, children });
  }
  return /* @__PURE__ */ jsx60(SheetPrimitive.Root, { open, onOpenChange, ...rest, children });
};
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
var SheetPortal = SheetPrimitive.Portal;
var SheetOverlay = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx60(
  SheetPrimitive.Overlay,
  {
    className: cn49(
      "fixed inset-0 z-50 bg-background/80 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
var sheetVariants = cva2(
  "fixed z-50 bg-muted-bg text-foreground border border-border shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      // `none` é usado pelo center-view: precisa de uma opção sem slide pra
      // não cair no default `right`. CVA usa defaultVariants quando o valor
      // recebido é `undefined`, então não dá pra simplesmente passar undefined.
      side: {
        none: "",
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right: "inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
      },
      viewMode: {
        "side-view": "w-1/2 min-w-[400px] max-w-[50vw]",
        // center-view: altura dinâmica até 85vh. Conteúdos pequenos (ex: só
        // drop-zone de upload) não ficam com vazio absurdo; conteúdos grandes
        // (ex: editor com tabs) ainda respeitam o teto da viewport e ganham
        // scroll interno. Animação: fade + zoom (sem slide). Largura
        // controlada pela variante `size` via compoundVariants.
        "center-view": "w-full max-h-[85vh] rounded-lg data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        // full-view: ocupa a tela inteira. Animação: só fade (sem slide nem
        // zoom, que ficariam estranhos em tela cheia).
        "full-view": "inset-0 w-full h-full max-w-full data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      },
      // Fixed-width sizes for center-view. Side-view stays viewport-relative
      // and full-view always covers the screen — both ignore this variant
      // via the compoundVariants below.
      size: {
        sm: "",
        md: "",
        xl: ""
      }
    },
    defaultVariants: {
      side: "right",
      viewMode: "side-view",
      size: "md"
    },
    compoundVariants: [
      {
        viewMode: "center-view",
        className: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      },
      { viewMode: "center-view", size: "sm", className: "max-w-md" },
      { viewMode: "center-view", size: "md", className: "max-w-2xl" },
      { viewMode: "center-view", size: "xl", className: "max-w-6xl" }
    ]
  }
);
var VIEW_MODE_ICONS = {
  "side-view": Minimize,
  "center-view": Maximize,
  "full-view": Expand
};
var SheetContent = React10.forwardRef(
  ({
    side = "right",
    viewMode = "side-view",
    size = "md",
    className,
    children,
    onViewModeChange,
    title,
    subtitle,
    titleAlign = "center",
    headerBorder = true,
    footer,
    viewModeOptions,
    lockViewMode = false,
    bodyClassName,
    closeAriaLabel,
    srTitle,
    viewModeSwitcherTitle,
    viewModeLabels,
    ...props
  }, ref) => {
    const [currentViewMode, setCurrentViewMode] = React10.useState(viewMode);
    const switchableModes = React10.useMemo(() => {
      if (lockViewMode || !viewModeOptions || viewModeOptions.length === 0) {
        return [viewMode];
      }
      return Array.from(/* @__PURE__ */ new Set([viewMode, ...viewModeOptions]));
    }, [lockViewMode, viewModeOptions, viewMode]);
    const showModeSwitcher = switchableModes.length >= 2;
    return /* @__PURE__ */ jsxs32(SheetPortal, { children: [
      /* @__PURE__ */ jsx60(SheetOverlay, {}),
      /* @__PURE__ */ jsxs32(
        SheetPrimitive.Content,
        {
          ref,
          className: cn49(
            sheetVariants({
              // center-view e full-view não têm direção: precisam de `none`
              // pra não cair no default `right` e ganhar slide indesejado.
              side: currentViewMode === "side-view" ? side : "none",
              viewMode: currentViewMode,
              size
            }),
            className,
            "flex flex-col"
            // Estrutura flexbox para header fixo, content com scroll, footer fixo
          ),
          ...props,
          children: [
            (title || srTitle) && /* @__PURE__ */ jsx60(SheetPrimitive.Title, { className: "sr-only", children: title ?? srTitle }),
            /* @__PURE__ */ jsxs32(
              "div",
              {
                className: cn49(
                  "relative flex items-center justify-between gap-4 p-4 shrink-0",
                  headerBorder && "border-b border-border"
                ),
                children: [
                  /* @__PURE__ */ jsxs32("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [
                    currentViewMode === "side-view" && /* @__PURE__ */ jsx60(SheetPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx60(
                      Button,
                      {
                        variant: "outline",
                        size: "icon-sm",
                        "aria-label": closeAriaLabel,
                        children: side === "right" ? /* @__PURE__ */ jsx60(ChevronRight3, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx60(ChevronLeft2, { className: "h-4 w-4" })
                      }
                    ) }),
                    showModeSwitcher && /* @__PURE__ */ jsxs32(DropdownMenu, { children: [
                      /* @__PURE__ */ jsx60(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx60(
                        Button,
                        {
                          variant: "ghost",
                          size: "icon-sm",
                          title: viewModeSwitcherTitle,
                          children: /* @__PURE__ */ jsx60(MoreHorizontal2, { className: "h-4 w-4" })
                        }
                      ) }),
                      /* @__PURE__ */ jsx60(DropdownMenuContent, { align: "start", children: switchableModes.map((mode) => {
                        const Icon3 = VIEW_MODE_ICONS[mode];
                        const label = viewModeLabels?.[mode];
                        return /* @__PURE__ */ jsxs32(
                          DropdownMenuItem,
                          {
                            onClick: () => {
                              setCurrentViewMode(mode);
                              onViewModeChange?.(mode);
                            },
                            className: currentViewMode === mode ? "bg-accent" : "",
                            children: [
                              /* @__PURE__ */ jsx60(Icon3, { className: "h-4 w-4 mr-2" }),
                              label
                            ]
                          },
                          mode
                        );
                      }) })
                    ] }),
                    title && titleAlign === "left" && /* @__PURE__ */ jsxs32("div", { className: "flex flex-col min-w-0", children: [
                      /* @__PURE__ */ jsx60("h2", { className: "text-lg font-semibold text-foreground truncate", children: title }),
                      subtitle && /* @__PURE__ */ jsx60("p", { className: "text-sm text-muted-foreground truncate", children: subtitle })
                    ] })
                  ] }),
                  title && titleAlign === "center" && /* @__PURE__ */ jsxs32("div", { className: "absolute left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none max-w-[60%]", children: [
                    /* @__PURE__ */ jsx60("h2", { className: "text-lg font-semibold text-foreground truncate w-full text-center", children: title }),
                    subtitle && /* @__PURE__ */ jsx60("p", { className: "text-sm text-muted-foreground truncate w-full text-center", children: subtitle })
                  ] }),
                  (currentViewMode === "center-view" || currentViewMode === "full-view") && /* @__PURE__ */ jsx60(SheetPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx60(
                    Button,
                    {
                      variant: "outline",
                      size: "icon-sm",
                      "aria-label": closeAriaLabel,
                      children: /* @__PURE__ */ jsx60(X4, { className: "h-4 w-4" })
                    }
                  ) })
                ]
              }
            ),
            /* @__PURE__ */ jsx60(
              "div",
              {
                className: cn49(
                  "flex-1 overflow-auto min-h-0",
                  (currentViewMode === "center-view" || currentViewMode === "side-view") && "p-6",
                  bodyClassName
                ),
                children
              }
            ),
            footer && /* @__PURE__ */ jsx60("div", { className: "shrink-0", children: footer })
          ]
        }
      )
    ] });
  }
);
SheetContent.displayName = SheetPrimitive.Content.displayName;
var SheetHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx60(
  "div",
  {
    className: cn49(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
SheetHeader.displayName = "SheetHeader";
var SheetFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx60(
  "div",
  {
    className: cn49(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx60(
  SheetPrimitive.Title,
  {
    ref,
    className: cn49("text-lg font-semibold text-foreground", className),
    ...props
  }
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
var SheetDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx60(
  SheetPrimitive.Description,
  {
    ref,
    className: cn49("text-sm text-muted-foreground", className),
    ...props
  }
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// src/components/sheet/ConfirmActionSheet.tsx
import { Loader2 as Loader22 } from "lucide-react";
import { jsx as jsx61, jsxs as jsxs33 } from "react/jsx-runtime";
var ConfirmActionSheet = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmText,
  cancelText,
  variant = "primary",
  onConfirm,
  isLoading = false
}) => {
  return /* @__PURE__ */ jsx61(Sheet, { open, onOpenChange, children: /* @__PURE__ */ jsx61(
    SheetContent,
    {
      viewMode: "center-view",
      size: "sm",
      lockViewMode: true,
      title,
      titleAlign: "left",
      headerBorder: false,
      bodyClassName: "px-6 py-3",
      footer: /* @__PURE__ */ jsxs33("div", { className: "flex items-center justify-end gap-2 px-4 pb-4 pt-2", children: [
        /* @__PURE__ */ jsx61(
          Button,
          {
            variant: "ghost",
            onClick: () => onOpenChange(false),
            disabled: isLoading,
            children: cancelText
          }
        ),
        /* @__PURE__ */ jsxs33(
          Button,
          {
            variant: variant === "primary" ? "primary" : variant,
            onClick: () => void onConfirm(),
            disabled: isLoading,
            children: [
              isLoading ? /* @__PURE__ */ jsx61(Loader22, { className: "mr-2 h-4 w-4 animate-spin" }) : null,
              confirmText
            ]
          }
        )
      ] }),
      children: children ?? /* @__PURE__ */ jsx61("p", { className: "text-sm text-muted-foreground", children: description })
    }
  ) });
};

// src/components/sidebar/Sidebar.tsx
import { ScrollArea as ScrollArea2 } from "@radix-ui/react-scroll-area";
import { Slot } from "@radix-ui/react-slot";
import { cn as cn50, useIsMobile } from "@tessel/utils";
import { ArrowBigLeft, ArrowBigRight } from "lucide-react";
import * as React11 from "react";
import { jsx as jsx62, jsxs as jsxs34 } from "react/jsx-runtime";
var SIDEBAR_WIDTH = "16rem";
var SIDEBAR_WIDTH_MOBILE = "18rem";
var SIDEBAR_WIDTH_ICON = "3.5rem";
var SIDEBAR_KEYBOARD_SHORTCUT = "b";
var SidebarContext = React11.createContext(null);
var useSidebar = () => {
  const context = React11.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
};
var SidebarProvider = React11.forwardRef(
  ({
    defaultOpen = true,
    open: openProp,
    onOpenChange: setOpenProp,
    className,
    style,
    children,
    ...props
  }, ref) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React11.useState(false);
    const [_open, _setOpen] = React11.useState(defaultOpen);
    const [hasAnimatedSidebar, setHasAnimatedSidebar] = React11.useState(false);
    const [mobileSidebarContent, setMobileSidebarContent] = React11.useState(null);
    const open = openProp ?? _open;
    const setOpen = React11.useCallback(
      (value) => {
        const openState = typeof value === "function" ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }
      },
      [setOpenProp, open]
    );
    const toggleSidebar = React11.useCallback(() => {
      return isMobile ? setOpenMobile((open2) => !open2) : setOpen((open2) => !open2);
    }, [isMobile, setOpen]);
    React11.useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);
    React11.useEffect(() => {
      if (!openMobile) {
        setHasAnimatedSidebar(false);
      }
    }, [openMobile]);
    const state = open ? "expanded" : "collapsed";
    const contextValue = React11.useMemo(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        hasAnimatedSidebar,
        setHasAnimatedSidebar,
        mobileSidebarContent,
        setMobileSidebarContent
      }),
      [
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        toggleSidebar,
        hasAnimatedSidebar,
        mobileSidebarContent
      ]
    );
    return /* @__PURE__ */ jsx62(SidebarContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx62(TooltipProvider, { delayDuration: 0, children: /* @__PURE__ */ jsx62(
      "div",
      {
        style: {
          "--sidebar-width": SIDEBAR_WIDTH,
          "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
          ...style
        },
        className: cn50("flex flex-1 h-full w-full", className),
        ref,
        ...props,
        children
      }
    ) }) });
  }
);
SidebarProvider.displayName = "SidebarProvider";
var Sidebar = React11.forwardRef(
  ({
    side = "left",
    variant = "sidebar",
    collapsible = "offcanvas",
    className,
    children,
    ...props
  }, ref) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
    if (collapsible === "none") {
      return /* @__PURE__ */ jsx62("div", { ref, className, ...props, children });
    }
    if (isMobile) {
      return /* @__PURE__ */ jsx62(Sheet, { open: openMobile, onOpenChange: setOpenMobile, ...props, children: /* @__PURE__ */ jsx62(
        SheetContent,
        {
          "data-sidebar": "sidebar",
          "data-mobile": "true",
          className: cn50(
            "w-[--sidebar-width]",
            "dark:bg-slate-600 bg-white",
            "dark:text-slate-50 text-slate-900",
            "p-0 [&>button]:hidden",
            className
          ),
          style: {
            "--sidebar-width": SIDEBAR_WIDTH_MOBILE
          },
          side,
          children: /* @__PURE__ */ jsx62("div", { className: "flex h-full w-full flex-col", children })
        }
      ) });
    }
    return /* @__PURE__ */ jsx62(
      "div",
      {
        ref,
        className: cn50(
          "hidden md:flex flex-shrink-0",
          "text-panel-foreground",
          className
        ),
        "data-state": state,
        "data-collapsible": state === "collapsed" ? collapsible : "",
        "data-variant": variant,
        "data-side": side,
        children: /* @__PURE__ */ jsx62(
          "div",
          {
            className: cn50(
              "flex flex-col",
              "transition-[width] duration-200 ease-linear",
              "flex w-[--sidebar-width] flex-shrink-0 flex-col",
              state === "collapsed" && collapsible === "offcanvas" && "w-[--sidebar-width-icon]",
              state === "collapsed" && collapsible === "icon" && "w-[--sidebar-width-icon]",
              state === "expanded" && "w-[--sidebar-width]",
              variant === "floating" && "shadow-sm",
              variant === "inset" && "m-2"
            ),
            ...props,
            children: /* @__PURE__ */ jsx62("div", { "data-sidebar": "sidebar", className: "flex flex-col h-full", children })
          }
        )
      }
    );
  }
);
Sidebar.displayName = "Sidebar";
var SidebarTrigger = React11.forwardRef(
  ({ className, onClick, location, ...props }, ref) => {
    const { toggleSidebar, state, isMobile } = useSidebar();
    const onClickHandler = (event) => {
      onClick?.(event);
      toggleSidebar();
    };
    if (isMobile) {
      if (location !== "navbar") {
        return null;
      }
      return /* @__PURE__ */ jsx62(
        Button,
        {
          ref,
          "data-sidebar": "trigger",
          variant: "ghost",
          size: "md",
          className: cn50("h-7 w-7 flex-1", className),
          onClick: onClickHandler,
          ...props,
          children: /* @__PURE__ */ jsx62("div", { className: "rotate-90", children: "|||" })
        }
      );
    }
    return /* @__PURE__ */ jsxs34(
      Button,
      {
        ref,
        "data-sidebar": "trigger",
        variant: "ghost",
        size: "md",
        className: cn50(
          "",
          location === "navbar" && state === "expanded" && "hidden",
          location !== "navbar" && state === "collapsed" && "hidden",
          className
        ),
        onClick: onClickHandler,
        ...props,
        children: [
          location !== "navbar" ? /* @__PURE__ */ jsx62(ArrowBigLeft, {}) : /* @__PURE__ */ jsx62(ArrowBigRight, {}),
          /* @__PURE__ */ jsx62("span", { className: "sr-only", children: "Toggle Sidebar" })
        ]
      }
    );
  }
);
SidebarTrigger.displayName = "SidebarTrigger";
var SidebarRail = React11.forwardRef(({ className, toggleSidebarAriaLabel, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsx62(
    "button",
    {
      ref,
      "data-sidebar": "rail",
      "aria-label": toggleSidebarAriaLabel,
      tabIndex: -1,
      onClick: toggleSidebar,
      title: toggleSidebarAriaLabel,
      className: cn50(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-panel-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-panel",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      ),
      ...props
    }
  );
});
SidebarRail.displayName = "SidebarRail";
var SidebarInset = React11.forwardRef(({ className, ...props }, ref) => {
  const { state } = useSidebar();
  return /* @__PURE__ */ jsx62(
    "main",
    {
      ref,
      className: cn50(
        "flex flex-1 flex-col",
        "transition-[margin] duration-200 ease-linear",
        state === "expanded" && "md:ml-[--sidebar-width]",
        state === "collapsed" && "md:ml-0",
        className
      ),
      ...props
    }
  );
});
SidebarInset.displayName = "SidebarInset";
var SidebarInput = React11.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx62(
    Input,
    {
      ref,
      "data-sidebar": "input",
      className: cn50(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-panel-ring",
        className
      ),
      ...props
    }
  );
});
SidebarInput.displayName = "SidebarInput";
var SidebarHeader = React11.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx62(
    "div",
    {
      ref,
      "data-sidebar": "header",
      className: cn50("flex flex-col gap-2 p-2 overflow-hidden", className),
      ...props
    }
  );
});
SidebarHeader.displayName = "SidebarHeader";
var SidebarFooter = React11.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx62(
    "div",
    {
      ref,
      "data-sidebar": "footer",
      className: cn50("flex flex-col gap-2 p-2", className),
      ...props
    }
  );
});
SidebarFooter.displayName = "SidebarFooter";
var SidebarSeparator = React11.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx62(
    Separator5,
    {
      ref,
      "data-sidebar": "separator",
      className: cn50("mx-2 w-auto bg-panel-border", className),
      ...props
    }
  );
});
SidebarSeparator.displayName = "SidebarSeparator";
var SidebarContent = React11.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx62(
  "div",
  {
    ref,
    "data-sidebar": "content",
    className: cn50("flex flex-1 flex-col gap-2 overflow-hidden", className),
    ...props,
    children: /* @__PURE__ */ jsx62(ScrollArea2, { className: "flex-1 overflow-auto", children })
  }
));
SidebarContent.displayName = "SidebarContent";
var SidebarGroup = React11.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx62(
    "div",
    {
      ref,
      "data-sidebar": "group",
      className: cn50("relative flex w-full min-w-0 flex-col p-1", className),
      ...props
    }
  );
});
SidebarGroup.displayName = "SidebarGroup";
var SidebarGroupLabel = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx62(
  "div",
  {
    ref,
    "data-sidebar": "group-label",
    className: cn50(
      "text-xs font-medium px-2 py-0.5",
      "text-panel-foreground/70",
      className
    ),
    ...props
  }
));
SidebarGroupLabel.displayName = "SidebarGroupLabel";
var SidebarGroupAction = React11.forwardRef(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return /* @__PURE__ */ jsx62(
    Comp,
    {
      ref,
      "data-sidebar": "group-action",
      className: cn50(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-none ring-panel-ring transition-transform hover:bg-panel-accent hover:text-panel-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
});
SidebarGroupAction.displayName = "SidebarGroupAction";
var SidebarGroupContent = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx62(
  "div",
  {
    ref,
    "data-sidebar": "group-content",
    className: cn50("w-full text-sm", className),
    ...props
  }
));
SidebarGroupContent.displayName = "SidebarGroupContent";
var SidebarMenu = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx62(
  "ul",
  {
    ref,
    "data-sidebar": "menu",
    className: cn50("flex flex-col gap-0.5", className),
    ...props
  }
));
SidebarMenu.displayName = "SidebarMenu";
var SidebarMenuItem = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx62(
  "li",
  {
    ref,
    "data-sidebar": "menu-item",
    className: cn50(
      "group/menu-item",
      "hover:bg-panel-accent rounded-md",
      className
    ),
    ...props
  }
));
SidebarMenuItem.displayName = "SidebarMenuItem";
var SidebarMenuButton = React11.forwardRef(
  ({
    asChild = false,
    isActive = false,
    tooltip,
    className,
    children,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();
    const buttonClasses = cn50(
      "flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm rounded-md",
      "outline-none transition-colors",
      "hover:bg-panel-accent hover:text-panel-accent-foreground",
      "text-panel-foreground",
      "focus-visible:bg-panel-accent focus-visible:text-panel-accent-foreground",
      isActive && "bg-panel-accent text-panel-accent-foreground font-medium",
      className
    );
    const button = /* @__PURE__ */ jsx62(
      Comp,
      {
        ref,
        "data-sidebar": "menu-button",
        "data-active": isActive,
        className: buttonClasses,
        ...props,
        children
      }
    );
    if (!tooltip) {
      return button;
    }
    return /* @__PURE__ */ jsxs34(Tooltip, { children: [
      /* @__PURE__ */ jsx62(TooltipTrigger, { asChild: true, children: button }),
      /* @__PURE__ */ jsx62(
        TooltipContent,
        {
          side: "right",
          align: "center",
          hidden: state !== "collapsed" || isMobile,
          children: tooltip
        }
      )
    ] });
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";
var SidebarMenuAction = React11.forwardRef(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return /* @__PURE__ */ jsx62(
    Comp,
    {
      ref,
      "data-sidebar": "menu-action",
      className: cn50(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-panel-foreground outline-none ring-panel-ring transition-transform hover:bg-panel-accent hover:text-panel-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-panel-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 after:md:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-panel-accent-foreground md:opacity-0",
        className
      ),
      ...props
    }
  );
});
SidebarMenuAction.displayName = "SidebarMenuAction";
var SidebarMenuBadge = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx62(
  "div",
  {
    ref,
    "data-sidebar": "menu-badge",
    className: cn50(
      "pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-panel-foreground",
      "peer-hover/menu-button:text-panel-accent-foreground peer-data-[active=true]/menu-button:text-panel-accent-foreground",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className
    ),
    ...props
  }
));
SidebarMenuBadge.displayName = "SidebarMenuBadge";
var SidebarMenuSkeleton = React11.forwardRef(({ className, showIcon = false, ...props }, ref) => {
  const width = React11.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);
  return /* @__PURE__ */ jsxs34(
    "div",
    {
      ref,
      "data-sidebar": "menu-skeleton",
      className: cn50("flex h-8 items-center gap-2 rounded-md px-2", className),
      ...props,
      children: [
        showIcon && /* @__PURE__ */ jsx62(
          Skeleton,
          {
            className: "size-4 rounded-md",
            "data-sidebar": "menu-skeleton-icon"
          }
        ),
        /* @__PURE__ */ jsx62(
          Skeleton,
          {
            className: "h-4 max-w-[--skeleton-width] flex-1",
            "data-sidebar": "menu-skeleton-text",
            style: {
              "--skeleton-width": width
            }
          }
        )
      ]
    }
  );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";
var SidebarMenuSub = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx62(
  "ul",
  {
    ref,
    "data-sidebar": "menu-sub",
    className: cn50(
      "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-panel-border px-2.5 py-0.5",
      "group-data-[collapsible=icon]:hidden",
      className
    ),
    ...props
  }
));
SidebarMenuSub.displayName = "SidebarMenuSub";
var SidebarMenuSubItem = React11.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx62("li", { ref, ...props }));
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";
var SidebarMenuSubButton = React11.forwardRef(({ asChild = false, size = "md", isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";
  return /* @__PURE__ */ jsx62(
    Comp,
    {
      ref,
      "data-sidebar": "menu-sub-button",
      "data-size": size,
      "data-active": isActive,
      className: cn50(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-panel-foreground outline-none ring-panel-ring hover:bg-panel-accent hover:text-panel-accent-foreground focus-visible:ring-2 active:bg-panel-accent active:text-panel-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-panel-accent-foreground",
        "data-[active=true]:bg-panel-accent data-[active=true]:text-panel-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className
      ),
      ...props
    }
  );
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

// src/components/sidebar-nav/isSidebarNavActive.ts
var isSidebarNavActive = (pathname, href, options) => {
  if (!pathname) {
    return false;
  }
  if (pathname === href) {
    return true;
  }
  const exactMatchHrefs = options?.exactMatchHrefs ?? ["/dashboard"];
  if (exactMatchHrefs.includes(href)) {
    return false;
  }
  return pathname.startsWith(href);
};

// src/components/sidebar-nav/SidebarLink.tsx
import { cn as cn51 } from "@tessel/utils";
import { jsx as jsx63, jsxs as jsxs35 } from "react/jsx-runtime";
var DefaultSidebarLink = ({
  href,
  className,
  onClick,
  children
}) => /* @__PURE__ */ jsx63("a", { href, className, onClick, children });
var SidebarLink = ({
  href,
  label,
  icon: Icon3,
  isActive = false,
  className,
  onClick,
  LinkComponent = DefaultSidebarLink
}) => {
  const tooltip = typeof label === "string" ? label : void 0;
  return /* @__PURE__ */ jsx63(SidebarMenuItem, { children: /* @__PURE__ */ jsx63(SidebarMenuButton, { asChild: true, isActive, tooltip, children: /* @__PURE__ */ jsxs35(LinkComponent, { href, onClick, className: cn51(className), children: [
    Icon3 ? /* @__PURE__ */ jsx63(Icon3, { className: "h-4 w-4 shrink-0" }) : null,
    /* @__PURE__ */ jsx63("span", { className: "truncate", children: label })
  ] }) }) });
};

// src/components/site-footer/SiteFooter.tsx
import { cn as cn52 } from "@tessel/utils";
import { jsx as jsx64, jsxs as jsxs36 } from "react/jsx-runtime";
var LINK_BASE_CLASS = "text-sm transition-colors hover:text-primary";
var LINK_MUTED_CLASS = "text-muted-foreground";
var FooterLink = ({
  link,
  linkComponent: LinkComponent
}) => {
  const className = cn52(LINK_BASE_CLASS, LINK_MUTED_CLASS);
  let externalProps = {};
  if (link.external) {
    externalProps = { target: "_blank", rel: "noopener noreferrer" };
  }
  if (link.fullNavigation) {
    return /* @__PURE__ */ jsx64("a", { href: link.href, className, ...externalProps, children: link.label });
  }
  return /* @__PURE__ */ jsx64(LinkComponent, { href: link.href, className, ...externalProps, children: link.label });
};
var FooterLinks = ({ links, linkComponent }) => {
  if (links.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx64(
    "nav",
    {
      "aria-label": "Footer",
      className: "flex flex-wrap items-center justify-center gap-x-4 gap-y-1",
      children: links.map((link, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: footer links are a small, stable, ordered list
        /* @__PURE__ */ jsx64(FooterLink, { link, linkComponent }, index)
      ))
    }
  );
};
var FooterLogo = ({
  homeHref,
  logoAlt,
  logoLightSrc,
  logoDarkSrc
}) => {
  if (!homeHref) {
    return null;
  }
  return /* @__PURE__ */ jsx64(
    Logo,
    {
      href: homeHref,
      showDivider: false,
      alt: logoAlt,
      lightModeSrc: logoLightSrc,
      darkModeSrc: logoDarkSrc
    }
  );
};
var FooterLocalePicker = ({
  locale,
  onLocaleChange
}) => {
  if (!locale || !onLocaleChange) {
    return null;
  }
  return /* @__PURE__ */ jsx64(
    LanguageSwitcher,
    {
      locale,
      onLocaleChange,
      align: "start"
    }
  );
};
var FooterNote = ({ note }) => {
  if (!note) {
    return null;
  }
  return /* @__PURE__ */ jsx64("p", { className: "text-center text-muted-foreground text-xs md:text-right", children: note });
};
var SiteFooter = ({
  homeHref,
  logoAlt,
  logoLightSrc,
  logoDarkSrc,
  links = [],
  note,
  actions,
  locale,
  onLocaleChange,
  linkComponent,
  className
}) => {
  let LinkComponent = "a";
  if (linkComponent) {
    LinkComponent = linkComponent;
  }
  return /* @__PURE__ */ jsx64(
    "footer",
    {
      className: cn52(
        "w-full border-t border-border/50 bg-background/40 backdrop-blur-xl supports-[backdrop-filter]:bg-background/30",
        className
      ),
      children: /* @__PURE__ */ jsxs36("div", { className: "mx-auto flex max-w-4xl flex-col items-center gap-4 px-4 py-4 md:flex-row md:justify-between", children: [
        /* @__PURE__ */ jsxs36("div", { className: "flex flex-shrink-0 items-center gap-3", children: [
          /* @__PURE__ */ jsx64(
            FooterLogo,
            {
              homeHref,
              logoAlt,
              logoLightSrc,
              logoDarkSrc
            }
          ),
          /* @__PURE__ */ jsx64(
            FooterLocalePicker,
            {
              locale,
              onLocaleChange
            }
          ),
          actions
        ] }),
        /* @__PURE__ */ jsx64(FooterLinks, { links, linkComponent: LinkComponent }),
        /* @__PURE__ */ jsx64(FooterNote, { note })
      ] })
    }
  );
};

// src/components/site-header/SiteHeader.tsx
import { cn as cn53 } from "@tessel/utils";
import { ArrowUpRight, Menu as Menu2, Moon, Sun as Sun2, X as X5 } from "lucide-react";
import { useId as useId3, useState as useState8 } from "react";
import { jsx as jsx65, jsxs as jsxs37 } from "react/jsx-runtime";
var SEPARATOR_CLASS = "text-base text-muted-foreground/60";
var LINK_BASE_CLASS2 = "whitespace-nowrap text-base transition-colors hover:text-primary";
var NORMAL_CLASS = "font-medium text-muted-foreground";
var EMPHASIS_CLASS = "font-semibold text-foreground truncate max-w-[200px]";
var ICON_BUTTON_CLASS = "flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-primary";
var Crumb = ({ item, linkComponent: LinkComponent }) => {
  let stateClass = NORMAL_CLASS;
  if (item.emphasis) {
    stateClass = EMPHASIS_CLASS;
  }
  const textClass = cn53(LINK_BASE_CLASS2, stateClass);
  if (item.loading) {
    return /* @__PURE__ */ jsx65(Skeleton, { className: "h-5 w-32" });
  }
  if (item.href) {
    return /* @__PURE__ */ jsx65(LinkComponent, { href: item.href, className: textClass, children: item.label });
  }
  return /* @__PURE__ */ jsx65("span", { className: textClass, children: item.label });
};
var Breadcrumb = ({ items, linkComponent }) => {
  if (items.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx65("nav", { className: "flex h-full items-center gap-2", children: items.map((item, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: breadcrumb is a small, stable, ordered list
    /* @__PURE__ */ jsxs37("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx65("span", { className: SEPARATOR_CLASS, children: "/" }),
      /* @__PURE__ */ jsx65(Crumb, { item, linkComponent })
    ] }, index)
  )) });
};
var NavLink = ({
  link,
  linkComponent: LinkComponent,
  onNavigate,
  className
}) => {
  let stateClass = NORMAL_CLASS;
  if (link.current) {
    stateClass = "font-semibold text-foreground";
  }
  let ariaCurrent;
  if (link.current) {
    ariaCurrent = "page";
  }
  let target;
  let rel;
  if (link.external) {
    target = "_blank";
    rel = "noopener noreferrer";
  }
  let content = link.label;
  if (link.external) {
    content = /* @__PURE__ */ jsxs37("span", { className: "inline-flex items-center gap-1", children: [
      link.label,
      /* @__PURE__ */ jsx65(ArrowUpRight, { "aria-hidden": "true", className: "h-3.5 w-3.5" })
    ] });
  }
  return /* @__PURE__ */ jsx65(
    LinkComponent,
    {
      href: link.href,
      "aria-current": ariaCurrent,
      target,
      rel,
      onClick: onNavigate,
      className: cn53(LINK_BASE_CLASS2, stateClass, className),
      children: content
    }
  );
};
var DesktopNav = ({ links, linkComponent, label }) => {
  if (links.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx65(
    "nav",
    {
      "aria-label": label,
      className: "hidden flex-1 items-center justify-center gap-6 md:flex",
      children: links.map((link) => /* @__PURE__ */ jsx65(NavLink, { link, linkComponent }, link.href))
    }
  );
};
var BarActions = ({ actions }) => {
  if (!actions) {
    return null;
  }
  return /* @__PURE__ */ jsx65("div", { className: "hidden items-center gap-3 md:flex", children: actions });
};
var LocalePicker = ({ locale, onLocaleChange }) => {
  if (!locale || !onLocaleChange) {
    return null;
  }
  return /* @__PURE__ */ jsx65(LanguageSwitcher, { locale, onLocaleChange });
};
var ThemeToggle = ({ theme, onThemeToggle, label }) => {
  if (!onThemeToggle) {
    return null;
  }
  let Icon3 = Moon;
  if (theme === "dark") {
    Icon3 = Sun2;
  }
  return /* @__PURE__ */ jsx65(
    "button",
    {
      type: "button",
      "aria-label": label,
      onClick: onThemeToggle,
      className: ICON_BUTTON_CLASS,
      children: /* @__PURE__ */ jsx65(Icon3, { className: "h-5 w-5" })
    }
  );
};
var MobileMenuButton = ({
  show,
  open,
  controls,
  label,
  onToggle
}) => {
  if (!show) {
    return null;
  }
  let Icon3 = Menu2;
  if (open) {
    Icon3 = X5;
  }
  return /* @__PURE__ */ jsx65(
    "button",
    {
      type: "button",
      "aria-label": label,
      "aria-expanded": open,
      "aria-controls": controls,
      onClick: onToggle,
      className: cn53(ICON_BUTTON_CLASS, "md:hidden"),
      children: /* @__PURE__ */ jsx65(Icon3, { className: "h-5 w-5" })
    }
  );
};
var MobileDrawer = ({
  show,
  open,
  id,
  label,
  links,
  actions,
  linkComponent,
  onNavigate
}) => {
  if (!show) {
    return null;
  }
  return /* @__PURE__ */ jsxs37(
    "nav",
    {
      id,
      "aria-label": label,
      hidden: !open,
      className: "border-border/50 border-t bg-background px-4 py-3 md:hidden",
      children: [
        /* @__PURE__ */ jsx65("ul", { className: "flex flex-col gap-3", children: links.map((link) => /* @__PURE__ */ jsx65("li", { children: /* @__PURE__ */ jsx65(
          NavLink,
          {
            link,
            linkComponent,
            onNavigate,
            className: "block"
          }
        ) }, link.href)) }),
        /* @__PURE__ */ jsx65(MobileDrawerActions, { actions, hasLinks: links.length > 0 })
      ]
    }
  );
};
var MobileDrawerActions = ({
  actions,
  hasLinks
}) => {
  if (!actions) {
    return null;
  }
  return /* @__PURE__ */ jsx65(
    "div",
    {
      className: cn53(
        "flex items-center gap-3",
        hasLinks && "mt-3 border-border/50 border-t pt-3"
      ),
      children: actions
    }
  );
};
var Divider = () => /* @__PURE__ */ jsx65("span", { "aria-hidden": "true", className: "h-5 w-px bg-border" });
var SiteHeader = ({
  homeHref = "/",
  logoAlt,
  logoLightSrc = "/tessel-dark.svg",
  logoDarkSrc = "/tessel-light.svg",
  breadcrumb = [],
  links = [],
  actions,
  theme,
  onThemeToggle,
  themeLabel = "Toggle theme",
  locale,
  onLocaleChange,
  linkComponent,
  menuLabel = "Menu",
  className
}) => {
  let LinkComponent = "a";
  if (linkComponent) {
    LinkComponent = linkComponent;
  }
  const [menuOpen, setMenuOpen] = useState8(false);
  const menuId = useId3();
  const hasDrawerContent = links.length > 0 || Boolean(actions);
  const hasLocale = Boolean(locale) && Boolean(onLocaleChange);
  const hasTheme = Boolean(onThemeToggle);
  const toggleMenu = () => setMenuOpen((open) => !open);
  const closeMenu = () => setMenuOpen(false);
  return /* @__PURE__ */ jsxs37(
    "header",
    {
      className: cn53(
        "sticky top-0 z-50 w-full border-b border-border/50 bg-background/40 backdrop-blur-xl supports-[backdrop-filter]:bg-background/30",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs37("div", { className: "mx-auto flex max-w-5xl items-center gap-4 px-4 py-4", children: [
          /* @__PURE__ */ jsxs37("div", { className: "flex h-[22px] items-center gap-3", children: [
            /* @__PURE__ */ jsx65(
              MobileMenuButton,
              {
                show: hasDrawerContent,
                open: menuOpen,
                controls: menuId,
                label: menuLabel,
                onToggle: toggleMenu
              }
            ),
            /* @__PURE__ */ jsx65(
              Logo,
              {
                href: homeHref,
                showDivider: false,
                alt: logoAlt,
                lightModeSrc: logoLightSrc,
                darkModeSrc: logoDarkSrc
              }
            ),
            /* @__PURE__ */ jsx65(Breadcrumb, { items: breadcrumb, linkComponent: LinkComponent })
          ] }),
          /* @__PURE__ */ jsx65(
            DesktopNav,
            {
              links,
              linkComponent: LinkComponent,
              label: menuLabel
            }
          ),
          /* @__PURE__ */ jsxs37("div", { className: "ml-auto flex items-center gap-3", children: [
            /* @__PURE__ */ jsx65(LocalePicker, { locale, onLocaleChange }),
            hasLocale && hasTheme && /* @__PURE__ */ jsx65(Divider, {}),
            /* @__PURE__ */ jsx65(
              ThemeToggle,
              {
                theme,
                onThemeToggle,
                label: themeLabel
              }
            ),
            (hasLocale || hasTheme) && Boolean(actions) && /* @__PURE__ */ jsx65(Divider, {}),
            /* @__PURE__ */ jsx65(BarActions, { actions })
          ] })
        ] }),
        /* @__PURE__ */ jsx65(
          MobileDrawer,
          {
            show: hasDrawerContent,
            open: menuOpen,
            id: menuId,
            label: menuLabel,
            links,
            actions,
            linkComponent: LinkComponent,
            onNavigate: closeMenu
          }
        )
      ]
    }
  );
};

// src/components/slider/Slider.tsx
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn as cn54 } from "@tessel/utils";
import * as React12 from "react";
import { jsx as jsx66, jsxs as jsxs38 } from "react/jsx-runtime";
var Slider = React12.forwardRef(({ className, orientation = "horizontal", ...props }, ref) => /* @__PURE__ */ jsxs38(
  SliderPrimitive.Root,
  {
    ref,
    orientation,
    className: cn54(
      "relative flex touch-none select-none",
      orientation === "horizontal" ? "w-full items-center" : "h-full flex-col items-center justify-center",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx66(
        SliderPrimitive.Track,
        {
          className: cn54(
            "relative grow overflow-hidden rounded-full bg-muted border border-border",
            orientation === "horizontal" ? "h-2 w-full" : "w-2 h-full"
          ),
          children: /* @__PURE__ */ jsx66(
            SliderPrimitive.Range,
            {
              className: cn54(
                "absolute bg-primary",
                orientation === "horizontal" ? "h-full" : "w-full"
              )
            }
          )
        }
      ),
      /* @__PURE__ */ jsx66(SliderPrimitive.Thumb, { className: "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" })
    ]
  }
));
Slider.displayName = SliderPrimitive.Root.displayName;

// src/components/sonner/Sonner.tsx
import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { jsx as jsx67 } from "react/jsx-runtime";
var Toaster = ({ ...props }) => {
  const { theme = "system" } = useTheme();
  return /* @__PURE__ */ jsx67(
    Sonner,
    {
      theme,
      className: "toaster group",
      toastOptions: {
        classNames: {
          toast: "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground"
        }
      },
      ...props
    }
  );
};

// src/components/stat-card/StatCard.tsx
import { cn as cn55 } from "@tessel/utils";
import { jsx as jsx68, jsxs as jsxs39 } from "react/jsx-runtime";
var StatCard = ({
  icon,
  value,
  label,
  isLoading,
  onClick,
  iconBgColor = "bg-primary/10",
  iconColor = "text-primary",
  className
}) => {
  const isClickable = Boolean(onClick);
  return /* @__PURE__ */ jsxs39(
    Card,
    {
      className: cn55(
        "flex items-center gap-4 p-5 bg-background border-border transition-all",
        isClickable && "cursor-pointer hover:bg-primary/5 hover:border-primary/50 hover:shadow-sm",
        className
      ),
      onClick,
      children: [
        /* @__PURE__ */ jsx68("div", { className: cn55("p-3 rounded-lg", iconBgColor), children: /* @__PURE__ */ jsx68("div", { className: iconColor, children: icon }) }),
        /* @__PURE__ */ jsxs39("div", { children: [
          isLoading ? /* @__PURE__ */ jsx68("div", { className: "h-8 w-12 bg-muted animate-pulse rounded" }) : /* @__PURE__ */ jsx68("p", { className: "text-2xl font-bold text-foreground", children: value }),
          /* @__PURE__ */ jsx68("p", { className: "text-sm text-muted-foreground", children: label })
        ] })
      ]
    }
  );
};

// src/components/table/Table.tsx
import { cn as cn56 } from "@tessel/utils";
import { jsx as jsx69 } from "react/jsx-runtime";
var Table = ({ children, className }) => {
  return /* @__PURE__ */ jsx69("div", { className: "w-full overflow-auto", children: /* @__PURE__ */ jsx69("table", { className: cn56("w-full caption-bottom text-sm", className), children }) });
};
var TableHeader = ({ children, className }) => {
  return /* @__PURE__ */ jsx69("thead", { className: cn56("[&_tr]:border-b border-border", className), children });
};
var TableBody = ({ children, className }) => {
  return /* @__PURE__ */ jsx69("tbody", { className: cn56("[&_tr:last-child]:border-0", className), children });
};
var TableFooter = ({ children, className }) => {
  return /* @__PURE__ */ jsx69(
    "tfoot",
    {
      className: cn56(
        "bg-muted/50 font-medium [&>tr]:last:border-b-0 border-t border-border",
        className
      ),
      children
    }
  );
};
var TableRow = ({ children, className }) => {
  return /* @__PURE__ */ jsx69(
    "tr",
    {
      className: cn56(
        "border-b border-border transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      ),
      children
    }
  );
};
var TableHead = ({ children, className }) => {
  return /* @__PURE__ */ jsx69(
    "th",
    {
      className: cn56(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className
      ),
      children
    }
  );
};
var TableCell = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx69(
    "td",
    {
      className: cn56(
        "p-4 align-middle [&:has([role=checkbox])]:pr-0",
        className
      ),
      ...props,
      children
    }
  );
};
var TableCaption = ({ children, className }) => {
  return /* @__PURE__ */ jsx69("caption", { className: cn56("mt-4 text-sm text-muted-foreground", className), children });
};

// src/components/tabs/Tabs.tsx
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn as cn57 } from "@tessel/utils";
import { ChevronLeft as ChevronLeft3, ChevronRight as ChevronRight4, X as X6 } from "lucide-react";
import {
  createContext as createContext6,
  useContext as useContext6,
  useEffect as useEffect9
} from "react";

// src/components/tabs/useScrollableTabs.ts
import { useCallback as useCallback8, useEffect as useEffect8, useRef as useRef6, useState as useState9 } from "react";
function useScrollableTabs({
  scrollAmount = 240
} = {}) {
  const containerRef = useRef6(null);
  const scrollContainerRef = useRef6(null);
  const [canScrollLeft, setCanScrollLeft] = useState9(false);
  const [canScrollRight, setCanScrollRight] = useState9(false);
  const updateScrollState = useCallback8(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = container;
    if (clientWidth === 0 || scrollWidth === 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const hasOverflow = scrollWidth > clientWidth;
    setCanScrollLeft(hasOverflow && scrollLeft > 1);
    setCanScrollRight(hasOverflow && scrollLeft + clientWidth < scrollWidth - 1);
  }, []);
  useEffect8(() => {
    const scrollContainer = scrollContainerRef.current;
    const wrapper = containerRef.current;
    if (!scrollContainer) {
      return;
    }
    const handleResizeEvent = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateScrollState);
      });
    };
    const resizeObserver = new ResizeObserver(handleResizeEvent);
    resizeObserver.observe(scrollContainer);
    if (wrapper) {
      resizeObserver.observe(wrapper);
    }
    scrollContainer.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", handleResizeEvent);
    return () => {
      resizeObserver.disconnect();
      scrollContainer.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", handleResizeEvent);
    };
  }, [updateScrollState]);
  const handleScrollLeft = useCallback8(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  }, [scrollAmount]);
  const handleScrollRight = useCallback8(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }, [scrollAmount]);
  const refreshScrollState = useCallback8(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(updateScrollState);
    });
  }, [updateScrollState]);
  return {
    containerRef,
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    handleScrollLeft,
    handleScrollRight,
    refreshScrollState
  };
}

// src/components/tabs/Tabs.tsx
import { jsx as jsx70, jsxs as jsxs40 } from "react/jsx-runtime";
var TabsContext = createContext6({ variant: "default" });
function Tabs({ className, variant = "default", ...props }) {
  return /* @__PURE__ */ jsx70(TabsContext.Provider, { value: { variant }, children: /* @__PURE__ */ jsx70(
    TabsPrimitive.Root,
    {
      "data-slot": "tabs",
      className: cn57(
        variant !== "browser" && "flex flex-col gap-2",
        className
      ),
      ...props
    }
  ) });
}
function TabsList({
  className,
  scrollable,
  arrowButtonWidth = 28,
  scrollAmount = 240,
  scrollLeftAriaLabel,
  scrollRightAriaLabel,
  children,
  ...props
}) {
  const { variant } = useContext6(TabsContext);
  const variantClassName = cn57(
    variant === "default" && "bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]",
    variant === "underline" && "inline-flex items-center gap-1 border-b border-border",
    variant === "browser" && "flex w-full"
  );
  if (!scrollable) {
    return /* @__PURE__ */ jsx70(
      TabsPrimitive.List,
      {
        "data-slot": "tabs-list",
        className: cn57(variantClassName, className),
        ...props,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx70(
    ScrollableTabsList,
    {
      variantClassName,
      wrapperClassName: className,
      arrowButtonWidth,
      scrollAmount,
      scrollLeftAriaLabel,
      scrollRightAriaLabel,
      listProps: props,
      children
    }
  );
}
function ScrollGradient({ side }) {
  return /* @__PURE__ */ jsx70(
    "div",
    {
      className: cn57(
        "absolute top-0 bottom-0 w-8 pointer-events-none z-10",
        side === "left" ? "left-0" : "right-0"
      ),
      style: {
        background: `linear-gradient(to ${side === "left" ? "right" : "left"}, var(--secondary), transparent)`
      }
    }
  );
}
function ScrollArrowButton({
  direction,
  enabled,
  onClick,
  width,
  scrollLeftAriaLabel,
  scrollRightAriaLabel
}) {
  return /* @__PURE__ */ jsx70(
    "button",
    {
      type: "button",
      onClick,
      disabled: !enabled,
      className: cn57(
        "flex items-center justify-center transition-colors flex-shrink-0",
        direction === "left" ? "border-r border-panel-border" : "border-l border-panel-border",
        enabled ? "hover:bg-accent text-muted-foreground hover:text-foreground cursor-pointer" : "cursor-default"
      ),
      style: { width },
      "aria-label": direction === "left" ? scrollLeftAriaLabel : scrollRightAriaLabel,
      children: enabled && (direction === "left" ? /* @__PURE__ */ jsx70(ChevronLeft3, { className: "w-4 h-4" }) : /* @__PURE__ */ jsx70(ChevronRight4, { className: "w-4 h-4" }))
    }
  );
}
function ScrollableTabsList({
  variantClassName,
  wrapperClassName,
  arrowButtonWidth,
  scrollAmount,
  scrollLeftAriaLabel,
  scrollRightAriaLabel,
  children,
  listProps
}) {
  const {
    containerRef,
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    handleScrollLeft,
    handleScrollRight,
    refreshScrollState
  } = useScrollableTabs({ scrollAmount });
  useEffect9(() => {
    refreshScrollState();
  }, [refreshScrollState, children]);
  return /* @__PURE__ */ jsxs40("div", { ref: containerRef, className: cn57("flex w-full", wrapperClassName), children: [
    /* @__PURE__ */ jsx70(
      ScrollArrowButton,
      {
        direction: "left",
        enabled: canScrollLeft,
        onClick: handleScrollLeft,
        width: arrowButtonWidth,
        scrollLeftAriaLabel,
        scrollRightAriaLabel
      }
    ),
    /* @__PURE__ */ jsxs40("div", { className: "relative flex-1 min-w-0", children: [
      canScrollLeft && /* @__PURE__ */ jsx70(ScrollGradient, { side: "left" }),
      /* @__PURE__ */ jsx70(
        "div",
        {
          ref: scrollContainerRef,
          className: "flex min-w-0 w-full overflow-x-auto scrollbar-none",
          style: { scrollbarWidth: "none", msOverflowStyle: "none" },
          children: /* @__PURE__ */ jsx70(
            TabsPrimitive.List,
            {
              "data-slot": "tabs-list",
              className: variantClassName,
              ...listProps,
              children
            }
          )
        }
      ),
      canScrollRight && /* @__PURE__ */ jsx70(ScrollGradient, { side: "right" })
    ] }),
    /* @__PURE__ */ jsx70(
      ScrollArrowButton,
      {
        direction: "right",
        enabled: canScrollRight,
        onClick: handleScrollRight,
        width: arrowButtonWidth,
        scrollLeftAriaLabel,
        scrollRightAriaLabel
      }
    )
  ] });
}
function TabsTrigger({
  className,
  icon,
  onClose,
  closeLabel,
  children,
  ...props
}) {
  const { variant } = useContext6(TabsContext);
  const { "data-state": _ds, ...triggerProps } = props;
  const triggerClassName = cn57(
    // Common styles
    "inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap transition-all cursor-pointer select-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-40 disabled:cursor-not-allowed",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    // Default variant
    variant === "default" && [
      "h-[calc(100%-1px)] flex-1 rounded-md border border-transparent px-2 py-1",
      "text-muted-foreground hover:text-foreground",
      "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=active]:border-border"
    ],
    // Underline variant
    variant === "underline" && [
      "relative px-4 py-3 -mb-px rounded-t-lg",
      "text-muted-foreground hover:text-foreground hover:bg-muted/50",
      "data-[state=active]:text-primary",
      // Underline indicator
      "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full",
      "after:scale-x-0 after:transition-transform after:duration-200",
      "data-[state=active]:after:scale-x-100 data-[state=active]:after:bg-primary"
    ],
    // Browser variant
    variant === "browser" && [
      "flex items-center gap-2 px-3 py-2 border-r border-panel-border transition-colors flex-shrink-0",
      "bg-secondary text-muted-foreground hover:bg-accent hover:text-foreground",
      "data-[state=active]:bg-card data-[state=active]:text-foreground"
    ],
    className
  );
  if (onClose) {
    return /* @__PURE__ */ jsxs40(
      TabsPrimitive.Trigger,
      {
        "data-slot": "tabs-trigger",
        className: cn57(triggerClassName, "group"),
        ...triggerProps,
        children: [
          icon && /* @__PURE__ */ jsx70("span", { className: "flex-shrink-0", children: icon }),
          /* @__PURE__ */ jsx70("span", { className: "truncate text-sm flex-1", children }),
          /* @__PURE__ */ jsx70(
            "span",
            {
              role: "button",
              tabIndex: 0,
              onPointerDown: (e) => {
                e.stopPropagation();
              },
              onMouseDown: (e) => {
                e.stopPropagation();
              },
              onFocus: (e) => {
                e.stopPropagation();
              },
              onClick: (e) => {
                e.stopPropagation();
                onClose(e);
              },
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose(e);
                }
              },
              className: cn57(
                "flex-shrink-0 p-0.5 rounded transition-colors cursor-pointer pointer-events-auto",
                "opacity-0 group-hover:opacity-100",
                "group-data-[state=active]:opacity-100",
                "hover:bg-accent text-muted-foreground hover:text-foreground"
              ),
              "aria-label": closeLabel,
              children: /* @__PURE__ */ jsx70(X6, { className: "w-3 h-3" })
            }
          )
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs40(
    TabsPrimitive.Trigger,
    {
      "data-slot": "tabs-trigger",
      className: triggerClassName,
      ...triggerProps,
      children: [
        icon && /* @__PURE__ */ jsx70("span", { className: "flex-shrink-0", children: icon }),
        children
      ]
    }
  );
}
function TabsContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx70(
    TabsPrimitive.Content,
    {
      "data-slot": "tabs-content",
      className: cn57("flex-1 outline-none mt-2", className),
      ...props
    }
  );
}

// src/components/theme-provider/ThemeProvider.tsx
import { ThemeProvider as NextThemeProvider } from "next-themes";
import { jsx as jsx71 } from "react/jsx-runtime";
var ThemeProvider = ({ children, storageKey = "tessel-theme", ...props }) => /* @__PURE__ */ jsx71(
  NextThemeProvider,
  {
    attribute: "class",
    defaultTheme: "system",
    enableSystem: true,
    storageKey,
    themes: ["light", "dark", "system"],
    ...props,
    children
  }
);

// src/components/toast/Toast.tsx
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cn as cn58 } from "@tessel/utils";
import { X as X7 } from "lucide-react";
import * as React13 from "react";
import { tv as tv6 } from "tailwind-variants";
import { jsx as jsx72 } from "react/jsx-runtime";
var ToastProvider = ToastPrimitives.Provider;
var ToastViewport = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx72(
  ToastPrimitives.Viewport,
  {
    ref,
    className: cn58(
      "fixed",
      "top-4",
      "right-4",
      "z-[100]",
      "flex",
      "max-h-screen",
      "w-full",
      "flex-col",
      "p-4",
      "md:max-w-[420px]",
      className || ""
    ),
    ...props
  }
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
var toastVariants = tv6({
  base: "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full",
  variants: {
    variant: {
      default: "border bg-background text-foreground",
      destructive: "destructive group border-destructive bg-destructive text-destructive-foreground",
      success: "border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-100",
      warning: "border-yellow-200 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-100"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var Toast = React13.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx72(
    ToastPrimitives.Root,
    {
      ref,
      className: toastVariants({
        variant,
        className
      }),
      ...props
    }
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;
var ToastAction = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx72(
  ToastPrimitives.Action,
  {
    ref,
    className: cn58(
      "inline-flex",
      "h-8",
      "shrink-0",
      "items-center",
      "justify-center",
      "rounded-md",
      "border",
      "bg-transparent",
      "px-3",
      "text-sm",
      "font-medium",
      "ring-offset-background",
      "transition-colors",
      "hover:bg-secondary",
      "focus:outline-none",
      "focus:ring-2",
      "focus:ring-ring",
      "focus:ring-offset-2",
      "disabled:pointer-events-none",
      "disabled:opacity-50",
      "group-[.destructive]:border-muted/40",
      "group-[.destructive]:hover:border-destructive/30",
      "group-[.destructive]:hover:bg-destructive",
      "group-[.destructive]:hover:text-destructive-foreground",
      "group-[.destructive]:focus:ring-destructive",
      className || ""
    ),
    ...props
  }
));
ToastAction.displayName = ToastPrimitives.Action.displayName;
var ToastClose = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx72(
  ToastPrimitives.Close,
  {
    ref,
    className: cn58(
      "absolute",
      "right-2",
      "top-2",
      "rounded-md",
      "p-1",
      "text-foreground/50",
      "opacity-0",
      "transition-opacity",
      "hover:text-foreground",
      "focus:opacity-100",
      "focus:outline-none",
      "focus:ring-2",
      "group-hover:opacity-100",
      "group-[.destructive]:text-red-300",
      "group-[.destructive]:hover:text-red-50",
      "group-[.destructive]:focus:ring-red-400",
      "group-[.destructive]:focus:ring-offset-red-600",
      className || ""
    ),
    "toast-close": "",
    ...props,
    children: /* @__PURE__ */ jsx72(X7, { className: "h-4 w-4" })
  }
));
ToastClose.displayName = ToastPrimitives.Close.displayName;
var ToastTitle = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx72(
  ToastPrimitives.Title,
  {
    ref,
    className: cn58("text-sm", "font-semibold", className || ""),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
var ToastDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx72(
  ToastPrimitives.Description,
  {
    ref,
    className: cn58("text-sm", "opacity-90", className || ""),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

// src/components/toast/Toaster.tsx
import { jsx as jsx73 } from "react/jsx-runtime";
var Toaster2 = () => /* @__PURE__ */ jsx73(ToastProvider, { children: /* @__PURE__ */ jsx73(ToastViewport, {}) });

// src/components/toast/radixToastCompat.ts
import { toast as sonnerToast } from "sonner";
var radixToastCompat = ({
  title,
  description,
  variant = "default",
  duration
}) => {
  const message = typeof title === "string" ? title : title ? String(title) : "Notification";
  const desc = typeof description === "string" ? description : description ? String(description) : void 0;
  if (variant === "destructive") {
    return sonnerToast.error(message, { description: desc, duration });
  }
  if (variant === "success") {
    return sonnerToast.success(message, { description: desc, duration });
  }
  return sonnerToast(message, { description: desc, duration });
};

// src/components/toast/useToast.ts
import { useCallback as useCallback10 } from "react";

// src/hooks/useSonnerToast.ts
import { useCallback as useCallback9 } from "react";
import { toast as sonnerToast2 } from "sonner";
var useSonnerToast = () => {
  const showToast = useCallback9((options) => {
    const { title, description, duration, action } = options;
    if (!title && !description) {
      return;
    }
    return sonnerToast2(title ?? description ?? "", {
      description,
      duration,
      action: action ? {
        label: action.label,
        onClick: action.onClick
      } : void 0
    });
  }, []);
  const showSuccess = useCallback9(
    (message, options) => {
      return sonnerToast2.success(message, {
        description: options?.description,
        duration: options?.duration,
        action: options?.action ? {
          label: options.action.label,
          onClick: options.action.onClick
        } : void 0
      });
    },
    []
  );
  const showError = useCallback9(
    (message, options) => {
      return sonnerToast2.error(message, {
        description: options?.description,
        duration: options?.duration,
        action: options?.action ? {
          label: options.action.label,
          onClick: options.action.onClick
        } : void 0
      });
    },
    []
  );
  const showWarning = useCallback9(
    (message, options) => {
      return sonnerToast2.warning(message, {
        description: options?.description,
        duration: options?.duration,
        action: options?.action ? {
          label: options.action.label,
          onClick: options.action.onClick
        } : void 0
      });
    },
    []
  );
  const showInfo = useCallback9(
    (message, options) => {
      return sonnerToast2.info(message, {
        description: options?.description,
        duration: options?.duration,
        action: options?.action ? {
          label: options.action.label,
          onClick: options.action.onClick
        } : void 0
      });
    },
    []
  );
  const showUndoToast = useCallback9((options) => {
    const { title, description, undoLabel, onUndo, duration = 5e3 } = options;
    return sonnerToast2.success(title, {
      description,
      duration,
      action: undoLabel ? {
        label: undoLabel,
        onClick: async () => {
          try {
            await onUndo();
          } catch (error) {
            console.error("Failed to undo action:", error);
          }
        }
      } : void 0
    });
  }, []);
  const dismiss = useCallback9((toastId) => {
    if (toastId) {
      sonnerToast2.dismiss(toastId);
    } else {
      sonnerToast2.dismiss();
    }
  }, []);
  const dismissAll = useCallback9(() => {
    sonnerToast2.dismiss();
  }, []);
  return {
    // New Sonner-based methods
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showUndoToast,
    dismiss,
    dismissAll,
    // Compatibility with old toast API (for gradual migration)
    toast: showToast,
    // Raw Sonner access for advanced usage
    sonner: sonnerToast2
  };
};

// src/components/toast/useToast.ts
var toast = radixToastCompat;
var useToast = () => {
  const sonner = useSonnerToast();
  const compatToast = useCallback10(
    (options) => {
      radixToastCompat(options);
    },
    []
  );
  return {
    toast: compatToast,
    dismiss: sonner.dismiss,
    toasts: []
  };
};

// src/components/toggle-group/ToggleGroup.tsx
import { cn as cn59 } from "@tessel/utils";

// src/components/toggle-group/toggle-group.styles.ts
import { tv as tv7 } from "tailwind-variants";
var toggleGroupStyles = tv7({
  slots: {
    root: ["inline-flex gap-1 rounded-lg p-[3px]", "bg-muted"],
    item: [
      "inline-flex items-center justify-center gap-1.5",
      "px-3 py-1.5 text-sm font-medium",
      "rounded-md transition-all cursor-pointer",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      "[&_svg]:pointer-events-none [&_svg]:shrink-0"
    ]
  },
  variants: {
    state: {
      active: {
        item: [
          "bg-background text-foreground",
          "shadow-md border border-border"
        ]
      },
      inactive: {
        item: ["text-muted-foreground", "hover:text-foreground"]
      }
    },
    size: {
      sm: {
        root: "p-0.5 gap-0.5",
        item: "px-2 py-1 text-xs gap-1 [&_svg]:size-3"
      },
      md: {
        root: "p-[3px] gap-1",
        item: "px-3 py-1.5 text-sm gap-1.5 [&_svg]:size-4"
      },
      lg: {
        root: "p-1 gap-1",
        item: "px-4 py-2 text-base gap-2 [&_svg]:size-5"
      }
    }
  },
  defaultVariants: {
    state: "inactive",
    size: "md"
  }
});

// src/components/toggle-group/ToggleGroup.tsx
import { jsx as jsx74, jsxs as jsxs41 } from "react/jsx-runtime";
function ToggleGroup({
  value,
  onChange,
  options,
  size = "md",
  className,
  itemClassName,
  disabled = false
}) {
  const styles = toggleGroupStyles({ size });
  return (
    // biome-ignore lint/a11y/useSemanticElements: Custom toggle group uses ARIA roles for flexibility
    /* @__PURE__ */ jsx74("div", { className: cn59(styles.root(), className), role: "group", children: options.map((option) => {
      const isActive = value === option.value;
      const isDisabled = disabled || option.disabled;
      return (
        // biome-ignore lint/a11y/useSemanticElements: Custom toggle styled as radio for better UX
        /* @__PURE__ */ jsxs41(
          "button",
          {
            type: "button",
            role: "radio",
            "aria-checked": isActive,
            disabled: isDisabled,
            onClick: () => {
              if (!isDisabled) {
                onChange(option.value);
              }
            },
            className: cn59(
              styles.item({ state: isActive ? "active" : "inactive" }),
              itemClassName
            ),
            children: [
              option.icon,
              option.label
            ]
          },
          option.value
        )
      );
    }) })
  );
}

// src/components/tree-view/TreeView.tsx
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { cn as cn60 } from "@tessel/utils";
import React14 from "react";
import { jsx as jsx75, jsxs as jsxs42 } from "react/jsx-runtime";
var TreeView = React14.forwardRef(
  ({
    data,
    initialSelectedItemId,
    onSelectChange,
    expandAll,
    expandedItemIds,
    onExpandedIdsChange,
    defaultLeafIcon,
    defaultNodeIcon,
    className,
    onDocumentDrag,
    onParentChange,
    onReorder,
    emptyStateMessage,
    loadingText,
    ...props
  }, ref) => {
    const [isMounted, setIsMounted] = React14.useState(false);
    React14.useEffect(() => {
      setIsMounted(true);
    }, []);
    const [selectedItemId, setSelectedItemId] = React14.useState(initialSelectedItemId);
    const [insertionTarget, setInsertionTarget] = React14.useState(null);
    const stableSetInsertionTarget = React14.useCallback(
      (target) => {
        setInsertionTarget(target);
      },
      []
    );
    const [activeItem, setActiveItem] = React14.useState(
      null
    );
    const memoizedData = data;
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5
          // Require 5px movement before starting drag
        }
      })
    );
    React14.useEffect(() => {
      setSelectedItemId(initialSelectedItemId);
    }, [initialSelectedItemId]);
    const handleSelectChange = React14.useCallback(
      (item) => {
        setSelectedItemId(item?.id);
        if (onSelectChange) {
          onSelectChange(item);
        }
      },
      [onSelectChange]
    );
    const findItemByIdCallback = React14.useCallback(
      (id) => {
        return findItemById(
          id,
          Array.isArray(memoizedData) ? memoizedData : [memoizedData]
        );
      },
      [memoizedData]
    );
    const handleDragStart = React14.useCallback(
      (event) => {
        const draggedItem = findItemByIdCallback(event.active.id);
        setActiveItem(draggedItem);
        stableSetInsertionTarget(null);
      },
      [findItemByIdCallback, stableSetInsertionTarget]
    );
    const handleDragEnd = React14.useCallback(
      (event) => {
        setActiveItem(null);
        stableSetInsertionTarget(null);
        const { active, over } = event;
        if (over && active.id !== over.id) {
          if (over.data.current?.type === "insertion-zone") {
            const { itemId, position } = over.data.current;
            const activeItem2 = findItemByIdCallback(active.id);
            const targetItem = findItemByIdCallback(itemId);
            if (activeItem2 && targetItem) {
              const targetParentId = targetItem.parent_id || null;
              const activeParentId = activeItem2.parent_id || null;
              const isParentChange = targetParentId !== activeParentId;
              if (isParentChange && onParentChange) {
                const newParentId = targetParentId;
                const newParentItem = newParentId ? findItemByIdCallback(newParentId) : { id: "", parent_id: void 0, name: "" };
                if (newParentItem || newParentId === null) {
                  onParentChange(
                    activeItem2,
                    newParentItem || { id: "", parent_id: void 0, name: "" }
                  );
                }
              }
              if (onReorder) {
                onReorder(activeItem2, targetItem, position);
              }
            }
          } else {
            const activeItem2 = findItemByIdCallback(active.id);
            const overItem = findItemByIdCallback(over.id);
            if (activeItem2 && overItem) {
              const targetParentId = over.data.current?.parentId || over.id;
              const targetParentItem = findItemByIdCallback(
                targetParentId
              );
              if (targetParentItem && targetParentId !== activeItem2.parent_id) {
                if (onParentChange) {
                  onParentChange(activeItem2, targetParentItem);
                }
              } else {
                if (onParentChange) {
                  onParentChange(activeItem2, overItem);
                } else if (onDocumentDrag) {
                  onDocumentDrag(activeItem2, overItem);
                }
              }
            }
          }
        }
      },
      [
        findItemByIdCallback,
        onParentChange,
        onReorder,
        onDocumentDrag,
        stableSetInsertionTarget
      ]
    );
    const handleDragOver = React14.useCallback(
      (event) => {
        const { over } = event;
        if (!(onReorder && over)) {
          setInsertionTarget(null);
          return;
        }
        if (over.data.current?.type === "insertion-zone") {
          const newTarget = {
            itemId: over.data.current.itemId,
            position: over.data.current.position
          };
          stableSetInsertionTarget(
            insertionTarget?.itemId === newTarget.itemId && insertionTarget?.position === newTarget.position ? insertionTarget : newTarget
          );
        } else if (typeof over.id === "string" && (over.id.endsWith("-before") || over.id.endsWith("-after"))) {
          const position = over.id.endsWith("-before") ? "before" : "after";
          const itemId = over.id.replace(/-(?:before|after)$/, "");
          const newTarget = { itemId, position };
          stableSetInsertionTarget(
            insertionTarget?.itemId === newTarget.itemId && insertionTarget?.position === newTarget.position ? insertionTarget : newTarget
          );
        } else {
          if (over.data.current?.type !== "tree-item") {
            stableSetInsertionTarget(null);
          } else {
          }
        }
      },
      [onReorder, stableSetInsertionTarget, insertionTarget]
    );
    const computedExpandedItemIds = React14.useMemo(() => {
      return computeExpandedItemIds(
        memoizedData,
        expandAll,
        initialSelectedItemId,
        expandedItemIds
      );
    }, [memoizedData, expandAll, initialSelectedItemId, expandedItemIds]);
    if (Array.isArray(memoizedData) && memoizedData.length === 0) {
      return /* @__PURE__ */ jsx75(
        "div",
        {
          className: cn60("p-4 text-center text-muted-foreground", className),
          ref,
          children: emptyStateMessage
        }
      );
    }
    const treeContent = /* @__PURE__ */ jsx75("div", { className: cn60("overflow-hidden relative p-2", className), children: /* @__PURE__ */ jsx75(
      TreeItem,
      {
        data: memoizedData,
        ref,
        selectedItemId,
        handleSelectChange,
        expandedItemIds: computedExpandedItemIds,
        onExpandedIdsChange,
        defaultLeafIcon,
        defaultNodeIcon,
        draggedItem: activeItem,
        insertionTarget,
        setInsertionTarget: stableSetInsertionTarget,
        emptyStateMessage,
        loadingText,
        ...props
      },
      `tree-root-${selectedItemId || "none"}`
    ) });
    if (!isMounted) {
      return treeContent;
    }
    return /* @__PURE__ */ jsxs42(
      DndContext,
      {
        sensors,
        collisionDetection: closestCenter,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        onDragOver: handleDragOver,
        children: [
          treeContent,
          /* @__PURE__ */ jsx75(
            DragOverlay,
            {
              dropAnimation: {
                duration: 0,
                easing: "linear",
                sideEffects: ({ active, dragOverlay }) => {
                  active.node.style.opacity = "0";
                  dragOverlay.node.style.opacity = "0";
                  return () => {
                    active.node.style.opacity = "";
                  };
                }
              },
              children: activeItem ? /* @__PURE__ */ jsx75(
                "div",
                {
                  className: "pointer-events-none z-50 opacity-90 transform rotate-2 scale-90",
                  style: { transition: "none" },
                  children: /* @__PURE__ */ jsx75("div", { className: "bg-background/95 backdrop-blur-md border-2 border-primary/30 rounded-lg shadow-2xl px-3 py-2 max-w-xs", children: /* @__PURE__ */ jsxs42("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx75("div", { className: "flex-shrink-0", children: activeItem.icon ? /* @__PURE__ */ jsx75(activeItem.icon, { className: "h-4 w-4 text-primary" }) : defaultNodeIcon ? React14.createElement(defaultNodeIcon, {
                      className: "h-4 w-4 text-primary"
                    }) : /* @__PURE__ */ jsx75("div", { className: "h-4 w-4 rounded bg-primary/20" }) }),
                    /* @__PURE__ */ jsx75("div", { className: "text-sm font-medium text-foreground min-w-0 flex-1 truncate", children: typeof activeItem.name === "string" ? activeItem.name : activeItem.name }),
                    /* @__PURE__ */ jsx75("div", { className: "flex-shrink-0 w-1 h-4 bg-primary/50 rounded-full animate-pulse" })
                  ] }) })
                }
              ) : null
            }
          )
        ]
      }
    );
  }
);
TreeView.displayName = "TreeView";

// src/components/video-player/VideoPlayer.tsx
import { cn as cn61 } from "@tessel/utils";
import { jsx as jsx76 } from "react/jsx-runtime";
function VideoPlayer({
  src,
  className,
  poster,
  preload = "metadata",
  autoPlay,
  loop,
  muted
}) {
  return /* @__PURE__ */ jsx76(
    "video",
    {
      controls: true,
      src,
      poster,
      preload,
      autoPlay,
      loop,
      muted,
      className: cn61("w-full rounded-md", className),
      children: /* @__PURE__ */ jsx76("track", { kind: "captions" })
    }
  );
}

// src/components/zoomable-image/Minimap.tsx
import { cn as cn62 } from "@tessel/utils";
import { useMemo as useMemo6 } from "react";
import { jsx as jsx77, jsxs as jsxs43 } from "react/jsx-runtime";
var MAX_SIZE = 96;
function Minimap({
  src,
  zoom,
  panPosition,
  aspectRatio = 1,
  containerSize,
  className,
  isHovering = true
}) {
  const dimensions = useMemo6(() => {
    if (aspectRatio > 1) {
      return { width: MAX_SIZE, height: Math.round(MAX_SIZE / aspectRatio) };
    }
    if (aspectRatio < 1) {
      return { width: Math.round(MAX_SIZE * aspectRatio), height: MAX_SIZE };
    }
    return { width: MAX_SIZE, height: MAX_SIZE };
  }, [aspectRatio]);
  const viewportStyle = useMemo6(() => {
    if (!containerSize || containerSize.width === 0 || containerSize.height === 0) {
      return {
        width: `${Math.max(100 / zoom, 10)}%`,
        height: `${Math.max(100 / zoom, 10)}%`,
        left: `${50 - 50 / zoom}%`,
        top: `${50 - 50 / zoom}%`
      };
    }
    const containerRatio = containerSize.width / containerSize.height;
    let displayedWidth;
    let displayedHeight;
    if (containerRatio > aspectRatio) {
      displayedHeight = containerSize.height;
      displayedWidth = containerSize.height * aspectRatio;
    } else {
      displayedWidth = containerSize.width;
      displayedHeight = containerSize.width / aspectRatio;
    }
    const viewportW = Math.max(
      containerSize.width / (displayedWidth * zoom) * 100,
      10
    );
    const viewportH = Math.max(
      containerSize.height / (displayedHeight * zoom) * 100,
      10
    );
    const left = 50 - panPosition.x * 100 / (displayedWidth * zoom) - viewportW / 2;
    const top = 50 - panPosition.y * 100 / (displayedHeight * zoom) - viewportH / 2;
    return {
      width: `${viewportW}%`,
      height: `${viewportH}%`,
      left: `${left}%`,
      top: `${top}%`
    };
  }, [containerSize, aspectRatio, zoom, panPosition]);
  return /* @__PURE__ */ jsxs43(
    "div",
    {
      className: cn62(
        "relative rounded-lg border border-border bg-background/95 backdrop-blur-sm overflow-hidden shadow-lg transition-opacity",
        isHovering ? "opacity-100" : "opacity-70",
        className
      ),
      style: { width: dimensions.width, height: dimensions.height },
      children: [
        /* @__PURE__ */ jsx77(
          "img",
          {
            src,
            alt: "",
            className: "w-full h-full object-cover p-1",
            draggable: false
          }
        ),
        /* @__PURE__ */ jsx77(
          "div",
          {
            className: "absolute border-2 border-primary bg-primary/30 rounded-sm pointer-events-none",
            style: viewportStyle
          }
        )
      ]
    }
  );
}

// src/components/zoomable-image/useImageInteraction.ts
import { useCallback as useCallback11, useEffect as useEffect10, useRef as useRef7, useState as useState10 } from "react";
var DEFAULT_MIN_ZOOM = 0.5;
var DEFAULT_MAX_ZOOM = 4;
var DEFAULT_ZOOM_STEP = 0.25;
var DEFAULT_WHEEL_ZOOM_STEP = 0.1;
function useImageInteraction(options = {}) {
  const {
    minZoom = DEFAULT_MIN_ZOOM,
    maxZoom = DEFAULT_MAX_ZOOM,
    zoomStep = DEFAULT_ZOOM_STEP,
    wheelZoomStep = DEFAULT_WHEEL_ZOOM_STEP,
    enabled = true,
    initialZoom = 1,
    onZoomChange,
    onPanChange
  } = options;
  const [zoom, setZoomState] = useState10(initialZoom);
  const [panPosition, setPanPositionState] = useState10({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState10(false);
  const [isHovering, setIsHovering] = useState10(false);
  const [imageOverflows, setImageOverflows] = useState10(false);
  const [imageAspectRatio, setImageAspectRatio] = useState10(1);
  const [containerSize, setContainerSize] = useState10({ width: 0, height: 0 });
  const containerRef = useRef7(null);
  const imageRef = useRef7(null);
  const dragStart = useRef7({ x: 0, y: 0 });
  const lastTouchDistance = useRef7(null);
  const setZoom = useCallback11(
    (newZoom) => {
      const clampedZoom = Math.min(Math.max(newZoom, minZoom), maxZoom);
      setZoomState(clampedZoom);
      onZoomChange?.(clampedZoom);
    },
    [minZoom, maxZoom, onZoomChange]
  );
  const setPanPosition = useCallback11(
    (newPosition) => {
      setPanPositionState(newPosition);
      onPanChange?.(newPosition);
    },
    [onPanChange]
  );
  const checkOverflow = useCallback11(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    setContainerSize({
      width: container.clientWidth,
      height: container.clientHeight
    });
    const img = imageRef.current || container.querySelector("img");
    if (!img) {
      return;
    }
    const containerRatio = container.clientWidth / container.clientHeight;
    const imageRatio = img.naturalWidth / img.naturalHeight;
    const isOverflowing = img.naturalWidth > 0 && img.naturalHeight > 0 && Math.abs(containerRatio - imageRatio) > 0.1;
    setImageOverflows(isOverflowing);
  }, []);
  const handleImageLoad = useCallback11(() => {
    checkOverflow();
    const img = imageRef.current;
    if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setImageAspectRatio(img.naturalWidth / img.naturalHeight);
    }
  }, [checkOverflow]);
  useEffect10(() => {
    const handleResize = () => checkOverflow();
    window.addEventListener("resize", handleResize);
    const timeout = setTimeout(checkOverflow, 100);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeout);
    };
  }, [checkOverflow]);
  const zoomIn = useCallback11(() => {
    if (!enabled) {
      return;
    }
    setZoom(zoom + zoomStep);
  }, [enabled, zoom, zoomStep, setZoom]);
  const zoomOut = useCallback11(() => {
    if (!enabled) {
      return;
    }
    setZoom(zoom - zoomStep);
  }, [enabled, zoom, zoomStep, setZoom]);
  const resetZoom = useCallback11(() => {
    setZoomState(1);
    setPanPositionState({ x: 0, y: 0 });
    onZoomChange?.(1);
    onPanChange?.({ x: 0, y: 0 });
  }, [onZoomChange, onPanChange]);
  const handleMouseDown = useCallback11(
    (e) => {
      if (!enabled) {
        return;
      }
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y
      };
    },
    [enabled, panPosition]
  );
  const handleMouseMove = useCallback11(
    (e) => {
      if (!(enabled && isDragging)) {
        return;
      }
      setPanPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      });
    },
    [enabled, isDragging, setPanPosition]
  );
  const handleMouseUp = useCallback11(() => {
    setIsDragging(false);
  }, []);
  const handleMouseLeave = useCallback11(() => {
    setIsDragging(false);
    setIsHovering(false);
  }, []);
  const handleMouseEnter = useCallback11(() => {
    setIsHovering(true);
  }, []);
  const handleWheel = useCallback11(
    (e) => {
      if (!enabled) {
        return;
      }
      e.preventDefault();
      const delta = e.deltaY > 0 ? -wheelZoomStep : wheelZoomStep;
      setZoom(zoom + delta);
    },
    [enabled, wheelZoomStep, zoom, setZoom]
  );
  const handleTouchStart = useCallback11(
    (e) => {
      if (!enabled) {
        return;
      }
      const touch0 = e.touches[0];
      const touch1 = e.touches[1];
      if (e.touches.length === 2 && touch0 && touch1) {
        const dx = touch0.clientX - touch1.clientX;
        const dy = touch0.clientY - touch1.clientY;
        lastTouchDistance.current = Math.sqrt(dx * dx + dy * dy);
      } else if (e.touches.length === 1 && touch0) {
        setIsDragging(true);
        dragStart.current = {
          x: touch0.clientX - panPosition.x,
          y: touch0.clientY - panPosition.y
        };
      }
    },
    [enabled, panPosition]
  );
  const handleTouchMove = useCallback11(
    (e) => {
      if (!enabled) {
        return;
      }
      const touch0 = e.touches[0];
      const touch1 = e.touches[1];
      if (e.touches.length === 2 && lastTouchDistance.current !== null && touch0 && touch1) {
        e.preventDefault();
        const dx = touch0.clientX - touch1.clientX;
        const dy = touch0.clientY - touch1.clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const scale = distance / lastTouchDistance.current;
        setZoom(zoom * scale);
        lastTouchDistance.current = distance;
      } else if (e.touches.length === 1 && isDragging && touch0) {
        setPanPosition({
          x: touch0.clientX - dragStart.current.x,
          y: touch0.clientY - dragStart.current.y
        });
      }
    },
    [enabled, isDragging, zoom, setZoom, setPanPosition]
  );
  const handleTouchEnd = useCallback11(() => {
    lastTouchDistance.current = null;
    setIsDragging(false);
  }, []);
  const transform = `scale(${zoom}) translate(${panPosition.x / zoom}px, ${panPosition.y / zoom}px)`;
  return {
    // State
    zoom,
    panPosition,
    isDragging,
    imageOverflows,
    isHovering,
    imageAspectRatio,
    containerSize,
    // Setters
    setZoom,
    resetZoom,
    zoomIn,
    zoomOut,
    // Refs
    containerRef,
    imageRef,
    // Handlers
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleMouseEnter,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleImageLoad,
    // Computed
    transform,
    // Config
    minZoom,
    maxZoom
  };
}

// src/components/zoomable-image/ZoomableImage.tsx
import { cn as cn64 } from "@tessel/utils";
import { ChevronLeft as ChevronLeft4, ChevronRight as ChevronRight5 } from "lucide-react";
import { useCallback as useCallback12, useEffect as useEffect11 } from "react";

// src/components/zoomable-image/ZoomControls.tsx
import { cn as cn63 } from "@tessel/utils";
import { Minus as Minus2, Plus as Plus2, RotateCcw as RotateCcw3 } from "lucide-react";
import { jsx as jsx78, jsxs as jsxs44 } from "react/jsx-runtime";
function ZoomControls({
  zoom,
  minZoom,
  maxZoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onZoomChange,
  showSlider = true,
  className,
  isHovering = true
}) {
  return /* @__PURE__ */ jsxs44(
    "div",
    {
      className: cn63(
        "flex items-center gap-1 px-2 py-1.5 rounded-lg border border-border bg-background/95 backdrop-blur-sm shadow-lg transition-opacity",
        isHovering ? "opacity-100" : "opacity-70",
        className
      ),
      children: [
        /* @__PURE__ */ jsx78(
          Button,
          {
            variant: "ghost",
            size: "icon-sm",
            onClick: onZoomOut,
            disabled: zoom <= minZoom,
            children: /* @__PURE__ */ jsx78(Minus2, { className: "h-3 w-3" })
          }
        ),
        showSlider && /* @__PURE__ */ jsx78(
          Slider,
          {
            value: [zoom],
            onValueChange: onZoomChange,
            min: minZoom,
            max: maxZoom,
            step: 0.1,
            className: "w-24"
          }
        ),
        /* @__PURE__ */ jsx78(
          Button,
          {
            variant: "ghost",
            size: "icon-sm",
            onClick: onZoomIn,
            disabled: zoom >= maxZoom,
            children: /* @__PURE__ */ jsx78(Plus2, { className: "h-3 w-3" })
          }
        ),
        /* @__PURE__ */ jsxs44("span", { className: "text-xs text-muted-foreground w-10 text-center", children: [
          Math.round(zoom * 100),
          "%"
        ] }),
        /* @__PURE__ */ jsx78(
          Button,
          {
            variant: "ghost",
            size: "icon-sm",
            onClick: onZoomReset,
            disabled: zoom === 1,
            children: /* @__PURE__ */ jsx78(RotateCcw3, { className: "h-3 w-3" })
          }
        )
      ]
    }
  );
}

// src/components/zoomable-image/ZoomableImage.tsx
import { jsx as jsx79, jsxs as jsxs45 } from "react/jsx-runtime";
var CONTROLS_POSITION_CLASSES = {
  "bottom-left": "bottom-2 left-2",
  "bottom-right": "bottom-2 right-2",
  "top-left": "top-2 left-2",
  "top-right": "top-2 right-2"
};
var MINIMAP_POSITION_CLASSES = {
  "bottom-left": "top-2 right-2",
  "bottom-right": "top-2 right-2",
  "top-left": "bottom-2 right-2",
  "top-right": "bottom-2 left-2"
};
function ZoomableImage({
  src,
  alt,
  className,
  showControls = true,
  showMinimap: showMinimapProp = true,
  showSlider = true,
  enableKeyboard = false,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  minZoom = 0.5,
  maxZoom = 4,
  controlsPosition = "bottom-right",
  children,
  onZoomChange,
  onPanChange,
  resetKey
}) {
  const {
    zoom,
    panPosition,
    isDragging,
    imageOverflows,
    isHovering,
    resetZoom,
    zoomIn,
    zoomOut,
    setZoom,
    containerRef,
    imageRef,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleMouseEnter,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleImageLoad,
    transform,
    imageAspectRatio,
    containerSize,
    minZoom: hookMinZoom,
    maxZoom: hookMaxZoom
  } = useImageInteraction({
    minZoom,
    maxZoom,
    onZoomChange,
    onPanChange
  });
  useEffect11(() => {
    if (resetKey !== void 0) {
      resetZoom();
    }
  }, [resetKey, resetZoom]);
  useEffect11(() => {
    if (!enableKeyboard) {
      return;
    }
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" && hasPrev && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === "ArrowRight" && hasNext && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enableKeyboard, hasPrev, hasNext, onPrev, onNext]);
  const handleZoomChange = useCallback12(
    (values) => {
      const value = values[0];
      if (value !== void 0) {
        setZoom(value);
      }
    },
    [setZoom]
  );
  const showMinimap = showMinimapProp && (zoom > 1 || imageOverflows);
  return /* @__PURE__ */ jsxs45(
    "div",
    {
      ref: containerRef,
      className: cn64(
        "relative rounded-lg overflow-hidden bg-muted group w-full h-full",
        "cursor-grab",
        isDragging && "cursor-grabbing",
        className
      ),
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onMouseEnter: handleMouseEnter,
      onWheel: handleWheel,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      children: [
        children ? children({
          zoom,
          panPosition,
          transform,
          imageRef,
          containerRef
        }) : (
          /* biome-ignore lint/performance/noImgElement: shared UI library, not Next.js */
          /* @__PURE__ */ jsx79(
            "img",
            {
              ref: imageRef,
              src,
              alt,
              className: "w-full h-full object-contain select-none",
              style: {
                transform,
                transformOrigin: "center center"
              },
              draggable: false,
              onLoad: handleImageLoad
            }
          )
        ),
        onPrev && hasPrev && /* @__PURE__ */ jsx79(
          Button,
          {
            variant: "secondary",
            size: "icon-sm",
            className: cn64(
              "absolute left-2 top-1/2 -translate-y-1/2 z-10",
              "opacity-0 group-hover:opacity-100 transition-opacity"
            ),
            onClick: onPrev,
            children: /* @__PURE__ */ jsx79(ChevronLeft4, { className: "h-5 w-5" })
          }
        ),
        onNext && hasNext && /* @__PURE__ */ jsx79(
          Button,
          {
            variant: "secondary",
            size: "icon-sm",
            className: cn64(
              "absolute right-2 top-1/2 -translate-y-1/2 z-10",
              "opacity-0 group-hover:opacity-100 transition-opacity"
            ),
            onClick: onNext,
            children: /* @__PURE__ */ jsx79(ChevronRight5, { className: "h-5 w-5" })
          }
        ),
        showMinimap && /* @__PURE__ */ jsx79(
          "div",
          {
            className: cn64(
              "absolute z-20",
              MINIMAP_POSITION_CLASSES[controlsPosition]
            ),
            children: /* @__PURE__ */ jsx79(
              Minimap,
              {
                src,
                zoom,
                panPosition,
                aspectRatio: imageAspectRatio,
                containerSize,
                isHovering
              }
            )
          }
        ),
        showControls && /* @__PURE__ */ jsx79(
          "div",
          {
            className: cn64(
              "absolute z-20",
              CONTROLS_POSITION_CLASSES[controlsPosition]
            ),
            children: /* @__PURE__ */ jsx79(
              ZoomControls,
              {
                zoom,
                minZoom: hookMinZoom,
                maxZoom: hookMaxZoom,
                onZoomIn: zoomIn,
                onZoomOut: zoomOut,
                onZoomReset: resetZoom,
                onZoomChange: handleZoomChange,
                showSlider,
                isHovering
              }
            )
          }
        )
      ]
    }
  );
}
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  Alert,
  AudioPlayer,
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
  Badge,
  BaseInput,
  Button,
  ButtonLink,
  Calendar,
  CalendarDayButton,
  CampaignSelector,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ChatContainer,
  ChatEmptyState,
  ChatInput,
  ChatMessageItem,
  ChatMessageList,
  Checkbox,
  ColorVariants,
  ConfirmActionSheet,
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  DatePicker,
  DatePickerInput,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  EChart,
  FILLABLE_ICONS,
  Form,
  FormContext,
  FormCustom,
  FormField,
  FormFieldWrapper,
  FormInput,
  FormRoot,
  FormSelect,
  FormSubmit,
  FormTextarea,
  ICON_MAP,
  Icon,
  IconPicker,
  IconRenderer,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  InputSelectCard,
  InputSelectCardGroup,
  Label3 as Label,
  LanguageSwitcher,
  Loading,
  LoadingInline,
  LoadingOverlay,
  Logo,
  Menu,
  MenuPopover,
  Minimap,
  PageHeader,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  ParseMarkdown,
  ParseMarkdownLinks,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  ProgressBar,
  REACTION_ICONS,
  ReactionButton,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  RouterAdapterProvider,
  ScrollArea,
  ScrollBar,
  SearchInput,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectSingle,
  SelectTrigger,
  SelectValue,
  Separator5 as Separator,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetPrimitive,
  SheetTitle,
  SheetTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarLink,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  SiteFooter,
  SiteHeader,
  SizeVariants,
  Skeleton,
  Slider,
  Toaster as SonnerToaster,
  StatCard,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  TextareaWithActions,
  ThemeProvider,
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  Toaster2 as Toaster,
  ToggleGroup,
  ToolCallGroup,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TreeView,
  VideoPlayer,
  ZoomControls,
  ZoomableImage,
  baseInputStyles,
  buttonStyles,
  fieldControlStyles,
  formatError,
  getRandomVariant,
  isSidebarNavActive,
  radixToastCompat,
  reactionButtonVariants,
  reactionIconVariants,
  shouldShowErrors,
  toast2 as sonnerToast,
  toast,
  toggleGroupStyles,
  useFormContext,
  useImageInteraction,
  useRouterAdapter,
  useSheetUrl,
  useSheetUrlController,
  useSidebar,
  useSonnerToast,
  useToast
};
//# sourceMappingURL=index.js.map